import { useState, useRef, useCallback, useEffect, createContext, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import API from "../../../helpers/axiosInstance";
import { getApiHeaders, hasPermission } from "../../../utils/DefaultFunction";
import DeepLinkPicker from "../../../components/DeepLinkPicker/DeepLinkPicker";
import { usePermissionRouteContext } from "../../../components/PermissionRoute";
import SendTestModal from "./SendTestModal";
import ConditionBuilder, { Condition } from "../../../components/goal/ConditionBuilder";
import { goalEventName, goalEventOptions } from "../../../components/goal/goalModel";
import { DeliverySettings, deliveryFromApi, deliveryToApi, STALE_DAYS } from "./inAppDelivery";
import { FrequencySettings, frequencyFromApi, frequencyToApi } from "./inAppFrequency";
import { editorBlocksFromApi, apiBlocksFromEditor, resizeColumns } from "./inAppColumns";
import { parseColour, contrast } from "./inAppColour";
import { Appearance, effectiveAppearance, hasDarkValues, DARK_DEFAULTS, darkPreviewBlocks, darkPreviewSurface, cssOf, Brand } from "./inAppDark";
import { SURFACE_CONFIG, coerceStyleForSurface, resolveCssColor, hexToRgb, hasExitPath, hasDismissAction, textBlockContrastFails, validateMessageDoc, validateMessageIssues, Issue, targetsAllCustomers, ALL_CUSTOMERS_CONFIRM, imageUrlsOf, videoUrlsOf, DEFAULT_VARIABLES } from "./inAppRules";
import { probeImages, probeVideos } from "./inAppImageProbe";
import { urlProblem } from "./inAppUrls";
import { resolvePreviewBlocks, IDENTIFYING_KEYS, countdownEndMs, countdownExpiredText, formatCountdown } from "./inAppPreview";
import { resolveMergeTags } from "./inAppMergeTags";
import {
  ChevronLeft,
  GripVertical,
  Copy,
  Trash2,
  Smartphone,
  Eye,
  Send,
  Save as SaveIcon,
  Undo2,
  Redo2,
  X,
  ChevronRight,
  ChevronLeft as ChevronLeftIcon,
  ChevronUp,
  ChevronDown,
  Type,
  Heading,
  Image as ImageIcon,
  Square,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Upload,
  Loader2,
  Layers,
  Settings,
  Users,
  Clock,
  CalendarClock,
  Target,
  Timer,
  Tag,
  ShoppingBag,
  Star,
  ListChecks,
  CheckSquare,
  BarChart3,
  Sparkles,
  Columns as ColumnsIcon,
  Rows as RowsIcon,
  Box as BoxIcon,
  PlayCircle,
  TextCursorInput,
  Check,
  Info,
} from "lucide-react";
import { BsAndroid2, BsApple } from "react-icons/bs";

// ─── API ─────────────────────────────────────────────────────────────────────

const API_URL = `${process.env.REACT_APP_API_URL}/in-app-messages`;

const getHeaders = () => ({
  "x-api-key": localStorage.getItem("api-key"),
  "x-access-token": localStorage.getItem("customer-portal"),
});

const MAX_IMAGE_SIZE_MB = 5;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "Please upload a JPG, PNG, GIF, or WEBP image.";
  if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) return `Images must be under ${MAX_IMAGE_SIZE_MB}MB.`;
  return null;
}

// IMG (DA-BUG-1135): In-App images upload to RRS POST /in-app/media-upload (multipart "file"),
// which converts to WebP, resizes to ≤ 2048 px, strips metadata and stores it under a sha256 name
// → {url, width, height, bytes, sha256}. The size and dimensions are kept on the block.
interface UploadedMedia { url: string; width?: number; height?: number; bytes?: number }
async function uploadMediaFile(file: File): Promise<UploadedMedia> {
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post(`${process.env.REACT_APP_API_URL}/in-app/media-upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      "x-access-token": localStorage.getItem("customer-portal"),
      "x-api-key": localStorage.getItem("api-key"),
    },
  });
  const result = response.data;
  if (result && result.success === false) throw new Error(result?.message || "Upload failed");
  const d = result?.data || result || {};
  const url = d.url || result?.fileUrl || result?.url;
  if (!url) throw new Error(result?.message || "Upload failed");
  return { url, width: d.width, height: d.height, bytes: d.bytes };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type MessageType = "modal" | "banner" | "slide-in" | "fullscreen";
type Device = "ios" | "android";
type BlockType =
  | "heading"
  | "text"
  | "image"
  | "button"
  | "divider"
  | "spacer"
  // Basic
  | "icon"
  | "feature-list"
  // Layout
  | "container"
  | "row"
  | "columns"
  | "column"   // Gap 2: one column of a Columns block (editor-only wrapper → content.columns[i])
  // Offers
  | "countdown"
  | "coupon"
  | "product"
  // Interactive
  | "carousel"
  | "rating"
  | "nps"
  | "choice"
  | "input"
  | "consent"
  | "progress"
  | "video"
  // Form — groups data-capture fields → one submission (+ optional SDK-dispatched API post)
  | "form";

// Unified block action (Button / Tap / CTA / On-submit). Matches the Figma's action menu.
type ActionType = "dismiss" | "navigate" | "openUrl" | "deepLink" | "emitEvent" | "nextPage" | "previousPage" | "copyCoupon" | "none";
interface BlockAction {
  type: ActionType;
  couponBlockId?: string;             // Gap 5: copyCoupon target (absent = the first coupon)
  url?: string;
  fallbackUrl?: string;
  screen?: string;
  event?: string;
}

interface BlockContent {
  // text / heading
  text?: string;
  fontSize?: number;
  fontWeight?: "normal" | "medium" | "semibold" | "bold";
  color?: string;
  alignment?: "left" | "center" | "right";
  lineHeight?: number;
  // image
  imageUrl?: string;
  imageAlt?: string;
  imageHeight?: number;
  imageWidth?: number;      // IMG: stored pixel size / bytes from /in-app/media-upload
  imageHeightPx?: number;
  imageBytes?: number;
  fit?: "cover" | "contain" | "fill";
  cornerRadius?: number;
  tapAction?: BlockAction;
  // button / CTA
  buttonStyle?: "primary" | "secondary" | "outline" | "ghost";
  buttonColor?: string;
  buttonTextColor?: string;
  buttonRadius?: number;
  action?: BlockAction;               // Button + Product CTA
  // divider / spacer
  spacerHeight?: number;
  dividerColor?: string;
  lineStyle?: "solid" | "dashed" | "dotted";
  label?: string;
  thickness?: number;
  verticalMargin?: number;
  // icon / badge
  iconValue?: string;
  iconLabel?: string;
  iconSubLabel?: string;
  iconSize?: number;
  iconBg?: string;
  iconShape?: "circle" | "rounded" | "square";
  iconLayout?: "stacked" | "inline"; // D7 (DA-BUG-1412): badge above the labels (stacked) or beside them (inline)
  // feature-list
  items?: string[];
  // container / row / columns (layout blocks nest child blocks)
  containerBg?: string;
  containerBgOpacity?: number;
  containerPadding?: number;
  containerRadius?: number;
  containerBorderColor?: string;
  containerBorderWidth?: number;
  containerBorder?: boolean;          // "Show border"
  containerShadow?: boolean;
  children?: Block[];
  gap?: number;
  distribution?: "start" | "center" | "end" | "between" | "around";
  alignCross?: "start" | "center" | "end" | "stretch";
  columnCount?: 2 | 3 | 4;
  // countdown
  endDate?: string;
  countdownMode?: "fixed" | "duration" | "attribute";
  countdownFormat?: "dhms" | "hms" | "ms";
  showDays?: boolean;
  expiredAction?: "text" | "disable" | "dismiss";
  // R2-16 (device contract): mode "duration" → durationSeconds; mode "attribute" → attribute (user
  // property holding the end date); expiredAction "text" → expiredText (device fallback: label, "Expired").
  durationSeconds?: number;
  attribute?: string;
  expiredText?: string;
  // coupon
  code?: string;
  copyText?: string;
  accent?: string;
  confetti?: boolean;
  showExpiry?: boolean;
  expiryDate?: string;
  couponStyle?: "outlined" | "filled" | "card";
  afterCopy?: BlockAction;
  // product / offer card
  productName?: string;
  productDescription?: string;
  price?: string;
  originalPrice?: string;
  badge?: string;
  productLayout?: "vertical" | "horizontal";
  showShadow?: boolean;
  // carousel
  slides?: { title?: string; description?: string; imageUrl?: string; cta?: string; action?: BlockAction }[]; // R2-19: slide action
  autoAdvance?: boolean;
  loop?: boolean;
  indicators?: boolean;
  arrows?: boolean;
  // rating / feedback (NPS is a TYPE here, not a separate block)
  ratingType?: "stars" | "thumbs" | "emoji" | "nps";
  ratingMax?: number;
  ratingSubLabel?: string;
  thankYouMessage?: string;
  activeColor?: string;
  requireToSubmit?: boolean;
  autoSubmit?: boolean;               // X-10: submit on tap (read by RRS + SDKs; not edited here)
  lowLabel?: string;                  // NPS scale ends
  highLabel?: string;
  min?: number;
  max?: number;
  // choice (single / multiple)
  options?: string[];
  multiple?: boolean;
  submitText?: string;
  choiceLayout?: "list" | "grid";
  // input-field
  placeholder?: string;
  inputType?: "text" | "email" | "phone" | "number" | "multiline";
  maxChars?: number;
  required?: boolean;
  saveToAttribute?: string;
  // consent
  link?: string;
  linkUrl?: string;
  preChecked?: boolean;
  saveToChannel?: string;
  // progress
  current?: number;
  showPercent?: boolean;
  trackColor?: string;
  progressHeight?: number;
  progressStyle?: "rounded" | "square";
  animate?: boolean;
  // video
  videoUrl?: string;
  videoPoster?: string;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
  // data-capture ON-SUBMIT action (Figma model: Dismiss / Navigate / Emit event)
  onSubmit?: BlockAction;
  // Form grouping: a field's key in the grouped submission payload (input/choice/consent).
  fieldKey?: string;
  // D-1 (DA-BUG-1051): optional dark-appearance value per colour key, saved as `<base>Dark`.
  colorDark?: string;
  // P6 (DA-BUG-1384): countdown label + coupon label/expiry text colour (so dark backgrounds are readable).
  labelColor?: string;
  labelColorDark?: string;
  // DA-BUG-1421 (D2/D9): coupon expiry colour + rating Submit button fill/label colour (+ dark variants).
  expiryColor?: string;
  expiryColorDark?: string;
  submitColor?: string;
  submitColorDark?: string;
  submitTextColor?: string;
  submitTextColorDark?: string;
  buttonColorDark?: string;
  buttonTextColorDark?: string;
  dividerColorDark?: string;
  iconBgDark?: string;
  accentDark?: string;
  activeColorDark?: string;
  trackColorDark?: string;
  containerBgDark?: string;
  containerBorderColorDark?: string;
  // Form block: the SDK-dispatched API destination (no server-held secrets — headers are visible on device).
  formApi?: FormApiConfig;
}

// Form API destination (SDK posts this directly). Body is auto-built from the form's fields
// ({fieldKey: value}); `staticBody` rows are merged in. Headers carry any (visible) auth.
interface FormApiConfig {
  endpoint: string;
  method: "POST" | "PUT";
  headers: { key: string; value: string }[];
  staticBody: { key: string; value: string }[];
}

interface Block {
  id: string;
  type: BlockType;
  content: BlockContent;
}

interface MessageStyle {
  background: string;
  backgroundDark?: string;   // D-1: card background in dark appearance (empty = inherit light)
  // Gap 13 (DA-BUG-1097): explicit fullscreen background image + scrim (contract item 8).
  backgroundImage?: { url: string; fit: "cover" | "contain" };
  scrimOpacity?: number;     // 0–0.8
  scrimColor?: string;       // default #000000
  scrimColorDark?: string;   // dark-appearance scrim colour (be-rrs passes it through)
  appearance?: Appearance;   // D-1: auto | light | dark; unset → auto when any dark value exists, else light
  width: number;             // % of screen (modal/fullscreen width control)
  cornerRadius: number;
  backdropOpacity: number;
  animation: string;         // entry animation: fade-scale|slide-up|slide-down|none
  position: string;          // banner/slide-in placement
  closeButtonPosition: string; // modal close-X position: top-right|top-left|inside
  imageHeroTop?: boolean;     // DA-BUG-1401: modal only, opt-in — first image edge-to-edge + close overlaid
  // Dismiss group (BUG-02) — which exit paths the surface offers.
  closeButton: boolean;      // visible close (X)
  tapBackdrop: boolean;      // tap the dimmed backdrop to close (modal only)
  swipeToDismiss: boolean;   // swipe to close
  autoDismissSecs: number;   // 0 = off; banner/slide-in only
}


// A screen name is OS-specific (an Activity on Android vs a ViewController on iOS), so each
// selected/excluded screen carries the platform it applies to. "both" = applies everywhere.
type ScreenPlatform = "android" | "ios" | "both";
interface ScreenRef {
  name: string;
  platform: ScreenPlatform;
}

interface TriggerSettings {
  event: string;                 // app_open|session_start|screen_view|screen_time|custom_event|session_time|app_request
  delay: number;
  delayUnit: string;
  // screen_view
  screens?: ScreenRef[];
  requireSameScreen?: boolean;
  // screen_time
  screenName?: string;
  screenPlatform?: ScreenPlatform;   // the OS that screen name belongs to
  activeDuration?: number;
  activeDurationUnit?: "seconds" | "minutes";
  resetTimerOnLeave?: boolean;
  // custom_event
  customEvent?: string;
  occurrenceOp?: "exactly" | "at_least";
  occurrenceCount?: number;
  eventConditions?: Condition[];
  // session_time
  sessionThreshold?: number;
  sessionThresholdUnit?: "seconds" | "minutes";
  // app_request — the app explicitly requests this message by its trigger key
  requestKey?: string;
  contextConditions?: Condition[];
  // screen restrictions (collapsible) — excluded screens only
  excludedScreens?: ScreenRef[];
}

// A screen from GET /in-app/screens (Registered registry ⊕ Observed from event data).
interface ScreenItem {
  name: string;
  label?: string | null;
  status: "registered" | "observed" | "both";
  platform?: ScreenPlatform; // registered screens may carry their platform
}

// Normalize a stored screen list (legacy string[] OR ScreenRef[]) → ScreenRef[].
const PLATFORMS: ScreenPlatform[] = ["android", "ios", "both"];
const toScreenRefs = (arr: unknown): ScreenRef[] =>
  (Array.isArray(arr) ? arr : [])
    .map((s): ScreenRef | null => {
      if (typeof s === "string") return s.trim() ? { name: s.trim(), platform: "both" } : null;
      if (s && typeof s === "object" && typeof (s as any).name === "string" && (s as any).name.trim()) {
        const p = (s as any).platform;
        return { name: (s as any).name.trim(), platform: PLATFORMS.includes(p) ? p : "both" };
      }
      return null;
    })
    .filter((x): x is ScreenRef => x !== null);
const PLATFORM_LABEL: Record<ScreenPlatform, string> = { android: "Android", ios: "iOS", both: "Both" };

interface AudienceSettings {
  type: "" | "all" | "segments" | "tags";   // "" = not chosen yet (P-1: no default audience)
  cohortIds: string[];
  cohortNames: string[];
  // tags — contacts carrying the picked tags (from GET /tags)
  tagIds?: string[];
  tagNames?: string[];
  estimatedReach?: number;
  // legacy single-cohort fields kept for load back-compat
  cohortId?: string;
  cohortName?: string;
}

interface ScheduleSettings {
  startMode: "immediate" | "scheduled";
  startDate: string;
  endEnabled: boolean;
  endDate: string;
  activeDays: number[];              // 0=Mon … 6=Sun
  timeWindowEnabled: boolean;
  timeStart: string;
  timeEnd: string;
  timezone: string;                  // "customer_local" | IANA
  fallbackTimezone: string;
}


interface GoalSettings {
  event: string;
  conditions: { property: string; operator: string; value: string }[];
  window: number;
  windowUnit: "hours" | "days";
  stopAfterConversion: boolean;
  trackBlockInteractions: boolean;
}

// Arbitration when ≥2 messages are eligible at once (only one can show). The SDK ranks by
// priority (then freshness, then id); on conflict it discards or queues the loser.
// DeliverySettings (+ D15 offlineOk) and its API mapping live in ./inAppDelivery.

// ─── Helper ──────────────────────────────────────────────────────────────────

const uid = () => Math.random().toString(36).substr(2, 9);

// ─── Color Palettes ──────────────────────────────────────────────────────────

const TEXT_COLORS = ["#1A202C", "#2D3748", "#4A5568", "#718096", "#A0AEC0", "#FFFFFF", "var(--brand-primary)", "#38A169", "#E53E3E", "#D69E2E"];
const BG_COLORS = ["#FFFFFF", "#F7FAFC", "#EBF8FF", "#F0FFF4", "#FFF5F5", "#FFFAF0", "#1A202C", "#2D3748", "var(--brand-primary)", "#38A169"];
const ACCENT_COLORS = ["var(--brand-primary)", "#38A169", "#E53E3E", "#D69E2E", "#7C3AED", "#DD6B20", "#319795", "#D53F8C", "#1A202C", "#718096"];

// ─── Templates ───────────────────────────────────────────────────────────────

export const TEMPLATES: Record<string, { name: string; description: string; type: MessageType; blocks: Block[] }> = {
  welcome: {
    name: "Welcome",
    description: "Onboard new users with a warm greeting",
    type: "modal",
    blocks: [
      { id: uid(), type: "icon", content: { iconValue: "👋", iconSize: 64, iconBg: "#EBF8FF", iconShape: "circle", alignment: "center" } },
      { id: uid(), type: "heading", content: { text: "Welcome to Apex Bank!", alignment: "center", fontWeight: "bold", color: "#1A202C", fontSize: 20 } },
      { id: uid(), type: "text", content: { text: "Your account is ready. Let's take a quick tour and set up your preferences.", alignment: "center", color: "#718096", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Explore dashboard →", buttonStyle: "primary", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: { type: "dismiss" } } },
    ],
  },
  announcement: {
    name: "Announcement",
    description: "Highlight a new feature or update",
    type: "modal",
    blocks: [
      { id: uid(), type: "icon", content: { iconValue: "⚡", iconSize: 64, iconBg: "#F0FFF4", iconShape: "circle", alignment: "center" } },
      { id: uid(), type: "heading", content: { text: "Instant Transfers are here", fontWeight: "bold", color: "#1A202C", fontSize: 18 } },
      { id: uid(), type: "text", content: { text: "Send money to anyone in seconds. Works 24/7, including weekends.", color: "#4A5568", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Try it now →", buttonStyle: "primary", buttonColor: "#38A169", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: { type: "openUrl" } } },
    ],
  },
  promo: {
    name: "Promo Offer",
    description: "Drive upgrades with a compelling offer",
    type: "modal",
    blocks: [
      { id: uid(), type: "icon", content: { iconValue: "💎", iconSize: 64, iconBg: "#FFFAF0", iconShape: "circle", alignment: "center" } },
      { id: uid(), type: "heading", content: { text: "Upgrade to Premium", fontWeight: "bold", alignment: "center", color: "#1A202C", fontSize: 18 } },
      { id: uid(), type: "text", content: { text: "Unlock unlimited transfers, zero fees, and priority support.", alignment: "center", color: "#718096", fontSize: 14 } },
      { id: uid(), type: "button", content: { text: "Get 3 months free", buttonStyle: "primary", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: { type: "openUrl" } } },
    ],
  },
  update: {
    name: "App Update",
    description: "Notify users of a pending update",
    type: "slide-in",
    blocks: [
      { id: uid(), type: "heading", content: { text: "Update available — v3.8.1", fontWeight: "bold", color: "#1A202C", fontSize: 13 } },
      { id: uid(), type: "text", content: { text: "Security patch • 12 MB • What's new: 🔒 Fix, ⚡ Faster loads", color: "#718096", fontSize: 11 } },
      { id: uid(), type: "button", content: { text: "Update now", buttonStyle: "primary", buttonColor: "#10B981", buttonTextColor: "#FFFFFF", buttonRadius: 10, action: { type: "openUrl" } } },
    ],
  },
};

// ─── Button label colour (P-5) ─────────────────────────────────────────────────
// `buttonTextColor` is synced as the device's `textColor`. Each style has its own default when
// it is empty: white on Primary, dark on Secondary, the accent on Outline / Ghost (what the SDKs do).
type ButtonStyle = NonNullable<BlockContent["buttonStyle"]>;
const BUTTON_TEXT_DEFAULT: Record<ButtonStyle, string | undefined> = { primary: "#FFFFFF", secondary: "#2D3748", outline: undefined, ghost: undefined };
function buttonLabelColor(c: BlockContent): string {
  const bs = (c.buttonStyle || "primary") as ButtonStyle;
  if (bs === "secondary" && c.buttonColor && !c.buttonTextColor) return readableOn(c.buttonColor);
  return c.buttonTextColor || BUTTON_TEXT_DEFAULT[bs] || c.buttonColor || "var(--brand-primary)";
}
// Gap 11 — a coloured Secondary fill gets white or dark text, whichever reads (≥ 3:1 for white).
function readableOn(fill: string): string {
  const f = parseColour(resolveCssColor(fill));
  return f && contrast("#FFFFFFFF", f) >= 3 ? "#FFFFFF" : "#1A202C";
}
// Changing the style resets the label colour to that style's default, so a white Primary label
// never carries over onto an Outline / Ghost button (invisible on a light card).
function buttonStylePatch(style: ButtonStyle): Partial<BlockContent> {
  // Gap 11: an authored colour is a Secondary button's fill; switching to Secondary starts grey.
  return style === "secondary"
    ? { buttonStyle: style, buttonTextColor: BUTTON_TEXT_DEFAULT[style], buttonColor: undefined }
    : { buttonStyle: style, buttonTextColor: BUTTON_TEXT_DEFAULT[style] };
}

// ─── Default Block Content ────────────────────────────────────────────────────

function defaultBlockContent(type: BlockType): BlockContent {
  switch (type) {
    case "heading": return { text: "Your heading here", fontSize: 18, fontWeight: "bold", color: "#1A202C", alignment: "left" };
    case "text": return { text: "Add your message text here.", fontSize: 14, fontWeight: "normal", color: "#4A5568", alignment: "left", lineHeight: 1.5 };
    case "image": return { imageUrl: "", imageAlt: "", imageHeight: 180, fit: "cover", cornerRadius: 0, tapAction: { type: "none" } };
    case "button": return { text: "Click here", buttonStyle: "primary", buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 12, action: { type: "dismiss" } };
    case "divider": return { dividerColor: "#E2E8F0", lineStyle: "solid", thickness: 1, verticalMargin: 8 };
    case "spacer": return { spacerHeight: 16 };
    case "icon": return { iconValue: "🔔", iconLabel: "", iconSubLabel: "", iconSize: 48, iconBg: "#EBF8FF", iconShape: "circle", iconLayout: "stacked", alignment: "center" };
    case "feature-list": return { items: ["Unlimited transfers", "Zero annual fee", "Priority support"], color: "#384150" };
    case "countdown": return { label: "Offer ends in", countdownMode: "fixed", endDate: "", countdownFormat: "dhms", showDays: true, expiredAction: "dismiss", color: "#1A202C" };
    case "coupon": return { label: "Your code", code: "SAVE20", copyText: "Copy code", couponStyle: "outlined", accent: "var(--brand-primary)", confetti: true, showExpiry: false, afterCopy: { type: "dismiss" } };
    case "product": return { imageUrl: "", productName: "Product name", productDescription: "Short description", price: "PKR 2,499", originalPrice: "PKR 3,599", badge: "-30%", text: "Add to cart", action: { type: "dismiss" }, buttonColor: "var(--brand-primary)", buttonTextColor: "#FFFFFF", buttonRadius: 10, productLayout: "vertical", imageHeight: 140, showShadow: true };
    case "rating": return { label: "How was your experience?", ratingSubLabel: "Tap a star to rate", thankYouMessage: "Thanks for your feedback!", ratingType: "stars", ratingMax: 5, activeColor: "#F59E0B", requireToSubmit: true, onSubmit: { type: "dismiss" } };
    case "choice": return { label: "Pick an option", submitText: "Submit", options: ["Option A", "Option B", "Option C"], multiple: false, choiceLayout: "list", onSubmit: { type: "dismiss" } };
    case "input": return { label: "Your email", placeholder: "you@example.com", submitText: "Submit", inputType: "email", required: false, onSubmit: { type: "dismiss" } };
    case "consent": return { text: "I agree to receive updates.", link: "Privacy Policy", linkUrl: "", required: false, preChecked: false };
    case "progress": return { label: "Profile completion", current: 70, max: 100, showPercent: true, accent: "var(--brand-primary)", trackColor: "#EEF1F6", progressHeight: 8, progressStyle: "rounded", animate: true };
    case "carousel": return { slides: [{ title: "Slide one", description: "", imageUrl: "", cta: "" }, { title: "Slide two", description: "", imageUrl: "", cta: "" }], autoAdvance: false, loop: true, indicators: true, arrows: true };
    case "video": return { videoUrl: "", videoPoster: "", autoplay: false, muted: true, loop: false, controls: true, imageHeight: 200, cornerRadius: 8 };
    case "container": return { containerBg: "#FFFFFF", containerPadding: 16, containerRadius: 12, containerBorderColor: "#E2E8F0", containerBorderWidth: 1, containerShadow: false, children: [] };
    case "row": return { gap: 12, alignment: "left", distribution: "start", containerPadding: 0, children: [] };
    case "columns": return { columnCount: 2, gap: 12, alignment: "left", containerPadding: 0, children: [
      { id: uid(), type: "column", content: { children: [] } }, { id: uid(), type: "column", content: { children: [] } },
    ] };
    // Form groups data-capture fields into ONE submission; the SDK posts it to the optional API.
    case "form": return {
      submitText: "Submit",
      containerBg: "#FFFFFF", containerPadding: 16, containerRadius: 12, containerBorderColor: "#E2E8F0", containerBorderWidth: 1,
      children: [
        { id: uid(), type: "input", content: { label: "Your email", placeholder: "you@example.com", inputType: "email", required: true, fieldKey: "email" } },
      ],
      onSubmit: { type: "dismiss" },
      formApi: { endpoint: "", method: "POST", headers: [], staticBody: [] },
    };
    default: return {};
  }
}


// Layout blocks nest child blocks in `content.children`. Tree helpers keep the
// edit/select/delete/duplicate operations recursion-safe across that nesting.
const LAYOUT_TYPES: BlockType[] = ["container", "row", "columns", "column", "form"];
const isLayoutType = (t: BlockType) => LAYOUT_TYPES.includes(t);

function treeUpdate(list: Block[], id: string, content: Partial<BlockContent>): Block[] {
  return list.map((b) => {
    if (b.id === id) return { ...b, content: { ...b.content, ...content } };
    if (b.content.children && b.content.children.length)
      return { ...b, content: { ...b.content, children: treeUpdate(b.content.children, id, content) } };
    return b;
  });
}
function treeDelete(list: Block[], id: string): Block[] {
  return list.filter((b) => b.id !== id).map((b) =>
    b.content.children && b.content.children.length
      ? { ...b, content: { ...b.content, children: treeDelete(b.content.children, id) } }
      : b);
}
function treeFind(list: Block[], id: string | null): Block | null {
  if (!id) return null;
  for (const b of list) {
    if (b.id === id) return b;
    if (b.content.children && b.content.children.length) {
      const found = treeFind(b.content.children, id);
      if (found) return found;
    }
  }
  return null;
}
function treeInsertChild(list: Block[], parentId: string, child: Block): Block[] {
  return list.map((b) => {
    if (b.id === parentId) return { ...b, content: { ...b.content, children: [...(b.content.children || []), child] } };
    if (b.content.children && b.content.children.length)
      return { ...b, content: { ...b.content, children: treeInsertChild(b.content.children, parentId, child) } };
    return b;
  });
}
// R2-12 — click-to-add: into the selected layout block (container / row / columns / form) when
// one is selected, else at the top level. A form holds fields, never another layout block.
// A selected block that already sits inside a layout block keeps the next block in that same
// parent, so adding several blocks into a row / container works click after click.
function treeParentOf(list: Block[], id: string | null, parent: Block | null = null): Block | null {
  if (!id) return null;
  for (const b of list) {
    if (b.id === id) return parent;
    const kids = b.content.children;
    if (kids && kids.length) { const p = treeParentOf(kids, id, b); if (p) return p; }
  }
  return null;
}
export function addBlockToTree(list: Block[], selectedId: string | null, child: Block): Block[] {
  const selected = treeFind(list, selectedId);
  const target = selected && isLayoutType(selected.type) ? selected : treeParentOf(list, selectedId);
  if (target && !(target.type === "form" && isLayoutType(child.type))) {
    return treeInsertChild(list, target.id, child);
  }
  return [...list, child];
}
function cloneBlock(b: Block, nextId: () => string): Block {
  return { ...b, id: nextId(), content: { ...b.content, children: b.content.children ? b.content.children.map((c) => cloneBlock(c, nextId)) : undefined } };
}
// Duplicate a block in place (as the next sibling) wherever it lives in the tree.
function treeDuplicate(list: Block[], id: string, nextId: () => string): { list: Block[]; newId: string | null } {
  let newId: string | null = null;
  const walk = (arr: Block[]): Block[] => {
    const out: Block[] = [];
    for (const b of arr) {
      const kids = b.content.children && b.content.children.length ? walk(b.content.children) : b.content.children;
      const nb = kids === b.content.children ? b : { ...b, content: { ...b.content, children: kids } };
      out.push(nb);
      if (b.id === id) { const copy = cloneBlock(b, nextId); newId = copy.id; out.push(copy); }
    }
    return out;
  };
  return { list: walk(list), newId };
}


// ─── Preview theme (DA-BUG-1054) ───────────────────────────────────────────────
// null = light preview (every literal below renders exactly as before). In the dark preview the
// unauthored parts take the SDK dark defaults (D-5), as on the device.
const DARK_PREVIEW: Record<string, string> = { ...DARK_DEFAULTS, placeholderFill: "#000000", couponFill: DARK_DEFAULTS.inputFill };
type PreviewPalette = Record<string, string>;
const PreviewTheme = createContext<PreviewPalette | null>(null);
const usePick = () => {
  const t = useContext(PreviewTheme);
  return (light: string, role: string) => (t && t[role] ? t[role] : light);
};

// Device safe area (Preview 11 / 12): what each frame covers at the top (status bar / Dynamic Island)
// and the bottom (home indicator / navigation bar), in frame pixels. No frame (isolated render) = 0.
interface SafeArea { top: number; bottom: number; platform: Device }
const SafeAreaCtx = createContext<SafeArea>({ top: 0, bottom: 0, platform: "android" });
const IOS_SAFE_AREA: SafeArea = { top: 50, bottom: 28, platform: "ios" };
const ANDROID_SAFE_AREA: SafeArea = { top: 32, bottom: 28, platform: "android" };

// Preview run (Preview differences 1–25): set by PreviewModal only. Sample values for merge tags /
// countdown attributes; null on the editor canvas.
interface PreviewRun { vars: Record<string, string>; act?: (a?: BlockAction) => void }
const PreviewRunCtx = createContext<PreviewRun | null>(null);

// Preview 2 (R2-15): a live countdown, as the iOS CountdownView (label above, value in the accent)
// with the shared end rules (fixed / duration from when the message opens / attribute).
function CountdownBlock({ content }: { content: BlockContent }) {
  const run = useContext(PreviewRunCtx);
  const pick = usePick();
  const [openedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  const endMs = countdownEndMs(content as Record<string, any>, openedAt, run?.vars || {});
  const remaining = endMs == null ? null : endMs - now;
  const ticking = remaining != null && remaining > 0;
  useEffect(() => {
    if (!ticking) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);
  let value: string | null = null;
  let faded = false;
  if (remaining != null) {
    if (remaining > 0) value = formatCountdown(remaining, content.countdownFormat, content.showDays !== false);
    else if (content.expiredAction === "text") value = countdownExpiredText(content as Record<string, any>);
    else { value = formatCountdown(0, content.countdownFormat, content.showDays !== false); faded = content.expiredAction === "disable"; }
  }
  return (
    <div className="flex flex-col items-center gap-1" style={{ opacity: faded ? 0.5 : 1 }}>
      {content.label && <p className="text-center" style={{ fontSize: 13, color: content.labelColor || pick("#666666", "secondaryText") }}>{content.label}</p>}
      {value != null && (
        <p data-testid="countdown-value" className="text-center" style={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: content.color || "var(--brand-primary)" }}>{value}</p>
      )}
    </div>
  );
}

// Preview 3 (R2-24): video plays inline (iOS InAppVideoView / Android ExoPlayer) with autoplay, muted,
// loop, controls and the poster; a missing or refused URL (UrlGate / SafeUrl: https only), a format
// AVPlayer cannot play, or a load error shows "Video unavailable".
const UNPLAYABLE_VIDEO = /\.(webm|ogv|ogg|mkv|avi|flv|wmv)(\?|#|$)/i;
function VideoBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const url = (content.videoUrl || "").trim();
  const refused = !url || !!urlProblem(url, "https") || UNPLAYABLE_VIDEO.test(url);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const error = refused || failed;
  const autoplay = !!content.autoplay;
  const controls = content.controls !== false;
  return (
    <div data-testid="video-frame" style={{ position: "relative", width: "100%", height: content.imageHeight || 180, borderRadius: content.cornerRadius ?? 10, overflow: "hidden", background: pick("#111827", "videoFrame"), display: "flex", alignItems: "center", justifyContent: "center" }}>
      {!error && (
        <video key={url} src={url} poster={content.videoPoster || undefined} autoPlay={autoplay} muted={content.muted ?? autoplay} loop={!!content.loop} controls={controls} playsInline
          onError={() => setFailed(true)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
      )}
      {error && content.videoPoster && <img src={content.videoPoster} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />}
      {!error && !controls && !autoplay && <span aria-hidden style={{ position: "relative", fontSize: 34, color: "#FFFFFF", pointerEvents: "none" }}>▶</span>}
      {error && <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.6)", color: "#FFFFFF", fontSize: 14, fontWeight: 600 }}>Video unavailable</span>}
    </div>
  );
}

// Preview 14 (R2-17): coupon as the SDKs draw it — optional label above; outlined (accent border:
// solid on a 6 % accent tint on iOS, dashed on #F3F6FF on Android), filled (accent fill, white code and
// copy text) or card (white raised card; Android adds a hairline border); "Expires <date>" under it
// on iOS when showExpiry is on (Android draws no expiry).
function CouponBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const safe = useContext(SafeAreaCtx);
  const ios = safe.platform === "ios";
  const accent = content.accent || "var(--brand-primary)";
  const style = content.couponStyle || "outlined";
  const filled = style === "filled";
  const row: React.CSSProperties = { borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 };
  if (filled) row.backgroundColor = accent;
  else if (style === "card") Object.assign(row, { backgroundColor: pick("#FFFFFF", "inputFill"), boxShadow: "0 2px 8px rgba(0,0,0,0.14)", ...(ios ? {} : { border: `1px solid ${pick("#E5E7EB", "containerBorder")}` }) });
  else Object.assign(row, ios
    ? { border: `1.5px solid ${accent}`, backgroundColor: withOpacity(accent, 0.06) }
    : { border: `1.5px dashed ${accent}`, backgroundColor: pick("#F3F6FF", "inputFill") });
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      {content.label && <p style={{ fontSize: 12, color: content.labelColor || pick("#666666", "secondaryText") }}>{content.label}</p>}
      <div data-testid="coupon-row" style={row}>
        <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontWeight: 700, fontSize: 17, color: filled ? "#FFFFFF" : pick("#14161D", "text"), ...WORD_WRAP, minWidth: 0 }}>{content.code || ""}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: filled ? "#FFFFFF" : accent, flex: "none" }}>{content.copyText || "COPY"}</span>
      </div>
      {ios && content.showExpiry && content.expiryDate && <p style={{ fontSize: 10, color: content.expiryColor || pick("#8C8C8C", "secondaryText") }}>Expires {content.expiryDate}</p>}
    </div>
  );
}

// Preview 15 (R2-18): product card as the SDKs — "horizontal" puts a 110 px image beside the details
// (iOS min(imageHeight, 110) tall, Android 110); vertical = full-width image at imageHeight (110
// default). With no image iOS draws its 🛍 placeholder and Android an empty area of the same size.
// Name and description wrap in full: neither SDK clamps lines. Empty badge: no pill. CTA: iOS draws it
// when its label is set, Android when it has an action.
function ProductBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const run = useContext(PreviewRunCtx);
  const ios = useContext(SafeAreaCtx).platform === "ios";
  const horizontal = content.productLayout === "horizontal";
  const h = content.imageHeight || 110;
  const accent = content.buttonColor || "var(--brand-primary)";
  const imgBox: React.CSSProperties = horizontal ? { width: 110, height: ios ? Math.min(h, 110) : 110, flex: "none" } : { width: "100%", height: h };
  const showCta = ios ? !!(content.text || "").trim() : !!content.action && content.action.type !== "none";
  return (
    <div data-testid="product-card" style={{ display: "flex", flexDirection: horizontal ? "row" : "column", alignItems: horizontal ? "center" : "stretch", border: `1px solid ${pick("#EEF0F4", "containerBorder")}`, borderRadius: 15, overflow: "hidden", background: pick("#FFFFFF", "surface"), textAlign: "left", boxShadow: content.showShadow ? "0 4px 8px rgba(0,0,0,0.12)" : undefined }}>
      <div data-testid="product-image" style={{ ...imgBox, background: pick("#F4F6FB", "placeholderFill"), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 38 }}>
        {content.imageUrl ? <img src={content.imageUrl} alt="" style={{ height: "100%", width: "100%", objectFit: "cover" }} /> : (ios ? "🛍" : "")}
      </div>
      <div style={{ padding: "11px 12px", display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 }}>
        {(content.badge || "").trim() && <span style={{ alignSelf: "flex-start", fontSize: 10, fontWeight: 800, color: "#fff", background: accent, borderRadius: 6, padding: "2px 7px" }}>{content.badge}</span>}
        <span style={{ fontWeight: 700, fontSize: 14, color: pick("#14161D", "text"), ...WORD_WRAP }}>{content.productName || ""}</span>
        {content.productDescription && <span style={{ fontSize: 12, color: pick("#666666", "secondaryText"), ...WORD_WRAP }}>{content.productDescription}</span>}
        <div><span style={{ fontWeight: 700, fontSize: 15, color: pick("#14161D", "text") }}>{content.price}</span>{content.originalPrice && <span style={{ fontSize: 12, color: "#AAB0BB", textDecoration: "line-through", marginLeft: 6 }}>{content.originalPrice}</span>}</div>
        {showCta && <button className="w-full text-sm font-semibold mt-1" onClick={() => run?.act?.(content.action)} style={{ minHeight: 40, borderRadius: 10, background: accent, color: content.buttonTextColor || "#FFFFFF" }}>{content.text || "View"}</button>}
      </div>
    </div>
  );
}

// Preview 16 (R2-19): carousel as the SDKs — one slide at a time with its image, title, description
// and CTA (iOS: a filled accent button; Android: bold accent text); arrows and dots page it;
// autoAdvance every 4 s (the CarouselRules / iOS default; the device payload carries no interval);
// loop wraps, otherwise it stops on the last slide.
function CarouselBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const run = useContext(PreviewRunCtx);
  const ios = useContext(SafeAreaCtx).platform === "ios";
  const slides = Array.isArray(content.slides) ? content.slides : [];
  const n = slides.length;
  const loop = content.loop !== false;
  const [index, setIndex] = useState(0);
  const step = useCallback((d: number) => setIndex((i) => {
    const next = i + d;
    if (next >= n) return loop ? 0 : i;
    if (next < 0) return loop ? n - 1 : i;
    return next;
  }), [n, loop]);
  const auto = content.autoAdvance === true && n > 1;
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => step(1), 4000);
    return () => clearInterval(t);
  }, [auto, step, index]);
  if (!n) return null;
  const cur = slides[Math.min(index, n - 1)] || {};
  const accent = "var(--brand-primary)";
  const showArrows = content.arrows !== false && n > 1;
  const showDots = content.indicators !== false && (ios || n > 1);
  const arrow = (label: string, glyph: string, d: number) => (
    <button type="button" aria-label={label} onClick={(e) => { e.stopPropagation(); step(d); }}
      className="flex items-center justify-center rounded-full" style={{ width: 30, height: 30, fontSize: 22, fontWeight: 700, lineHeight: 1, background: ios ? pick("rgba(255,255,255,0.85)", "closeDisc") : pick("rgba(0,0,0,0.4)", "closeDisc"), color: ios ? pick("#333333", "closeGlyph") : pick("#FFFFFF", "closeGlyph"), boxShadow: ios ? "0 0 0 1px rgba(0,0,0,0.08)" : undefined }}>{glyph}</button>
  );
  return (
    <div data-testid="carousel" className="w-full">
      <div className="flex flex-col" style={{ gap: 6, position: "relative", padding: !ios && showArrows ? "0 40px" : undefined }}>
        {cur.imageUrl && <img src={cur.imageUrl} alt={cur.title || ""} style={{ width: "100%", height: ios ? 110 : 140, objectFit: "cover", borderRadius: 10 }} />}
        {cur.title && <p style={{ fontWeight: 700, fontSize: 15, color: pick("#1A202C", "text"), ...WORD_WRAP }}>{cur.title}</p>}
        {cur.description && <p style={{ fontSize: 13, color: pick("#666666", "secondaryText"), ...WORD_WRAP }}>{cur.description}</p>}
        {(cur.cta || "").trim() && (ios
          ? <button type="button" onClick={() => run?.act?.(cur.action)} className="w-full font-semibold" style={{ minHeight: 40, borderRadius: 10, background: accent, color: "#FFFFFF", fontSize: 14 }}>{cur.cta}</button>
          : <button type="button" onClick={() => run?.act?.(cur.action)} className="text-left font-bold" style={{ color: accent, fontSize: 13 }}>{cur.cta}</button>)}
        {!ios && showArrows && <>
          <span style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)" }}>{arrow("Previous slide", "‹", -1)}</span>
          <span style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>{arrow("Next slide", "›", 1)}</span>
        </>}
      </div>
      {(showDots || (ios && showArrows)) && (
        <div className="flex items-center justify-between mt-2" style={{ minHeight: 30 }}>
          <span>{ios && showArrows && arrow("Previous slide", "‹", -1)}</span>
          {showDots && <div className="flex justify-center gap-1.5">{slides.map((_, i) => <span key={i} data-testid="carousel-dot" style={{ width: 8, height: 8, borderRadius: 999, background: i === index ? accent : pick("#D1D5DB", "secondaryText") }} />)}</div>}
          <span>{ios && showArrows && arrow("Next slide", "›", 1)}</span>
        </div>
      )}
    </div>
  );
}

// A data-capture block inside a Form has no Submit of its own (the form's Submit collects it).
const InFormCtx = createContext(false);
// P11 (DA-BUG-1387): a leaf block nested inside a column/row drops the top-level 16px
// side padding (px-4) so a narrow column gets its full width and a row's text sits next
// to its icon — matching the phones.
const InLayoutCtx = createContext(false);

// The standalone Submit both SDKs draw under rating / choice / input (accent fill, white, radius 10).
function SubmitButton({ label, disabled, onClick, fill, textColor }: { label: string; disabled?: boolean; onClick?: () => void; fill?: string; textColor?: string }) {
  return (
    <button type="button" disabled={disabled} onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      className="w-full font-semibold mt-1" style={{ minHeight: 44, borderRadius: 10, background: fill || "var(--brand-primary)", color: textColor || "#FFFFFF", fontSize: 15, opacity: disabled ? 0.5 : 1, ...WORD_WRAP }}>
      {label}
    </button>
  );
}

// Preview 17 (R2-20): rating as the SDKs — nothing selected at first; a tap selects; a Submit
// (disabled until a value is picked) submits, unless autoSubmit (submit on tap) or inside a form;
// then the thank-you replaces the control. NPS is always 0…10, thumbs 👎 👍, stars / emoji 1…max (≤ 11)
// (Core InAppRating / Android RatingRules). Glyphs as iOS RatingControl (☆ / ★), Android emoji faces.
export const RATING_HINTS: Record<string, string> = { stars: "Tap a star to rate", thumbs: "Tap 👍 or 👎", emoji: "Tap a face to rate", nps: "Tap a number from 0 to 10" };
function RatingBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const ios = useContext(SafeAreaCtx).platform === "ios";
  const inForm = useContext(InFormCtx);
  const type = content.ratingType || "stars";
  const active = content.activeColor || "var(--brand-primary)";
  const inactive = pick("#999999", "secondaryText");
  const max = Math.min(Math.max(Math.round(content.ratingMax || 5), 1), 11);
  const values = type === "nps" ? Array.from({ length: 11 }, (_, i) => i) : type === "thumbs" ? [1, 2] : Array.from({ length: max }, (_, i) => i + 1);
  const [picked, setPicked] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const faces = ios ? ["😞", "🙁", "😐", "🙂", "😍"] : ["😠", "🙁", "😐", "🙂", "😄"];
  const glyph = (v: number, i: number) => {
    if (type === "thumbs") return v === 1 ? "👎" : "👍";
    if (type === "nps") return String(v);
    if (type === "emoji") return ios ? faces[Math.min(values.length === 1 ? 0 : Math.floor(i * 4 / Math.max(values.length - 1, 1)), 4)] : faces[Math.min(Math.max(v - 1, 0), 4)];
    return picked != null && i <= values.indexOf(picked) ? "★" : "☆";
  };
  const spoken = (v: number) => type === "thumbs" ? (v === 1 ? "Thumbs down" : "Thumbs up") : type === "nps" ? `${v} of 10` : type === "emoji" ? `${v} of ${values.length}` : (v === 1 ? `1 star of ${values.length}` : `${v} stars of ${values.length}`);
  const autoSubmit = content.autoSubmit === true;
  const run = useContext(PreviewRunCtx);
  const submit = () => { setDone(true); run?.act?.(content.onSubmit); };
  const choose = (v: number) => { setPicked(v); if (!inForm && autoSubmit) submit(); };
  const ty = (content.thankYouMessage || "").trim();
  return (
    <div className="flex flex-col items-stretch w-full" style={{ gap: 8 }}>
      {content.label && <p className="text-center" style={{ fontWeight: 700, fontSize: 16, color: pick("#1A202C", "text"), ...WORD_WRAP }}>{content.label}</p>}
      {content.ratingSubLabel && <p className="text-center" style={{ fontSize: 13, color: pick("#666666", "secondaryText"), ...WORD_WRAP }}>{content.ratingSubLabel}</p>}
      {done && ty ? <p className="text-center" style={{ fontSize: 14, color: active, ...WORD_WRAP }}>{ty}</p> : (<>
        <div className="flex" style={{ gap: type === "nps" ? 3 : 6, justifyContent: "center" }}>
          {values.map((v, i) => {
            const on = picked === v;
            const lit = type === "stars" ? picked != null && i <= values.indexOf(picked) : on;
            return (
              <button key={v} type="button" aria-label={spoken(v)} onClick={(e) => { e.stopPropagation(); choose(v); }}
                className="flex-1 flex items-center justify-center" style={type === "nps"
                  ? { height: 36, minWidth: 0, borderRadius: 6, border: `1px solid ${active}`, fontSize: 14, backgroundColor: on ? active : "transparent", color: on ? "#FFFFFF" : inactive }
                  // V3-P13 (DA-BUG-1421): minWidth 0 lets the flex items shrink, and >5 glyphs scale down so
                  // 10 stars share the width without clipping the first/last (mirrors the SDK).
                  : { fontSize: values.length > 5 ? Math.max(16, Math.floor(130 / values.length)) : 26, lineHeight: 1.2, minWidth: 0, maxWidth: 44, borderRadius: 8, color: lit ? active : inactive, backgroundColor: type !== "stars" && on ? withOpacity(active, 0.15) : "transparent" }}>
                {glyph(v, i)}
              </button>
            );
          })}
        </div>
        {(content.lowLabel || content.highLabel) && <div className="flex justify-between" style={{ fontSize: 11, color: pick("#737373", "secondaryText") }}><span>{content.lowLabel}</span><span>{content.highLabel}</span></div>}
        {/* RRS sends no submitText for a rating, so the phones say "Submit". */}
        {!inForm && !autoSubmit && <SubmitButton label="Submit" disabled={picked == null} onClick={submit} fill={content.submitColor} textColor={content.submitTextColor} />}
      </>)}
    </div>
  );
}

// Preview 18 (R2-21): choice as the SDKs (iOS ChoiceControl / Android choiceBlock) — bordered options,
// a tap selects (one, or several when multiple), "grid" is two per row, and a standalone choice
// draws its own Submit (a form's Submit collects it instead).
function ChoiceBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const run = useContext(PreviewRunCtx);
  const inForm = useContext(InFormCtx);
  const options = Array.isArray(content.options) ? content.options : [];
  const [sel, setSel] = useState<string[]>([]);
  const toggle = (o: string) => setSel((cur) => content.multiple ? (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]) : [o]);
  const grid = content.choiceLayout === "grid";
  return (
    <div className="flex flex-col w-full" style={{ gap: 8 }}>
      {content.label && <p style={{ fontWeight: 700, fontSize: 16, color: pick("#1A202C", "text"), ...WORD_WRAP }}>{content.label}{content.required ? " *" : ""}</p>}
      <div data-testid="choice-options" style={grid ? { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 } : { display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((o, i) => {
          const on = sel.includes(o);
          return (
            <button key={i} type="button" aria-pressed={on} onClick={(e) => { e.stopPropagation(); toggle(o); }}
              style={{ padding: "10px 14px", borderRadius: 10, fontSize: 15, textAlign: "center", border: `1.5px solid ${on ? "var(--brand-primary)" : pick("#D9D9D9", "inputBorder")}`, background: on ? "var(--brand-primary)" : "transparent", color: on ? "#FFFFFF" : pick("#333333", "text"), ...WORD_WRAP }}>
              {o}
            </button>
          );
        })}
      </div>
      {!inForm && <SubmitButton label={content.submitText || "Submit"} onClick={() => run?.act?.(content.onSubmit)} />}
    </div>
  );
}

// Preview 18 (R2-21): input as the SDKs (iOS InputControl / Android inputBlock) — label (" *" when
// required), a filled field; multiline is at least three lines (Android minLines = 3); a standalone
// input draws its own Submit.
function InputBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const run = useContext(PreviewRunCtx);
  const inForm = useContext(InFormCtx);
  const multiline = content.inputType === "multiline";
  return (
    <div className="flex flex-col w-full" style={{ gap: 6 }}>
      {content.label && <p style={{ fontWeight: 700, fontSize: 15, color: pick("#1A202C", "text"), ...WORD_WRAP }}>{content.label}{content.required ? " *" : ""}</p>}
      <div data-testid="input-field" className="w-full text-sm" style={{ padding: 12, borderRadius: 10, minHeight: multiline ? 84 : undefined, border: `1px solid ${pick("#D1D5DB", "inputBorder")}`, backgroundColor: pick("#F3F4F6", "inputFill"), color: pick("#9CA3AF", "placeholder") }}>{content.placeholder || ""}</div>
      {!inForm && <SubmitButton label={content.submitText || "Submit"} onClick={() => run?.act?.(content.onSubmit)} />}
    </div>
  );
}

// Preview 19 (R2-22): consent as the SDKs — the box starts as preChecked says and a tap toggles it
// (Android: accent fill + white ✓; iOS: accent ✓ on the outlined box); a required box marks its text
// with " *" (red #DC2626 on Android, markRequired; plain on iOS, marked()).
function ConsentBlock({ content }: { content: BlockContent }) {
  const pick = usePick();
  const ios = useContext(SafeAreaCtx).platform === "ios";
  const [checked, setChecked] = useState(!!content.preChecked);
  useEffect(() => setChecked(!!content.preChecked), [content.preChecked]);
  const accent = "var(--brand-primary)";
  return (
    <div className="flex items-start gap-2 w-full text-left">
      <span role="checkbox" aria-checked={checked} tabIndex={0} onClick={(e) => { e.stopPropagation(); setChecked((c) => !c); }}
        className="flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${accent}`, flex: "none", fontSize: 12, fontWeight: 700,
          backgroundColor: checked && !ios ? accent : "transparent", color: ios ? accent : "#FFFFFF" }}>{checked ? "✓" : ""}</span>
      <p className="text-xs" style={{ color: pick("#4B5563", "text"), lineHeight: 1.45, ...WORD_WRAP }}>
        {content.text || ""}{content.link && content.linkUrl && <> <span style={{ color: accent, fontWeight: 600 }}>{content.link}</span></>}
        {content.required && <span data-testid="required-mark" style={ios ? undefined : { color: "#DC2626" }}> *</span>}
      </p>
    </div>
  );
}

// Gap 3 (DA-BUG-1104) — wrap by word (hyphenate where the browser can), never letter by letter.
const WORD_WRAP: React.CSSProperties = { overflowWrap: "break-word", wordBreak: "normal", hyphens: "auto" };
function withOpacity(color: string, alpha: number): string {
  if (alpha >= 1) return color;
  const c = parseColour(resolveCssColor(color));
  if (!c) return color;
  const a = Math.round(Math.max(0, alpha) * (parseInt(c.slice(7, 9), 16) / 255) * 100) / 100;
  return `rgba(${parseInt(c.slice(1, 3), 16)}, ${parseInt(c.slice(3, 5), 16)}, ${parseInt(c.slice(5, 7), 16)}, ${a})`;
}

// ─── BlockView ────────────────────────────────────────────────────────────────

// Context threaded through nested layout blocks so a child block can be selected,
// edited, deleted, duplicated, and accept drop-into — all by id, across the tree.
interface BlockTreeCtx {
  selectedId: string | null;
  select: (id: string | null) => void;
  update: (id: string, content: Partial<BlockContent>) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => void;
  dropInto: (parentId: string) => void;
}

interface BlockViewProps {
  block: Block;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (content: Partial<BlockContent>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  tree?: BlockTreeCtx;
}

function BlockActionBar({ onDelete, onDuplicate }: { onDelete: () => void; onDuplicate: () => void }) {
  return (
    <div
      className="absolute -top-7 right-1 flex items-center gap-1 rounded-md px-1.5 py-1 z-30 shadow-lg"
      style={{ backgroundColor: "#1A202C" }}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={12} className="text-gray-400 cursor-grab" />
      <button className="text-gray-300 hover:text-white p-0.5 transition-colors" onClick={onDuplicate}><Copy size={12} /></button>
      <button className="text-gray-300 hover:text-red-400 p-0.5 transition-colors" onClick={onDelete}><Trash2 size={12} /></button>
    </div>
  );
}

export function BlockView({ block, isSelected, onSelect, onUpdate, onDelete, onDuplicate, tree }: BlockViewProps) {
  const { content } = block;
  const pick = usePick();
  const previewRun = useContext(PreviewRunCtx);
  const safe = useContext(SafeAreaCtx);
  const inLayout = useContext(InLayoutCtx);   // P11: nested in a column/row → no side padding
  const [failedImage, setFailedImage] = useState<string | null>(null);   // Preview 23: URL that failed to load

  // Layout blocks (container / row / columns) nest child blocks. They render a
  // styled region that accepts drop-into and renders each child as its own
  // (selectable, editable) BlockView via the tree context.
  if (isLayoutType(block.type)) {
    const kids = content.children || [];
    const renderKids = (node: React.ReactNode) => (
      <div
        data-block-id={block.id}
        className={`relative cursor-pointer ${isSelected ? "ring-2 ring-blue-600 ring-offset-1 rounded" : ""}`}
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); tree?.dropInto(block.id); }}
      >
        {isSelected && <BlockActionBar onDelete={onDelete} onDuplicate={onDuplicate} />}
        {node}
      </div>
    );
    const childView = (child: Block) => (
      <BlockView key={child.id} block={child}
        isSelected={tree?.selectedId === child.id}
        onSelect={() => tree?.select(child.id)}
        onUpdate={(c) => tree?.update(child.id, c)}
        onDelete={() => tree?.remove(child.id)}
        onDuplicate={() => tree?.duplicate(child.id)}
        tree={tree} />
    );
    // Preview 21 (R2-13 / R2-26): editor affordances never show in Preview (an empty layout is empty).
    const emptyHint = previewRun ? null : (
      <div className="flex items-center justify-center text-xs" style={{ minHeight: 48, color: pick("#A0AEC0", "glyph"), border: `1px dashed ${pick("#CBD5E0", "containerBorder")}`, borderRadius: 8 }}>
        Drop blocks here
      </div>
    );
    const distMap: Record<string, string> = { start: "flex-start", center: "center", end: "flex-end", between: "space-between", around: "space-around" };
    const alignMap: Record<string, string> = { start: "flex-start", center: "center", end: "flex-end", stretch: "stretch" };
    if (block.type === "container") {
      return renderKids(
        <div data-testid="container-box" style={{
          // Gap 3: opacity fades the container's background only, never its children.
          background: withOpacity(content.containerBg || "#FFFFFF", (content.containerBgOpacity ?? 100) / 100),
          padding: content.containerPadding ?? 16,
          borderRadius: content.containerRadius ?? 12,
          border: content.containerBorder === false ? "none" : `${content.containerBorderWidth ?? 1}px solid ${content.containerBorderColor || "#E2E8F0"}`,
          boxShadow: content.containerShadow ? "0 4px 12px rgba(0,0,0,0.08)" : undefined,
          margin: "6px 16px",
        }}>
          {kids.length ? kids.map(childView) : emptyHint}
        </div>,
      );
    }
    if (block.type === "row") {
      // V3-X4 (phone parity): buttons in a row share the width equally (DA-BUG-0486); other children keep their
      // natural size so start / center / end pack them (icon + "Member since 2021" side by side).
      const rowChildFlex = (content.distribution === "between" || content.distribution === "around") ? "none"
        : kids.some((c) => c.type === "button") ? 1 : "0 1 auto";
      return renderKids(
        <div style={{ display: "flex", flexDirection: "row", gap: content.gap ?? 8, padding: content.containerPadding ?? 0,
          justifyContent: distMap[content.distribution || "start"], alignItems: alignMap[content.alignCross || "center"], margin: "6px 16px" }}>
          <InLayoutCtx.Provider value={true}>{kids.length ? kids.map((c) => <div key={c.id} style={{ flex: rowChildFlex, minWidth: 0 }}>{childView(c)}</div>) : emptyHint}</InLayoutCtx.Provider>
        </div>,
      );
    }
    if (block.type === "form") {
      // Form groups its data-capture children into ONE submission; a single Submit
      // button and (if configured) a small API-destination badge show it's grouped.
      const hasApi = !!(content.formApi && content.formApi.endpoint);
      return renderKids(
        <div style={{
          background: content.containerBg || "#FFFFFF",
          padding: content.containerPadding ?? 16,
          borderRadius: content.containerRadius ?? 12,
          border: `${content.containerBorderWidth ?? 1}px solid ${content.containerBorderColor || "#E2E8F0"}`,
          margin: "6px 16px",
        }}>
          {!previewRun && <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "#A0AEC0" }}>Form</span>
            {hasApi && (
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#EBF8FF", color: "#2B6CB0" }}>
                API → {content.formApi!.method}
              </span>
            )}
          </div>}
          <InFormCtx.Provider value={true}>
          <div style={{ display: "flex", flexDirection: "column", gap: content.gap ?? 10 }}>
            {kids.length ? kids.map(childView) : emptyHint}
          </div>
          </InFormCtx.Provider>
          <button className="w-full mt-3" onClick={() => previewRun?.act?.(content.onSubmit)} style={{
            padding: "10px 16px", borderRadius: 10, border: "none", fontWeight: 600, fontSize: 14,
            background: "var(--brand-primary)", color: "#FFFFFF", cursor: "default",
          }}>
            {content.submitText || "Submit"}
          </button>
        </div>,
      );
    }
    if (block.type === "column") {
      // Gap 2 — one column slot: its own drop zone and child list.
      return renderKids(
        <div style={{ minHeight: 48, display: "flex", flexDirection: "column", gap: 4 }}>
          <InLayoutCtx.Provider value={true}>{kids.length ? kids.map(childView) : emptyHint}</InLayoutCtx.Provider>
        </div>,
      );
    }
    // columns — each child is a "column" slot (legacy children were migrated on load)
    return renderKids(
      <div data-testid={`columns-${block.id}`} style={{ display: "grid", gridTemplateColumns: `repeat(${kids.length || content.columnCount || 2}, minmax(0,1fr))`, gap: content.gap ?? 8, padding: content.containerPadding ?? 0, alignItems: alignMap[content.alignCross || "start"], margin: "6px 16px" }}>
        <InLayoutCtx.Provider value={true}>{kids.length ? kids.map((c) => <div key={c.id} style={{ minWidth: 0 }}>{childView(c)}</div>) : emptyHint}</InLayoutCtx.Provider>
      </div>,
    );
  }

  const fontWeightClass = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  }[content.fontWeight || "normal"];

  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[content.alignment || "left"];

  // Image block — edge-to-edge (no px-4 padding)
  if (block.type === "image") {
    // Preview 23 (F12): a URL that fails to load shows what the phones show — Android (A-5,
    // urlImageBlock onFail): the alt text centred in the box, or the block collapses without one;
    // iOS (image() loadImage fallback): the alt text, else its 🖼 glyph, in the box. The canvas keeps a
    // slim placeholder where Android collapses, so the block stays selectable.
    const broken = !!content.imageUrl && failedImage === content.imageUrl;
    const alt = (content.imageAlt || "").trim();
    const h = content.imageHeight || 140;
    if (broken && !alt && safe.platform !== "ios" && previewRun) return null;
    const brokenView = !broken ? null : (alt || safe.platform === "ios")
      ? <div data-testid="image-alt" className="w-full flex items-center justify-center text-center" style={{ height: h, padding: 12, backgroundColor: pick(safe.platform === "ios" ? "#F2F2F2" : "#EEEEEE", "videoFrame"), color: pick("#4B5563", "glyph"), fontSize: safe.platform === "ios" ? 32 : 14, ...WORD_WRAP }}>{alt || "🖼"}</div>
      : <div className="w-full flex items-center justify-center text-xs" style={{ height: 28, color: "#A0AEC0", border: "1px dashed #CBD5E0" }}>Image could not load (hidden on Android)</div>;
    return (
      <div
        data-block-id={block.id}
        className={`relative cursor-pointer ${isSelected ? "ring-2 ring-blue-600 ring-offset-1 rounded" : ""}`}
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
      >
        {isSelected && <BlockActionBar onDelete={onDelete} onDuplicate={onDuplicate} />}
        {broken ? brokenView : content.imageUrl ? (
          <img
            src={content.imageUrl}
            alt={content.imageAlt || ""}
            onError={() => setFailedImage(content.imageUrl || null)}
            className="w-full"
            // Preview 13 (R2-4): fit as the SDKs (contain → aspect-fit, fill → stretch, else cover).
            style={{ height: content.imageHeight || 140, display: "block", objectFit: content.fit === "contain" || content.fit === "fill" ? content.fit : "cover" }}
          />
        ) : previewRun ? (
          // No image: the payload becomes an emoji image — iOS draws its default 🔔, Android an empty
          // #EEEEEE box (60…360 px).
          safe.platform === "ios"
            ? <div className="w-full flex items-center justify-center" style={{ height: Math.min(Math.max(content.imageHeight || 160, 24), 72) + 16, fontSize: Math.min(Math.max(content.imageHeight || 160, 24), 72) }}>🔔</div>
            : <div className="w-full" style={{ height: Math.min(Math.max(content.imageHeight || 200, 60), 360), backgroundColor: pick("#EEEEEE", "videoFrame") }} />
        ) : (
          <div
            className="w-full flex flex-col items-center justify-center gap-1"
            style={{ height: content.imageHeight || 140, backgroundColor: pick("#F7FAFC", "placeholderFill"), border: `1px dashed ${pick("#CBD5E0", "containerBorder")}` }}
          >
            <ImageIcon size={22} style={{ color: pick("#A0AEC0", "glyph") }} />
            <span className="text-xs" style={{ color: pick("#A0AEC0", "glyph") }}>No image uploaded</span>
          </div>
        )}
      </div>
    );
  }

  let inner: React.ReactNode = null;

  const isBlank = !content.text || content.text.trim() === "";
  // PB-5 (DA-BUG-1421): on the editor canvas, show merge tags the way the phone will — ${name|foodie}
  // renders "foodie" (its fallback); a tag with no fallback stays visible as ${name}. The Preview path
  // already resolves tags with sample values, so only substitute off-preview.
  const shownText = previewRun ? (content.text || "") : resolveMergeTags(content.text || "", {}, () => true);
  // BUG-07 — an empty text/heading must read as an editor placeholder, never as
  // shipped content. Dashed outline + greyed label, never rendered on publish.
  const emptyState = (label: string) => previewRun ? null : (
    <span className={`${alignClass} block rounded`} style={{
      border: "1px dashed #CBD5E0", color: "#A0AEC0", fontSize: 12, fontStyle: "italic",
      padding: "2px 6px",
    }}>{label}</span>
  );

  switch (block.type) {
    case "heading":
      inner = isBlank ? emptyState("Empty heading") : (
        <p className={`${fontWeightClass} ${alignClass} leading-tight`}
          style={{ fontSize: content.fontSize || 18, color: content.color || "#1A202C", ...WORD_WRAP }}>
          {shownText}
        </p>
      );
      break;

    case "text":
      inner = isBlank ? emptyState("Empty text") : (
        <p className={`${fontWeightClass} ${alignClass}`}
          style={{ fontSize: content.fontSize || 14, color: content.color || "#4A5568", lineHeight: content.lineHeight || 1.5, ...WORD_WRAP }}>
          {shownText}
        </p>
      );
      break;

    case "button": {
      const bs = content.buttonStyle || "primary";
      const accent = content.buttonColor || "var(--brand-primary)";
      let btnStyle: React.CSSProperties = { borderRadius: content.buttonRadius ?? 12 };
      const label = buttonLabelColor(content);
      if (bs === "primary") {
        btnStyle = { ...btnStyle, backgroundColor: accent, color: label };
      } else if (bs === "secondary") {
        btnStyle = { ...btnStyle, backgroundColor: content.buttonColor || "#EDF2F7", color: label };
      } else if (bs === "outline") {
        btnStyle = { ...btnStyle, border: `2px solid ${accent}`, color: label };
      } else {
        btnStyle = { ...btnStyle, color: label };
      }
      // Preview 22 (F13 / F25): an empty label (after merge tags) is not rendered on either phone (X-5);
      // the canvas shows an "Empty button" placeholder instead of a "Button" filler.
      inner = isBlank ? emptyState("Empty button") : (
        <button className="w-full py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 cursor-pointer" style={btnStyle}
          onClick={() => previewRun?.act?.(content.action)}>
          {content.text}
        </button>
      );
      break;
    }

    case "divider":
      // P12 (DA-BUG-1388): honour lineStyle (solid|dashed|dotted) — the phones draw it; Preview drew solid.
      inner = <hr style={{ borderTop: `${content.thickness || 1}px ${content.lineStyle || "solid"} ${content.dividerColor || "#E2E8F0"}`, borderBottom: "none", borderLeft: "none", borderRight: "none" }} />;
      break;

    case "spacer":
      inner = <div style={{ height: content.spacerHeight || 16 }} />;
      break;

    case "icon": {
      // Gap 4 / D7 (DA-BUG-1412): alignment moves the whole badge+labels group in BOTH layouts. Stacked
      // (default) = badge above the labels; inline = badge beside a vertical label holder. An empty icon
      // draws no badge; icon text is clipped to the badge.
      const iconInline = content.iconLayout === "inline";
      const alignVal = content.alignment === "left" ? "flex-start" : content.alignment === "right" ? "flex-end" : "center";
      const iconChip = (content.iconValue || "").trim() ? (
        <div style={{ width: content.iconSize || 48, height: content.iconSize || 48, background: content.iconBg || "#EBF8FF",
          borderRadius: content.iconShape === "square" ? 4 : content.iconShape === "rounded" ? 14 : "50%", overflow: "hidden", whiteSpace: "nowrap",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: (content.iconSize || 48) * 0.5, flex: "none" }}>
          {content.iconValue}
        </div>
      ) : null;
      const iconLabels = (
        <>
          {content.iconLabel && <p className={`text-sm font-semibold ${iconInline ? "" : "mt-1.5"}`} style={{ color: pick("#1A202C", "text") }}>{content.iconLabel}</p>}
          {content.iconSubLabel && <p className="text-xs" style={{ color: pick("#718096", "secondaryText") }}>{content.iconSubLabel}</p>}
        </>
      );
      inner = iconInline ? (
        <div data-testid="icon-block" data-icon-layout="inline" className="flex flex-row items-center" style={{ justifyContent: alignVal, gap: 10 }}>
          {iconChip}
          <div className="flex flex-col" style={{ textAlign: (content.alignment as any) || "left" }}>{iconLabels}</div>
        </div>
      ) : (
        <div data-testid="icon-block" data-icon-layout="stacked" className={`flex flex-col ${alignClass}`}
          style={{ textAlign: (content.alignment as any) || "center", alignItems: alignVal }}>
          {iconChip}
          {iconLabels}
        </div>
      );
      break;
    }

    case "feature-list":
      // Preview 22 (R2-11): no "Feature one / two" filler — the phones draw an empty list.
      inner = !(content.items && content.items.length) ? emptyState("Empty list") : (
        <div className="flex flex-col gap-1.5">
          {(content.items || []).map((it, i) => (
            <div key={i} className="flex items-start gap-2 text-sm" style={{ color: content.color || "#384150" }}>
              {/* P17 (DA-BUG-1390): the check matches the item text colour (dark), not brand blue — the phones draw a dark ✓. */}
              <Check size={15} style={{ color: content.color || "#384150", flex: "none", marginTop: 2 }} />
              <span>{it}</span>
            </div>
          ))}
        </div>
      );
      break;

    case "countdown":
      inner = <CountdownBlock content={content} />;
      break;

    case "coupon":
      inner = <CouponBlock content={content} />;
      break;

    case "product":
      inner = <ProductBlock content={content} />;
      break;

    case "rating":
      inner = <RatingBlock content={content} />;
      break;

    case "nps":
      inner = (
        <div className="flex flex-col gap-1.5 w-full">
          {content.label && <p className="text-sm text-center" style={{ color: "#4A5568" }}>{content.label}</p>}
          <div className="flex gap-0.5">{Array.from({ length: (content.max ?? 10) - (content.min ?? 0) + 1 }).map((_, i) => (
            <div key={i} className="flex-1 flex items-center justify-center" style={{ aspectRatio: "1", borderRadius: 6, background: i >= 9 ? "var(--brand-primary)" : "#f1f3f7", color: i >= 9 ? "#fff" : "#8a93a3", fontSize: 9.5, fontWeight: 700 }}>{(content.min ?? 0) + i}</div>
          ))}</div>
          <div className="flex justify-between" style={{ fontSize: 9.5, color: "#9aa1ad" }}><span>{content.lowLabel || "Not likely"}</span><span>{content.highLabel || "Very likely"}</span></div>
        </div>
      );
      break;

    case "choice":
      inner = <ChoiceBlock content={content} />;
      break;

    case "input":
      inner = <InputBlock content={content} />;
      break;

    case "consent":
      inner = <ConsentBlock content={content} />;
      break;

    case "progress": {
      const pct = Math.max(0, Math.min(100, Math.round(((content.current || 0) / (content.max || 100)) * 100)));
      const h = content.progressHeight || 8;
      const rad = content.progressStyle === "square" ? 2 : 999;
      inner = (
        <div className="w-full">
          <div style={{ height: h, borderRadius: rad, background: content.trackColor || "#eef1f6", overflow: "hidden" }}><div style={{ height: "100%", width: `${pct}%`, borderRadius: rad, background: content.accent || "var(--brand-primary)" }} /></div>
          <div className="flex justify-between mt-1.5" style={{ fontSize: 11, color: content.labelColor || pick("#4b5563", "secondaryText"), fontWeight: 600 }}><span>{content.label}</span>{content.showPercent !== false && <span style={{ color: content.accent || "var(--brand-primary)" }}>{pct}%</span>}</div>
        </div>
      );
      break;
    }

    case "carousel":
      inner = <CarouselBlock content={content} />;
      break;

    case "video":
      inner = <VideoBlock content={content} />;
      break;
  }

  if (previewRun && inner == null) return null;   // nothing drawn on the phone → no padded row
  return (
    <div
      data-block-id={block.id}
      className={`relative cursor-pointer ${inLayout ? "px-0" : "px-4"} py-1.5 ${isSelected ? "ring-2 ring-blue-600 ring-offset-1 rounded" : ""}`}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      {isSelected && <BlockActionBar onDelete={onDelete} onDuplicate={onDuplicate} />}
      {inner}
    </div>
  );
}

// Preview 8 + 9 + 10 (F24, R2-29, R2-3 / R2-9 / R2-30): the ✕ is its OWN ROW at the top of the card,
// as both SDKs draw it (iOS closeButton: a row above the blocks; Android closeButton: a full-width row,
// padding 12, or 20 for "inside", gravity start for top-left), so it reserves its space and never
// overlaps the first block. Light: grey ✕ (#9CA3AF); dark: ✕ on a disc (D-5).
function CloseRow({ dark = false, position = "top-right", overlay = false }: { dark?: boolean; position?: string; overlay?: boolean }) {
  const pick = usePick();
  const themeDark = !!useContext(PreviewTheme);
  const run = useContext(PreviewRunCtx);
  const left = position === "top-left";
  const pad = position === "inside" ? 20 : 12;
  // DA-BUG-1401: overlay mode (image-hero modal) always draws the disc over the image, like dark.
  const onDisc = dark || overlay;
  const disc = onDisc || themeDark;
  const glyph = (
    <span aria-label="Close" role={run?.act ? "button" : "img"} onClick={() => run?.act?.({ type: "dismiss" })} className="flex items-center justify-center rounded-full cursor-pointer"
      style={disc
        ? { width: 30, height: 30, fontSize: 15, backgroundColor: onDisc ? "rgba(0,0,0,0.35)" : pick("rgba(0,0,0,0.08)", "closeDisc"), color: onDisc ? "#FFFFFF" : pick("#4A5568", "closeGlyph") }
        : { fontSize: 18, lineHeight: 1, color: "#9CA3AF" }}>✕</span>
  );
  // DA-BUG-1401: overlaid on the hero image's top corner — absolute, no reserved row.
  if (overlay) {
    return (
      <div data-testid="close-x" className={`absolute ${run?.act ? "" : "pointer-events-none"}`} style={{ top: 10, [left ? "left" : "right"]: 10, zIndex: 3 }}>
        {glyph}
      </div>
    );
  }
  return (
    <div data-testid="close-x" className={`flex ${run?.act ? "" : "pointer-events-none"}`} style={{ justifyContent: left ? "flex-start" : "flex-end", paddingTop: pad, paddingLeft: pad, paddingRight: pad, paddingBottom: pad / 2 }}>
      {glyph}
    </div>
  );
}

// ─── Message Overlay ──────────────────────────────────────────────────────────

interface MessageOverlayProps {
  messageType: MessageType;
  messageStyle: MessageStyle;
  blocks: Block[];
  selectedBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
  onUpdateBlock: (id: string, content: Partial<BlockContent>) => void;
  onDeleteBlock: (id: string) => void;
  onDuplicateBlock: (id: string) => void;
  onDropInto: (parentId: string) => void;
  dragOverIndex: number | null;
}

export function MessageOverlay({
  messageType, messageStyle, blocks, selectedBlockId,
  onSelectBlock, onUpdateBlock, onDeleteBlock, onDuplicateBlock, onDropInto, dragOverIndex,
}: MessageOverlayProps) {
  const safe = useContext(SafeAreaCtx);
  const inPreview = !!useContext(PreviewRunCtx);
  const firstImage = blocks.find(b => b.type === "image");

  const tree: BlockTreeCtx = {
    selectedId: selectedBlockId, select: onSelectBlock, update: onUpdateBlock,
    remove: onDeleteBlock, duplicate: onDuplicateBlock, dropInto: onDropInto,
  };

  const renderBlocks = () => (
    <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
      {blocks.map((block, idx) => (
        <div key={block.id} data-blk-idx={idx}>
          {dragOverIndex === idx && <div className="h-1 mx-4 my-1 rounded-full transition-all" style={{ backgroundColor: "var(--brand-primary)" }} />}
          <BlockView
            block={block}
            isSelected={selectedBlockId === block.id}
            onSelect={() => onSelectBlock(block.id)}
            onUpdate={(c) => onUpdateBlock(block.id, c)}
            onDelete={() => onDeleteBlock(block.id)}
            onDuplicate={() => onDuplicateBlock(block.id)}
            tree={tree}
          />
        </div>
      ))}
      {dragOverIndex === blocks.length && <div className="h-1 mx-4 my-1 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
    </div>
  );

  if (blocks.length === 0) {
    if (inPreview) return null;   // Preview 21: no editor hint in Preview
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center p-4">
          <div className="text-3xl mb-2" style={{ color: "#CBD5E0" }}>+</div>
          <div className="text-xs" style={{ color: "#A0AEC0" }}>Drag blocks here</div>
        </div>
      </div>
    );
  }

  switch (messageType) {
    case "modal": {
      // DA-BUG-1401 (opt-in): image-hero modal — when the first block is an image, render it edge-to-edge
      // at the top (the card's overflow clips its rounded top corners) and overlay the close button on its
      // top-right corner instead of a reserved close row, so there's no blank strip above the hero.
      const heroMode = !!messageStyle.imageHeroTop && blocks[0]?.type === "image";
      return (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: `rgba(0,0,0,${messageStyle.backdropOpacity / 100})` }}
          onClick={() => onSelectBlock(null)}
        >
          <div
            data-testid="message-card"
            className="relative overflow-y-auto shadow-2xl"
            style={{ width: `${messageStyle.width ?? 90}%`, backgroundColor: messageStyle.background, borderRadius: messageStyle.cornerRadius, maxHeight: "85%",
              // FEAT-INAPP-MODAL-BG: a modal may carry a full-card background image + scrim, with the
              // blocks rendered on top — the same presentation.backgroundImage the fullscreen surface uses.
              ...(messageStyle.backgroundImage?.url ? { backgroundImage: `url(${messageStyle.backgroundImage.url})`, backgroundSize: messageStyle.backgroundImage.fit || "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" } : {}) }}
            onClick={(e) => e.stopPropagation()}
          >
            {messageStyle.backgroundImage?.url && (
              <div data-testid="modal-scrim" className="absolute inset-0 pointer-events-none"
                style={{ borderRadius: messageStyle.cornerRadius, backgroundColor: messageStyle.scrimColor || "#000000", opacity: Math.max(0, Math.min(0.8, messageStyle.scrimOpacity || 0)) }} />
            )}
            {/* DA-BUG-1401: hero mode overlays the ✕ on the image's top corner; no reserved row means the
                first image (already full-width, no padding) meets the card's top edge and the overflow
                clips its rounded top corners. */}
            {heroMode && messageStyle.closeButton && <CloseRow overlay position={messageStyle.closeButtonPosition} />}
            <div className="relative">
              {!heroMode && messageStyle.closeButton && <CloseRow dark={!!messageStyle.backgroundImage?.url} position={messageStyle.closeButtonPosition} />}
              {renderBlocks()}
              <div className="h-2" />
            </div>
          </div>
        </div>
      );
    }

    case "banner": {
      // Preview 5 (F18): both SDKs stack EVERY block of a banner in Layers order in one card
      // (iOS presentOverlay / Android showOverlay: renderer.views(for: blocks)), not a fixed
      // title / body / first-button slot bar. Banner corners default to 0.
      // Preview 11 (R2-31): clear of the home indicator / navigation bar — iOS pins the card 8 pt
      // inside the safe area (8 pt side insets); Android is full width, pushed off the system bars.
      const isTop = messageStyle.position !== "bottom";
      const edge = safe.platform === "ios" ? 8 : 0;
      return (
        <div data-testid="message-card"
          className={`absolute ${isTop ? "top-0" : "bottom-0"} shadow-lg overflow-y-auto`}
          style={{ backgroundColor: messageStyle.background || "#FFFFFF", maxHeight: "40%", paddingTop: 8, paddingBottom: 8,
            // Preview 12 (F19 / F27): a top banner starts below the status bar / Dynamic Island.
            left: edge, right: edge, ...(isTop ? { top: safe.top + edge } : { bottom: safe.bottom + edge }) }}
          onClick={(e) => e.stopPropagation()}
        >
          {messageStyle.closeButton && <CloseRow position={messageStyle.closeButtonPosition} />}
          {renderBlocks()}
        </div>
      );
    }

    case "slide-in": {
      // Preview 6 + 7 (F23, R2-34 / R2-35): a slide-in renders EVERY block type in Layers order through
      // the shared block renderer (containers keep their drop zones), as both SDKs do. Honour the
      // authored corner (position) and width. Height capped at 60 % (Android overlayHeightCap).
      const pos = messageStyle.position || "bottom-right";
      const cornerClass =
        pos === "bottom-left" ? "bottom-4 left-3" :
        pos === "top-right" ? "top-10 right-3" :
        pos === "top-left" ? "top-10 left-3" :
        "bottom-4 right-3"; // bottom-right (default)
      const w = Math.min(Math.max(messageStyle.width ?? 60, 40), 92); // % of device width
      return (
        <div data-testid="message-card" className={`absolute ${cornerClass} shadow-2xl overflow-y-auto`}
          style={{ width: `${w}%`, maxHeight: "60%", backgroundColor: messageStyle.background || "#FFFFFF", borderRadius: messageStyle.cornerRadius ?? 14, paddingTop: 8, paddingBottom: 8 }}
          onClick={(e) => e.stopPropagation()}>
          {messageStyle.closeButton && <CloseRow position={messageStyle.closeButtonPosition} />}
          {renderBlocks()}
        </div>
      );
    }

    case "fullscreen": {
      // Gap 13: the explicit background image wins; the legacy first-image rule stays for old messages.
      const bgSetting = messageStyle.backgroundImage?.url;
      const bgImage = bgSetting || firstImage?.content.imageUrl;
      return (
        <div data-testid="fullscreen-scroller" className="absolute inset-0 flex flex-col overflow-y-auto"
          style={{
            // Preview 12 (R2-33): content and the ✕ stay inside the safe area (Android pads the card by
            // the system-bar + cutout insets, showDialog).
            paddingTop: safe.top,
            paddingBottom: safe.bottom,
            // BUG-09 — the canvas must reflect the chosen Background, not a hard-coded
            // gradient. A background image still wins when one is present.
            backgroundColor: messageStyle.background || "#FFFFFF",
            ...(bgImage ? { backgroundImage: `url(${bgImage})`, backgroundSize: bgSetting ? (messageStyle.backgroundImage?.fit || "cover") : "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" } : {}),
          }}
          onClick={() => onSelectBlock(null)}>
          {bgSetting && (
            <div data-testid="fullscreen-scrim" className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: messageStyle.scrimColor || "#000000", opacity: Math.max(0, Math.min(0.8, messageStyle.scrimOpacity || 0)) }} />
          )}
          {/* Preview 4 (R2-25): both SDKs draw the ✕ on every fullscreen message (fullscreen || dismissible). */}
          <div className="relative"><CloseRow dark={!!bgImage} position={messageStyle.closeButtonPosition} /></div>
          {/* F10: centred when it fits, scrollable from the top when it doesn't. */}
          <div data-testid="fullscreen-body" className="relative" style={{ marginTop: "auto", marginBottom: "auto" }}>
            {renderBlocks()}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}

// ─── Sample App Background ────────────────────────────────────────────────────

function SampleAppBackground() {
  return (
    <div className="absolute inset-0 opacity-40">
      <div className="flex items-center justify-between px-4 pt-10 pb-3" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--brand-primary)" }}>
            <span className="text-white text-xs font-bold">A</span>
          </div>
          <span className="text-sm font-bold" style={{ color: "#1A202C" }}>Apex Bank</span>
        </div>
        <div className="w-8 h-8 rounded-full" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }} />
      </div>
      <div className="mx-4 mb-3 rounded-2xl p-4" style={{ background: "linear-gradient(135deg, var(--brand-primary), #5A67D8)" }}>
        <p className="text-xs mb-1" style={{ color: "#BEE3F8" }}>Total Balance</p>
        <p className="text-xl font-bold text-white">$24,891.50</p>
        <p className="text-xs mt-1" style={{ color: "#90CDF4" }}>↑ +$312.40 this month</p>
      </div>
      <div className="flex justify-around px-4 mb-4">
        {["Send", "Receive", "Pay", "More"].map((a) => (
          <div key={a} className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full" style={{ backgroundColor: "#EBF8FF", border: "1px solid #BEE3F8" }} />
            <span className="text-xs" style={{ color: "#718096" }}>{a}</span>
          </div>
        ))}
      </div>
      <div className="px-4">
        <p className="text-xs font-semibold mb-2" style={{ color: "#A0AEC0" }}>Recent Transactions</p>
        {["-$48.20", "-$15.99", "+$3,200"].map((amt, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b" style={{ borderColor: "#F7FAFC" }}>
            <div className="w-8 h-8 rounded-full" style={{ backgroundColor: i === 2 ? "#EBF8FF" : i === 1 ? "#FFF5F5" : "#F0FFF4" }} />
            <div className="flex-1">
              <div className="w-24 h-2.5 rounded" style={{ backgroundColor: "#E2E8F0" }} />
              <div className="w-16 h-2 rounded mt-1" style={{ backgroundColor: "#EDF2F7" }} />
            </div>
            <span className="text-xs" style={{ color: "#A0AEC0" }}>{amt}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Device Frames ────────────────────────────────────────────────────────────

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative" style={{ width: 360, height: 720 }}>
      <div className="absolute inset-0 rounded-[42px] shadow-2xl" style={{ backgroundColor: "#1A1A1A", padding: 3 }}>
        <div className="absolute inset-[3px] rounded-[40px] overflow-hidden" style={{ backgroundColor: "#000" }}>
          <div className="absolute inset-[2px] rounded-[38px] overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
            <SafeAreaCtx.Provider value={IOS_SAFE_AREA}>{children}</SafeAreaCtx.Provider>
          </div>
          {/* Home indicator (bottom safe area) */}
          <div className="absolute left-1/2 -translate-x-1/2 rounded-full z-30 pointer-events-none" style={{ bottom: 8, width: 120, height: 4, backgroundColor: "rgba(0,0,0,0.75)" }} />
          {/* Status bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 pt-2 z-30 pointer-events-none" style={{ height: 36 }}>
            <span className="text-white font-semibold" style={{ fontSize: 10 }}>9:41</span>
            <div data-testid="dynamic-island" className="absolute left-1/2 -translate-x-1/2 w-20 h-5 rounded-full" style={{ backgroundColor: "#000" }} />
            <div className="flex items-center gap-1">
              <div className="flex gap-0.5 items-end">
                {[3, 5, 7, 9].map((h, i) => (
                  <div key={i} className="w-0.5 rounded-sm" style={{ height: h, backgroundColor: "#FFFFFF", opacity: i < 3 ? 1 : 0.4 }} />
                ))}
              </div>
              <div className="relative" style={{ width: 20, height: 10 }}>
                <div className="absolute inset-0 rounded-sm border" style={{ borderColor: "rgba(255,255,255,0.6)" }} />
                <div className="absolute rounded-sm" style={{ left: 2, top: 2, bottom: 2, right: 4, backgroundColor: "#FFFFFF" }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Side buttons */}
      <div className="absolute rounded-r-sm" style={{ right: -4, top: 96, width: 4, height: 32, backgroundColor: "#555" }} />
      <div className="absolute rounded-r-sm" style={{ right: -4, top: 144, width: 4, height: 48, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 112, width: 4, height: 24, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 152, width: 4, height: 40, backgroundColor: "#555" }} />
      <div className="absolute rounded-l-sm" style={{ left: -4, top: 208, width: 4, height: 40, backgroundColor: "#555" }} />
    </div>
  );
}

function AndroidFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative" style={{ width: 350, height: 716 }}>
      <div className="absolute inset-0 rounded-[36px] shadow-2xl" style={{ backgroundColor: "#2D2D2D", padding: 3 }}>
        <div className="absolute inset-[3px] rounded-[34px] overflow-hidden" style={{ backgroundColor: "#000" }}>
          <div className="absolute inset-[2px] rounded-[32px] overflow-hidden" style={{ backgroundColor: "#FFFFFF" }}>
            <SafeAreaCtx.Provider value={ANDROID_SAFE_AREA}>{children}</SafeAreaCtx.Provider>
          </div>
          {/* Preview 24 (F3): an Android punch-hole camera (not an iPhone Dynamic Island) */}
          <div data-testid="punch-hole" className="absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none" style={{ top: 10, width: 12, height: 12, borderRadius: "50%", backgroundColor: "#000", boxShadow: "0 0 0 2px #1A1A1A" }} />
          {/* Android status bar: time on the left; signal, wifi and battery on the right */}
          <div data-testid="android-status-bar" className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 z-30 pointer-events-none" style={{ height: 32, paddingTop: 4 }}>
            <span className="text-white font-medium" style={{ fontSize: 10 }}>9:41</span>
            <div className="flex items-center gap-1.5">
              <svg data-testid="status-signal" width="10" height="10" viewBox="0 0 10 10"><path d="M10 0 L10 10 L0 10 Z" fill="#FFFFFF" /></svg>
              <svg data-testid="status-wifi" width="12" height="10" viewBox="0 0 12 10"><path d="M6 10 L0 3 A8.5 8.5 0 0 1 12 3 Z" fill="#FFFFFF" /></svg>
              <div data-testid="status-battery" className="relative" style={{ width: 7, height: 12 }}>
                <div className="absolute rounded-sm" style={{ inset: 0, top: 1, backgroundColor: "rgba(255,255,255,0.4)" }} />
                <div className="absolute rounded-sm" style={{ left: 0, right: 0, bottom: 0, height: "75%", backgroundColor: "#FFFFFF" }} />
              </div>
            </div>
          </div>
          {/* Nav bar */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-around items-center" style={{ height: 28, backgroundColor: "#111" }}>
            {["◁", "●", "▢"].map((s) => (
              <span key={s} className="text-white" style={{ opacity: 0.6, fontSize: 10 }}>{s}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Color Swatches ───────────────────────────────────────────────────────────

// R2-38 — the native colour input only takes #rrggbb; a swatch like var(--brand-primary) is shown
// as its resolved hex instead of falling back to black.
function swatchHex(value?: string): string {
  const v = resolveCssColor(value || "").trim();
  const rgb = hexToRgb(v);
  if (!rgb) return "#000000";
  return "#" + [rgb.r, rgb.g, rgb.b].map((n) => n.toString(16).padStart(2, "0")).join("");
}

function ColorSwatches({ colors, value, onChange, label, dark, onDark }: { colors: string[]; value: string; onChange: (c: string) => void; label?: string; dark?: string; onDark?: (c: string | undefined) => void }) {
  return (
    <>
    <div className="flex flex-wrap gap-1.5 mb-2">
      {colors.map((c) => (
        <button key={c} className="w-6 h-6 rounded-md border-2 transition-transform hover:scale-110"
          style={{
            backgroundColor: c,
            borderColor: value === c ? "var(--brand-primary)" : "transparent",
            outline: c === "#FFFFFF" ? "1px solid #E2E8F0" : undefined,
          }}
          onClick={() => onChange(c)}
        />
      ))}
      <input type="color" value={swatchHex(value)} onChange={(e) => onChange(e.target.value)}
        className="w-6 h-6 rounded-md cursor-pointer border p-0" style={{ borderColor: "#E2E8F0" }} title="Custom color" />
    </div>
    {onDark && <DarkColorRow label={label || "Colour"} value={dark} onChange={onDark} />}
    </>
  );
}

// D-1 (DA-BUG-1051) — the optional Dark value beside a colour control, saved as the content key
// `<base>Dark` (RRS passes it through next to its base key). Empty = inherit the light value on the
// device; Clear removes the key.
function DarkColorRow({ label, value, onChange }: { label: string; value?: string; onChange: (c: string | undefined) => void }) {
  return (
    <div className="flex gap-2 items-center mt-1.5">
      <span className="text-[11px] font-semibold w-9 flex-shrink-0" style={{ color: "#718096" }}>Dark</span>
      <input type="color" data-dark="" value={value ? swatchHex(value) : "#1C1C1E"} onChange={(e) => onChange(e.target.value)} aria-label={`${label} dark picker`}
        style={{ width: 28, height: 26, padding: 0, border: "1px solid #E2E8F0", borderRadius: 6, background: "none", cursor: "pointer", opacity: value ? 1 : 0.5 }} />
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value.trim() ? e.target.value : undefined)} aria-label={`${label} dark hex`}
        className="flex-1 text-xs border rounded-lg p-1.5" style={{ borderColor: "#E2E8F0" }} placeholder="Same as light" />
      {value && (
        <button type="button" aria-label={`Clear ${label} dark`} onClick={() => onChange(undefined)} style={{ color: "#A0AEC0" }}><X size={13} /></button>
      )}
    </div>
  );
}

function PanelLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#718096" }}>{children}</label>;
}

function PanelSection({ children, title, hint }: { children: React.ReactNode; title?: string; hint?: string }) {
  return (
    <div className="mb-4">
      {title && <PanelLabel>{title}</PanelLabel>}
      {hint && <p className="text-xs mb-1.5" style={{ color: "#A0AEC0" }}>{hint}</p>}
      {children}
    </div>
  );
}

// ─── Block Property Panel ─────────────────────────────────────────────────────

export function BlockPropertyPanel({ block, onUpdate, background, canSaveInAppMessage = true }: { block: Block; onUpdate: (c: Partial<BlockContent>) => void; background?: string | null; canSaveInAppMessage?: boolean }) {
  const { content } = block;
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imgError, setImgError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canSaveInAppMessage) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateImageFile(file);
    if (validationError) {
      setImgError(validationError);
      e.target.value = "";
      return;
    }
    setUploading(true);
    setImgError(null);
    try {
      const up = await uploadMediaFile(file);
      // DA-BUG-1421 (D3): never store the image's natural pixel height — the block height is the slider
      // value (`imageHeight`, clamped by RangeField), so a tall photo can't push the CTA below the fold.
      onUpdate({ imageUrl: up.url, ...(up.width ? { imageWidth: up.width, imageBytes: up.bytes } : {}) });
    } catch (err) {
      setImgError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  switch (block.type) {
    case "heading":
    case "text":
      return (
        <div>
          <PanelSection title="Content">
            <textarea className="w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none focus:ring-2"
              style={{ borderColor: "#E2E8F0", outlineColor: "var(--brand-primary)" }}
              rows={3} data-field="text" value={content.text || ""} onChange={(e) => onUpdate({ text: e.target.value })} />
            <div className="mt-2"><MergeTags onInsert={(tok) => onUpdate({ text: insertVariable(content.text || "", tok) })} /></div>
          </PanelSection>
          <PanelSection title="Font Size">
            <div className="flex items-center gap-2">
              <input type="range" min={8} max={32} value={content.fontSize || 14}
                onChange={(e) => onUpdate({ fontSize: Number(e.target.value) })} className="flex-1" />
              <span className="text-xs w-8 text-right" style={{ color: "#718096" }}>{content.fontSize || 14}px</span>
            </div>
          </PanelSection>
          <PanelSection title="Font Weight">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.fontWeight || "normal"} onChange={(e) => onUpdate({ fontWeight: e.target.value as BlockContent["fontWeight"] })}>
              {["normal", "medium", "semibold", "bold"].map(w => (
                <option key={w} value={w}>{w.charAt(0).toUpperCase() + w.slice(1)}</option>
              ))}
            </select>
          </PanelSection>
          <PanelSection title="Color">
            <ColorSwatches label="Color" colors={TEXT_COLORS} value={content.color || "#1A202C"} onChange={(c) => onUpdate({ color: c })} dark={content.colorDark} onDark={(c) => onUpdate({ colorDark: c })} />
            {background && textBlockContrastFails(block, background) && (
              <p className="text-xs mt-1" style={{ color: "#D69E2E" }}>
                ⚠ Low contrast against the background — may be hard to read.
              </p>
            )}
          </PanelSection>
          <PanelSection title="Alignment">
            <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: "#E2E8F0" }}>
              {(["left", "center", "right"] as const).map((a) => (
                <button key={a} className="flex-1 py-2 flex justify-center items-center transition-colors"
                  style={{ backgroundColor: content.alignment === a ? "var(--brand-primary)" : "#FFFFFF" }}
                  onClick={() => onUpdate({ alignment: a })}>
                  {a === "left" ? <AlignLeft size={14} color={content.alignment === a ? "#fff" : "#718096"} /> :
                   a === "center" ? <AlignCenter size={14} color={content.alignment === a ? "#fff" : "#718096"} /> :
                   <AlignRight size={14} color={content.alignment === a ? "#fff" : "#718096"} />}
                </button>
              ))}
            </div>
          </PanelSection>
          <PanelSection title="Line Height">
            <select className="w-full text-sm border rounded-lg p-2 focus:outline-none"
              style={{ borderColor: "#E2E8F0" }}
              value={content.lineHeight || 1.5} onChange={(e) => onUpdate({ lineHeight: Number(e.target.value) })}>
              {[1, 1.25, 1.5, 1.75, 2].map(lh => <option key={lh} value={lh}>{lh}</option>)}
            </select>
          </PanelSection>
        </div>
      );

    case "image":
      return (
        <div>
          <PanelSection title="Image URL"><TextField value={content.imageUrl} onChange={(v) => onUpdate({ imageUrl: v })} placeholder="https://…" /></PanelSection>
          <PanelSection title="Upload image">
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} disabled={!canSaveInAppMessage} />
            <button type="button"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60"
              style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
              disabled={uploading || !canSaveInAppMessage}
              onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading…" : content.imageUrl ? "Replace image" : "Upload image"}
            </button>
            {imgError && <p className="text-xs mt-1.5" style={{ color: "#E53E3E" }}>{imgError}</p>}
            {/* Gap 15: the preview slot keeps its 80 px whether or not an image is set or loaded, so the
                fields below never jump while the author is clicking or typing. */}
            <div data-testid="image-preview-slot" className="mt-2.5 rounded-lg overflow-hidden border flex items-center justify-center"
              style={{ borderColor: "#E2E8F0", height: 80, background: "#F7FAFC" }}>
              {content.imageUrl
                ? <img src={content.imageUrl} alt={content.imageAlt || ""} className="w-full object-cover" style={{ height: 80 }} />
                : <span className="text-xs" style={{ color: "#A0AEC0" }}>No image yet</span>}
            </div>
          </PanelSection>
          <PanelSection title="Alt text"><TextField field="imageAlt" value={content.imageAlt} onChange={(v) => onUpdate({ imageAlt: v })} placeholder="Describe this image…" /></PanelSection>
          <PanelSection title="Height"><RangeField label="Height" min={60} max={320} value={content.imageHeight || 180} onChange={(v) => onUpdate({ imageHeight: v })} /></PanelSection>
          <PanelSection title="Fit">
            <Segmented value={content.fit || "cover"} onChange={(v) => onUpdate({ fit: v as BlockContent["fit"] })}
              options={[["cover", "Cover"], ["contain", "Contain"], ["fill", "Fill"]]} />
          </PanelSection>
          <PanelSection title="Corner radius"><RangeField label="Corner radius" min={0} max={32} value={content.cornerRadius ?? 0} onChange={(v) => onUpdate({ cornerRadius: v })} /></PanelSection>
          <PanelSection title="Tap action"><ActionField action={content.tapAction} onChange={(a) => onUpdate({ tapAction: a })} /></PanelSection>
        </div>
      );

    case "button":
      return (
        <div>
          <PanelSection title="Button label"><TextField field="text" value={content.text} onChange={(v) => onUpdate({ text: v })} /></PanelSection>
          <PanelSection title="Style">
            <Segmented value={content.buttonStyle || "primary"} onChange={(v) => onUpdate(buttonStylePatch(v as ButtonStyle))}
              options={[["primary", "Primary"], ["secondary", "Secondary"], ["outline", "Outline"], ["ghost", "Ghost"]]} />
          </PanelSection>
          <PanelSection title="Color"><ColorSwatches label="Button colour" colors={ACCENT_COLORS} value={content.buttonColor || "var(--brand-primary)"} onChange={(c) => onUpdate({ buttonColor: c })} dark={content.buttonColorDark} onDark={(c) => onUpdate({ buttonColorDark: c })} /></PanelSection>
          <PanelSection title="Text colour" hint="Label colour on the device (sent as textColor). Empty = the style's default.">
            <ColorField label="Text colour" value={content.buttonTextColor} onChange={(c) => onUpdate({ buttonTextColor: c })} dark={content.buttonTextColorDark} onDark={(c) => onUpdate({ buttonTextColorDark: c })} />
          </PanelSection>
          <PanelSection title="Corner radius"><RangeField label="Corner radius" min={0} max={24} value={content.buttonRadius ?? 12} onChange={(v) => onUpdate({ buttonRadius: v })} /></PanelSection>
          <PanelSection title="Action"><ActionField action={content.action} onChange={(a) => onUpdate({ action: a })} allow={BUTTON_ACTIONS} /></PanelSection>
        </div>
      );

    case "divider":
      return (
        <div>
          <PanelSection title="Line style">
            <Segmented value={content.lineStyle || "solid"} onChange={(v) => onUpdate({ lineStyle: v as BlockContent["lineStyle"] })}
              options={[["solid", "Solid"], ["dashed", "Dashed"], ["dotted", "Dotted"]]} />
          </PanelSection>
          <PanelSection title="Color"><ColorField label="Color" value={content.dividerColor} onChange={(c) => onUpdate({ dividerColor: c })} dark={content.dividerColorDark} onDark={(c) => onUpdate({ dividerColorDark: c })} /></PanelSection>
          <PanelSection title="Thickness"><RangeField label="Thickness" min={1} max={6} value={content.thickness || 1} onChange={(v) => onUpdate({ thickness: v })} /></PanelSection>
          <PanelSection title="Vertical margin"><RangeField label="Vertical margin" min={0} max={32} value={content.verticalMargin ?? 8} onChange={(v) => onUpdate({ verticalMargin: v })} /></PanelSection>
          <PanelSection title="Label (optional)"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} placeholder="e.g. OR" /></PanelSection>
        </div>
      );

    case "spacer":
      return (
        <div>
          <PanelSection title="Height">
            <Segmented value={String(content.spacerHeight || 16)} onChange={(v) => onUpdate({ spacerHeight: Number(v) })}
              options={[["8", "8"], ["16", "16"], ["24", "24"], ["32", "32"], ["48", "48"]]} />
          </PanelSection>
        </div>
      );

    case "icon":
      return (
        <div>
          <PanelSection title="Icon (emoji or text)"><EmojiPicker value={content.iconValue} onChange={(v) => onUpdate({ iconValue: v })} /></PanelSection>
          <PanelSection title="Label"><TextField value={content.iconLabel} onChange={(v) => onUpdate({ iconLabel: v })} /></PanelSection>
          <PanelSection title="Sub-label"><TextField value={content.iconSubLabel} onChange={(v) => onUpdate({ iconSubLabel: v })} /></PanelSection>
          <PanelSection title="Size">
            <Segmented value={String(content.iconSize || 48)} onChange={(v) => onUpdate({ iconSize: Number(v) })}
              options={[["36", "SM"], ["48", "MD"], ["64", "LG"]]} />
          </PanelSection>
          <PanelSection title="Background color"><ColorField label="Background color" value={content.iconBg} onChange={(c) => onUpdate({ iconBg: c })} dark={content.iconBgDark} onDark={(c) => onUpdate({ iconBgDark: c })} /></PanelSection>
          <PanelSection title="Alignment">
            <Segmented value={content.alignment || "center"} onChange={(v) => onUpdate({ alignment: v as BlockContent["alignment"] })}
              options={[["left", "Left"], ["center", "Center"], ["right", "Right"]]} />
          </PanelSection>
          <PanelSection title="Shape">
            <Segmented value={content.iconShape || "circle"} onChange={(v) => onUpdate({ iconShape: v as BlockContent["iconShape"] })}
              options={[["circle", "Circle"], ["rounded", "Rounded"], ["square", "Square"]]} />
          </PanelSection>
          {/* D7 (DA-BUG-1412): badge above the labels (Stacked) or beside them (Inline); SDKs read iconLayout. */}
          <PanelSection title="Icon layout">
            <Segmented value={content.iconLayout || "stacked"} onChange={(v) => onUpdate({ iconLayout: v as BlockContent["iconLayout"] })}
              options={[["stacked", "Stacked"], ["inline", "Inline"]]} />
          </PanelSection>
        </div>
      );

    case "feature-list":
      return (
        <div>
          <PanelSection title="Items (one per line)"><ListField value={content.items} onChange={(items) => onUpdate({ items })} /></PanelSection>
          <PanelSection title="Text color"><ColorSwatches label="Text color" colors={TEXT_COLORS} value={content.color || "#384150"} onChange={(c) => onUpdate({ color: c })} dark={content.colorDark} onDark={(c) => onUpdate({ colorDark: c })} /></PanelSection>
        </div>
      );

    case "countdown":
      return (
        <div>
          <PanelSection title="Label"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Countdown type">
            <SelectField value={content.countdownMode || "fixed"} onChange={(v) => onUpdate({ countdownMode: v as BlockContent["countdownMode"] })}
              options={[["fixed", "Fixed end date"], ["duration", "Duration after first display"], ["attribute", "Customer / event attribute"]]} />
          </PanelSection>
          {(content.countdownMode || "fixed") === "fixed" && (
            <PanelSection title="End date & time">
              <input type="datetime-local" className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                value={content.endDate || ""} onChange={(e) => onUpdate({ endDate: e.target.value })} />
            </PanelSection>
          )}
          {content.countdownMode === "duration" && (
            <PanelSection title="Duration" hint="Counts down from the first time this customer sees the message.">
              <DurationField seconds={content.durationSeconds} onChange={(v) => onUpdate({ durationSeconds: v })} />
            </PanelSection>
          )}
          {content.countdownMode === "attribute" && (
            <PanelSection title="End-date attribute" hint="The customer property (set by the app) that holds the end date.">
              <TextField label="Countdown end-date attribute" value={content.attribute} onChange={(v) => onUpdate({ attribute: v })} placeholder="e.g. trial_ends_at" />
            </PanelSection>
          )}
          <PanelSection title="Format">
            <SelectField value={content.countdownFormat || "dhms"} onChange={(v) => onUpdate({ countdownFormat: v as BlockContent["countdownFormat"] })}
              options={[["dhms", "Days : Hours : Min : Sec"], ["hms", "Hours : Min : Sec"], ["ms", "Min : Sec"]]} />
          </PanelSection>
          {/* P6 (DA-BUG-1384): digits + label colour so a dark background stays readable (see P5 contrast).
              PB-4: one section each, so each picker says what it colours (as the coupon does). */}
          <PanelSection title="Digits colour">
            <ColorSwatches label="Digits colour" colors={TEXT_COLORS} value={content.color || "#1A202C"} onChange={(c) => onUpdate({ color: c })} dark={content.colorDark} onDark={(c) => onUpdate({ colorDark: c })} />
          </PanelSection>
          <PanelSection title="Label colour">
            <ColorSwatches label="Label colour" colors={TEXT_COLORS} value={content.labelColor || "#666666"} onChange={(c) => onUpdate({ labelColor: c })} dark={content.labelColorDark} onDark={(c) => onUpdate({ labelColorDark: c })} />
          </PanelSection>
          <PanelSection title="Show days"><Toggle checked={content.showDays !== false} onChange={(v) => onUpdate({ showDays: v })} /></PanelSection>
          <PanelSection title="When expired">
            <SelectField value={content.expiredAction || "dismiss"} onChange={(v) => onUpdate({ expiredAction: v as BlockContent["expiredAction"] })}
              options={[["text", "Replace with custom text"], ["disable", "Disable CTA action"], ["dismiss", "Dismiss message"]]} />
          </PanelSection>
          {content.expiredAction === "text" && (
            <PanelSection title="Text when expired" hint="Empty = the label, then “Expired”.">
              <TextField label="Text when expired" value={content.expiredText} onChange={(v) => onUpdate({ expiredText: v })} placeholder="Offer ended" />
            </PanelSection>
          )}
        </div>
      );

    case "coupon":
      return (
        <div>
          <PanelSection title="Label"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Coupon code"><TextField value={content.code} onChange={(v) => onUpdate({ code: v })} placeholder="SAVE20" /></PanelSection>
          <PanelSection title="Copy button text"><TextField value={content.copyText} onChange={(v) => onUpdate({ copyText: v })} placeholder="Copy code" /></PanelSection>
          {/* P6 (DA-BUG-1384): label + expiry text colour so a dark background stays readable (see P5 contrast). */}
          <PanelSection title="Label colour" hint="Label and expiry text — raise it for a dark background.">
            <ColorSwatches label="Label colour" colors={TEXT_COLORS} value={content.labelColor || "#666666"} onChange={(c) => onUpdate({ labelColor: c })} dark={content.labelColorDark} onDark={(c) => onUpdate({ labelColorDark: c })} />
          </PanelSection>
          <PanelSection title="Show expiry date"><Toggle checked={!!content.showExpiry} onChange={(v) => onUpdate({ showExpiry: v })} /></PanelSection>
          {content.showExpiry && (
            <PanelSection title="Expiry date">
              <input type="date" className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                value={content.expiryDate || ""} onChange={(e) => onUpdate({ expiryDate: e.target.value })} />
            </PanelSection>
          )}
          <PanelSection title="Style">
            <Segmented value={content.couponStyle || "outlined"} onChange={(v) => onUpdate({ couponStyle: v as BlockContent["couponStyle"] })}
              options={[["outlined", "Outlined"], ["filled", "Filled"], ["card", "Card"]]} />
          </PanelSection>
          <PanelSection title="Accent color"><ColorSwatches label="Accent color" colors={ACCENT_COLORS} value={content.accent || "var(--brand-primary)"} onChange={(c) => onUpdate({ accent: c })} dark={content.accentDark} onDark={(c) => onUpdate({ accentDark: c })} /></PanelSection>
          <PanelSection title="Confetti on copy"><Toggle checked={content.confetti !== false} onChange={(v) => onUpdate({ confetti: v })} /></PanelSection>
          <PanelSection title="After copy — CTA action"><ActionField action={content.afterCopy} onChange={(a) => onUpdate({ afterCopy: a })} allow={["none", "dismiss", "navigate", "openUrl"]} /></PanelSection>
        </div>
      );

    case "product":
      return (
        <div>
          <PanelSection title="Image URL"><TextField value={content.imageUrl} onChange={(v) => onUpdate({ imageUrl: v })} placeholder="https://…" /></PanelSection>
          <PanelSection title="Product name"><TextField value={content.productName} onChange={(v) => onUpdate({ productName: v })} /></PanelSection>
          <PanelSection title="Description"><TextField value={content.productDescription} onChange={(v) => onUpdate({ productDescription: v })} /></PanelSection>
          <PanelSection title="Price"><TextField value={content.price} onChange={(v) => onUpdate({ price: v })} /></PanelSection>
          <PanelSection title="Original price (optional)"><TextField value={content.originalPrice} onChange={(v) => onUpdate({ originalPrice: v })} /></PanelSection>
          <PanelSection title="Badge label (optional)"><TextField value={content.badge} onChange={(v) => onUpdate({ badge: v })} placeholder="-30%" /></PanelSection>
          <PanelSection title="Button text"><TextField value={content.text} onChange={(v) => onUpdate({ text: v })} /></PanelSection>
          <PanelSection title="Action"><ActionField action={content.action} onChange={(a) => onUpdate({ action: a })} allow={["dismiss", "navigate", "openUrl", "deepLink"]} /></PanelSection>
          <PanelSection title="Layout">
            <Segmented value={content.productLayout || "vertical"} onChange={(v) => onUpdate({ productLayout: v as BlockContent["productLayout"] })}
              options={[["vertical", "Vertical"], ["horizontal", "Horizontal"]]} />
          </PanelSection>
          <PanelSection title="Image height"><RangeField label="Image height" min={80} max={240} value={content.imageHeight || 140} onChange={(v) => onUpdate({ imageHeight: v })} /></PanelSection>
          {/* DA-BUG-1421 (D9): product CTA button fill + label colour (RRS already passes buttonColor/buttonColorDark). */}
          <PanelSection title="Button colour">
            <ColorField label="Button fill" value={content.buttonColor} onChange={(c) => onUpdate({ buttonColor: c })} dark={content.buttonColorDark} onDark={(c) => onUpdate({ buttonColorDark: c })} />
            <div className="mt-2"><ColorField label="Button text" value={content.buttonTextColor} onChange={(c) => onUpdate({ buttonTextColor: c })} dark={content.buttonTextColorDark} onDark={(c) => onUpdate({ buttonTextColorDark: c })} /></div>
          </PanelSection>
          <PanelSection title="Show shadow"><Toggle checked={content.showShadow !== false} onChange={(v) => onUpdate({ showShadow: v })} /></PanelSection>
        </div>
      );

    case "rating":
      return (
        <div>
          <PanelSection title="Prompt label"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Sub-label"><TextField value={content.ratingSubLabel} onChange={(v) => onUpdate({ ratingSubLabel: v })} /></PanelSection>
          <PanelSection title="Thank-you message"><TextField value={content.thankYouMessage} onChange={(v) => onUpdate({ thankYouMessage: v })} /></PanelSection>
          <PanelSection title="Rating type">
            <SelectField value={content.ratingType || "stars"} onChange={(v) => {
              // Preview 17: each type has its own hint; a default hint follows the type, a custom one stays.
              const hint = content.ratingSubLabel || "";
              const isDefault = !hint.trim() || Object.values(RATING_HINTS).includes(hint);
              onUpdate({ ratingType: v as BlockContent["ratingType"], ...(isDefault && RATING_HINTS[v] ? { ratingSubLabel: RATING_HINTS[v] } : {}) });
            }}
              options={[["stars", "Stars"], ["thumbs", "Thumbs up / down"], ["emoji", "Emoji scale"], ["nps", "NPS (0–10)"]]} />
          </PanelSection>
          {(content.ratingType || "stars") === "stars" && (
            <PanelSection title="Number of stars"><RangeField label="Number of stars" min={3} max={10} value={content.ratingMax || 5} onChange={(v) => onUpdate({ ratingMax: v })} /></PanelSection>
          )}
          {content.ratingType === "nps" && (
            <>
              <PanelSection title="Low label"><TextField value={content.lowLabel} onChange={(v) => onUpdate({ lowLabel: v })} placeholder="Not likely" /></PanelSection>
              <PanelSection title="High label"><TextField value={content.highLabel} onChange={(v) => onUpdate({ highLabel: v })} placeholder="Very likely" /></PanelSection>
            </>
          )}
          <PanelSection title="Active color"><ColorField label="Active color" value={content.activeColor} onChange={(c) => onUpdate({ activeColor: c })} dark={content.activeColorDark} onDark={(c) => onUpdate({ activeColorDark: c })} /></PanelSection>
          {/* DA-BUG-1421 (D9): Submit button fill + label colour (read by RRS + SDKs as submitColor/submitTextColor). */}
          <PanelSection title="Submit button colour">
            <ColorField label="Button fill" value={content.submitColor} onChange={(c) => onUpdate({ submitColor: c })} dark={content.submitColorDark} onDark={(c) => onUpdate({ submitColorDark: c })} />
            <div className="mt-2"><ColorField label="Button text" value={content.submitTextColor} onChange={(c) => onUpdate({ submitTextColor: c })} dark={content.submitTextColorDark} onDark={(c) => onUpdate({ submitTextColorDark: c })} /></div>
          </PanelSection>
          <PanelSection title="Action on submit"><ActionField action={content.onSubmit} onChange={(a) => onUpdate({ onSubmit: a })} allow={["dismiss", "navigate", "emitEvent"]} /></PanelSection>
          <PanelSection title="Require rating to submit"><Toggle checked={content.requireToSubmit !== false} onChange={(v) => onUpdate({ requireToSubmit: v })} /></PanelSection>
        </div>
      );

    case "choice":
      return (
        <div>
          <PanelSection title="Question"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Submit button text"><TextField value={content.submitText} onChange={(v) => onUpdate({ submitText: v })} placeholder="Submit" /></PanelSection>
          <PanelSection title="Choices (one per line)"><ListField value={content.options} onChange={(options) => onUpdate({ options })} /></PanelSection>
          <PanelSection title="Allow multiple selections"><Toggle checked={!!content.multiple} onChange={(v) => onUpdate({ multiple: v })} /></PanelSection>
          <PanelSection title="Layout">
            <Segmented value={content.choiceLayout || "list"} onChange={(v) => onUpdate({ choiceLayout: v as BlockContent["choiceLayout"] })}
              options={[["list", "List"], ["grid", "Grid"]]} />
          </PanelSection>
          <PanelSection title="On submit"><ActionField action={content.onSubmit} onChange={(a) => onUpdate({ onSubmit: a })} allow={["dismiss", "navigate", "emitEvent"]} /></PanelSection>
          <PanelSection title="Field key (inside a Form)"><TextField value={content.fieldKey} onChange={(v) => onUpdate({ fieldKey: v })} placeholder="e.g. plan_choice" /></PanelSection>
        </div>
      );

    case "input":
      return (
        <div>
          <PanelSection title="Label"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Placeholder"><TextField value={content.placeholder} onChange={(v) => onUpdate({ placeholder: v })} /></PanelSection>
          <PanelSection title="Submit button text"><TextField value={content.submitText} onChange={(v) => onUpdate({ submitText: v })} placeholder="Submit" /></PanelSection>
          <PanelSection title="Input type">
            <SelectField value={content.inputType || "text"} onChange={(v) => onUpdate({ inputType: v as BlockContent["inputType"] })}
              options={[["text", "Text"], ["email", "Email"], ["phone", "Phone number"], ["number", "Number"], ["multiline", "Multi-line text"]]} />
          </PanelSection>
          <PanelSection title="Max characters"><RangeField label="Max characters" min={0} max={500} value={content.maxChars || 0} onChange={(v) => onUpdate({ maxChars: v })} /></PanelSection>
          <PanelSection title="Required field"><Toggle checked={!!content.required} onChange={(v) => onUpdate({ required: v })} /></PanelSection>
          <PanelSection title="Save response to attribute (optional)"><TextField value={content.saveToAttribute} onChange={(v) => onUpdate({ saveToAttribute: v })} placeholder="e.g. email" /></PanelSection>
          <PanelSection title="Action on submit"><ActionField action={content.onSubmit} onChange={(a) => onUpdate({ onSubmit: a })} allow={["dismiss", "navigate", "emitEvent"]} /></PanelSection>
          <PanelSection title="Field key (inside a Form)"><TextField value={content.fieldKey} onChange={(v) => onUpdate({ fieldKey: v })} placeholder="e.g. full_name" /></PanelSection>
        </div>
      );

    case "consent":
      return (
        <div>
          <PanelSection title="Consent text"><TextField value={content.text} onChange={(v) => onUpdate({ text: v })} /></PanelSection>
          <PanelSection title="Link text (optional)"><TextField value={content.link} onChange={(v) => onUpdate({ link: v })} placeholder="Privacy Policy" /></PanelSection>
          <PanelSection title="Link URL"><TextField value={content.linkUrl} onChange={(v) => onUpdate({ linkUrl: v })} placeholder="https://…" /></PanelSection>
          <PanelSection title="Required to proceed"><Toggle checked={!!content.required} onChange={(v) => onUpdate({ required: v })} /></PanelSection>
          <PanelSection title="Pre-checked"><Toggle checked={!!content.preChecked} onChange={(v) => onUpdate({ preChecked: v })} /></PanelSection>
          <PanelSection title="Save response to channel">
            <SelectField value={content.saveToChannel || ""} onChange={(v) => onUpdate({ saveToChannel: v })}
              options={[["", "— Optional —"], ["email", "Email"], ["sms", "SMS"], ["push", "Push notification"], ["whatsapp", "WhatsApp"], ["in_app", "In-app"]]} />
          </PanelSection>
          <PanelSection title="Field key (inside a Form)"><TextField value={content.fieldKey} onChange={(v) => onUpdate({ fieldKey: v })} placeholder="e.g. marketing_ok" /></PanelSection>
        </div>
      );

    case "progress":
      return (
        <div>
          <PanelSection title="Label"><TextField value={content.label} onChange={(v) => onUpdate({ label: v })} /></PanelSection>
          <PanelSection title="Current value"><RangeField label="Current value" min={0} max={content.max || 100} value={content.current || 0} onChange={(v) => onUpdate({ current: v })} /></PanelSection>
          <PanelSection title="Maximum value"><RangeField label="Maximum value" min={1} max={1000} value={content.max || 100} onChange={(v) => onUpdate({ max: v })} /></PanelSection>
          <PanelSection title="Show percentage label"><Toggle checked={content.showPercent !== false} onChange={(v) => onUpdate({ showPercent: v })} /></PanelSection>
          <PanelSection title="Bar color"><ColorField label="Bar color" value={content.accent} onChange={(c) => onUpdate({ accent: c })} dark={content.accentDark} onDark={(c) => onUpdate({ accentDark: c })} /></PanelSection>
          <PanelSection title="Track color"><ColorField label="Track color" value={content.trackColor} onChange={(c) => onUpdate({ trackColor: c })} dark={content.trackColorDark} onDark={(c) => onUpdate({ trackColorDark: c })} /></PanelSection>
          {/* DA-BUG-1421 (D10): progress label colour + dark variant (so it isn't stuck grey in dark mode). */}
          <PanelSection title="Label colour"><ColorField label="Label colour" value={content.labelColor} onChange={(c) => onUpdate({ labelColor: c })} dark={content.labelColorDark} onDark={(c) => onUpdate({ labelColorDark: c })} /></PanelSection>
          <PanelSection title="Height"><RangeField label="Height" min={4} max={24} value={content.progressHeight || 8} onChange={(v) => onUpdate({ progressHeight: v })} /></PanelSection>
          <PanelSection title="Style">
            <Segmented value={content.progressStyle || "rounded"} onChange={(v) => onUpdate({ progressStyle: v as BlockContent["progressStyle"] })}
              options={[["rounded", "Rounded"], ["square", "Square"]]} />
          </PanelSection>
          <PanelSection title="Animate on display"><Toggle checked={content.animate !== false} onChange={(v) => onUpdate({ animate: v })} /></PanelSection>
        </div>
      );

    case "carousel":
      return (
        <div>
          <PanelSection title="Slides">
            <div className="space-y-2">
              {(content.slides || []).map((s, i) => (
                <div key={i} className="rounded-lg border p-2 space-y-1.5" style={{ borderColor: "#E2E8F0" }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: "#718096" }}>Slide {i + 1}</span>
                    <button type="button" aria-label="Remove slide" disabled={(content.slides || []).length <= 1}
                      title={(content.slides || []).length <= 1 ? "A carousel needs at least one slide" : undefined}
                      onClick={() => { if ((content.slides || []).length > 1) onUpdate({ slides: (content.slides || []).filter((_, j) => j !== i) }); }}
                      style={{ color: "#CBD5E0" }}><Trash2 size={13} /></button>
                  </div>
                  <TextField value={s.title} onChange={(v) => onUpdate({ slides: (content.slides || []).map((x, j) => j === i ? { ...x, title: v } : x) })} placeholder="Slide title" />
                  <TextField value={s.description} onChange={(v) => onUpdate({ slides: (content.slides || []).map((x, j) => j === i ? { ...x, description: v } : x) })} placeholder="Description" />
                  <TextField value={s.imageUrl} onChange={(v) => onUpdate({ slides: (content.slides || []).map((x, j) => j === i ? { ...x, imageUrl: v } : x) })} placeholder="Image URL" />
                  <TextField value={s.cta} onChange={(v) => onUpdate({ slides: (content.slides || []).map((x, j) => j === i ? { ...x, cta: v } : x) })} placeholder="CTA text (optional)" />
                  <ActionField label={`Slide ${i + 1} action`} action={s.action} allow={["none", ...BUTTON_ACTIONS]}
                    onChange={(a) => onUpdate({ slides: (content.slides || []).map((x, j) => j === i ? { ...x, action: a } : x) })} />
                </div>
              ))}
              <button type="button" onClick={() => onUpdate({ slides: [...(content.slides || []), { title: "", description: "", imageUrl: "", cta: "" }] })}
                className="w-full text-xs py-2 rounded-lg border border-dashed" style={{ borderColor: "#CBD5E0", color: "#718096" }}>+ Add slide</button>
            </div>
          </PanelSection>
          <PanelSection title="Auto-advance"><Toggle checked={!!content.autoAdvance} onChange={(v) => onUpdate({ autoAdvance: v })} /></PanelSection>
          <PanelSection title="Loop"><Toggle checked={content.loop !== false} onChange={(v) => onUpdate({ loop: v })} /></PanelSection>
          <PanelSection title="Show indicators (dots)"><Toggle checked={content.indicators !== false} onChange={(v) => onUpdate({ indicators: v })} /></PanelSection>
          <PanelSection title="Show arrow buttons"><Toggle checked={content.arrows !== false} onChange={(v) => onUpdate({ arrows: v })} /></PanelSection>
        </div>
      );

    case "video":
      return (
        <div>
          <PanelSection title="Video URL"><TextField value={content.videoUrl} onChange={(v) => onUpdate({ videoUrl: v })} placeholder="https://…/clip.mp4" /></PanelSection>
          <PanelSection title="Poster image URL"><TextField value={content.videoPoster} onChange={(v) => onUpdate({ videoPoster: v })} placeholder="https://…/poster.jpg" /></PanelSection>
          <PanelSection title="Autoplay"><Toggle checked={!!content.autoplay} onChange={(v) => onUpdate({ autoplay: v, muted: v ? true : content.muted })} /></PanelSection>
          <PanelSection title="Muted"><Toggle checked={content.muted !== false} onChange={(v) => onUpdate({ muted: v })} /></PanelSection>
          <PanelSection title="Loop"><Toggle checked={!!content.loop} onChange={(v) => onUpdate({ loop: v })} /></PanelSection>
          <PanelSection title="Show native controls"><Toggle checked={content.controls !== false} onChange={(v) => onUpdate({ controls: v })} /></PanelSection>
          <PanelSection title="Height"><RangeField label="Height" min={120} max={320} value={content.imageHeight || 200} onChange={(v) => onUpdate({ imageHeight: v })} /></PanelSection>
          <PanelSection title="Corner radius"><RangeField label="Corner radius" min={0} max={32} value={content.cornerRadius ?? 8} onChange={(v) => onUpdate({ cornerRadius: v })} /></PanelSection>
        </div>
      );

    case "container":
      return (
        <div>
          <PanelSection title="Background color"><ColorField label="Background color" value={content.containerBg} onChange={(c) => onUpdate({ containerBg: c })} dark={content.containerBgDark} onDark={(c) => onUpdate({ containerBgDark: c })} /></PanelSection>
          <PanelSection title="Opacity"><RangeField label="Opacity" min={0} max={100} value={content.containerBgOpacity ?? 100} onChange={(v) => onUpdate({ containerBgOpacity: v })} /></PanelSection>
          <PanelSection title="Padding (all sides)"><RangeField label="Padding (all sides)" min={0} max={48} value={content.containerPadding ?? 16} onChange={(v) => onUpdate({ containerPadding: v })} /></PanelSection>
          <PanelSection title="Corner radius"><RangeField label="Corner radius" min={0} max={32} value={content.containerRadius ?? 12} onChange={(v) => onUpdate({ containerRadius: v })} /></PanelSection>
          <PanelSection title="Show border"><Toggle checked={content.containerBorder !== false} onChange={(v) => onUpdate({ containerBorder: v })} /></PanelSection>
          {content.containerBorder !== false && (
            <PanelSection title="Border color"><ColorField label="Border color" value={content.containerBorderColor} onChange={(c) => onUpdate({ containerBorderColor: c })} dark={content.containerBorderColorDark} onDark={(c) => onUpdate({ containerBorderColorDark: c })} /></PanelSection>
          )}
          <PanelSection title="Drop shadow"><Toggle checked={!!content.containerShadow} onChange={(v) => onUpdate({ containerShadow: v })} /></PanelSection>
        </div>
      );

    case "row":
      return (
        <div>
          <PanelSection title="Gap"><RangeField label="Gap" min={0} max={32} value={content.gap ?? 8} onChange={(v) => onUpdate({ gap: v })} /></PanelSection>
          <PanelSection title="Alignment (cross-axis)">
            <Segmented value={content.alignCross || "center"} onChange={(v) => onUpdate({ alignCross: v as BlockContent["alignCross"] })}
              options={[["start", "Start"], ["center", "Center"], ["end", "End"], ["stretch", "Stretch"]]} />
          </PanelSection>
          <PanelSection title="Distribution">
            <SelectField value={content.distribution || "start"} onChange={(v) => onUpdate({ distribution: v as BlockContent["distribution"] })}
              options={[["start", "Start"], ["center", "Center"], ["end", "End"], ["between", "Space between"], ["around", "Space around"]]} />
          </PanelSection>
          <PanelSection title="Padding"><RangeField label="Padding" min={0} max={32} value={content.containerPadding ?? 0} onChange={(v) => onUpdate({ containerPadding: v })} /></PanelSection>
        </div>
      );

    case "columns":
      return (
        <div>
          <PanelSection title="Column count">
            <Segmented value={String(content.columnCount || 2)} onChange={(v) => onUpdate({ columnCount: Number(v) as BlockContent["columnCount"], children: resizeColumns((content.children || []) as any, Number(v), uid) as Block[] })}
              options={[["2", "2"], ["3", "3"], ["4", "4"]]} />
          </PanelSection>
          <PanelSection title="Gap"><RangeField label="Gap" min={0} max={32} value={content.gap ?? 8} onChange={(v) => onUpdate({ gap: v })} /></PanelSection>
          <PanelSection title="Alignment (cross-axis)">
            <Segmented value={content.alignCross || "start"} onChange={(v) => onUpdate({ alignCross: v as BlockContent["alignCross"] })}
              options={[["start", "Start"], ["center", "Center"], ["end", "End"], ["stretch", "Stretch"]]} />
          </PanelSection>
          <PanelSection title="Padding"><RangeField label="Padding" min={0} max={32} value={content.containerPadding ?? 0} onChange={(v) => onUpdate({ containerPadding: v })} /></PanelSection>
        </div>
      );

    case "column":
      return (
        <p className="text-xs" style={{ color: "#718096" }}>
          One column of a Columns block. Drop blocks into it, or keep it selected and click a block in the palette.
        </p>
      );

    case "form": {
      // SDK-dispatch API destination (no server-held secrets — headers travel to the device).
      const api: FormApiConfig = content.formApi || { endpoint: "", method: "POST", headers: [], staticBody: [] };
      const setApi = (patch: Partial<FormApiConfig>) => onUpdate({ formApi: { ...api, ...patch } });
      // Body preview = {fieldKey: <value>} for every child field that has a fieldKey, + static rows.
      const fieldKeys = collectFieldKeys(content.children || []);
      const bodyPreview: Record<string, string> = {};
      for (const k of fieldKeys) bodyPreview[k] = "<value>";
      for (const r of api.staticBody || []) if (r.key) bodyPreview[r.key] = r.value || "";
      return (
        <div>
          <PanelSection title="Submit button text"><TextField value={content.submitText} onChange={(v) => onUpdate({ submitText: v })} placeholder="Submit" /></PanelSection>
          <PanelSection title="Form background"><ColorField label="Form background" value={content.containerBg} onChange={(c) => onUpdate({ containerBg: c })} dark={content.containerBgDark} onDark={(c) => onUpdate({ containerBgDark: c })} /></PanelSection>
          <PanelSection title="Field spacing"><RangeField label="Field spacing" min={0} max={32} value={content.gap ?? 10} onChange={(v) => onUpdate({ gap: v })} /></PanelSection>
          <PanelSection title="Action after submit"><ActionField action={content.onSubmit} onChange={(a) => onUpdate({ onSubmit: a })} allow={["dismiss", "navigate", "emitEvent"]} /></PanelSection>

          <div className="mt-4 pt-3 border-t" style={{ borderColor: "#E2E8F0" }}>
            <div className="text-xs font-semibold mb-1" style={{ color: "#4A5568" }}>Post to third-party API (optional)</div>
            <p className="text-[11px] mb-2" style={{ color: "#A0AEC0" }}>
              The SDK posts the grouped response directly to this endpoint. Headers are sent from the device — use public endpoints / non-secret auth only.
            </p>
          </div>
          <PanelSection title="API endpoint"><TextField value={api.endpoint} onChange={(v) => setApi({ endpoint: v })} placeholder="https://api.example.com/leads" /></PanelSection>
          <PanelSection title="Method">
            <Segmented value={api.method} onChange={(v) => setApi({ method: v as FormApiConfig["method"] })} options={[["POST", "POST"], ["PUT", "PUT"]]} />
          </PanelSection>
          <PanelSection title="Header fields">
            <KeyValueRows rows={api.headers} onChange={(headers) => setApi({ headers })} keyPlaceholder="Authorization" valuePlaceholder="Bearer …" addLabel="+ Add header" />
          </PanelSection>
          <PanelSection title="Static body fields (added to every post)">
            <KeyValueRows rows={api.staticBody} onChange={(staticBody) => setApi({ staticBody })} keyPlaceholder="source" valuePlaceholder="in_app" addLabel="+ Add field" />
          </PanelSection>
          {api.endpoint ? (
            <PanelSection title="Body preview">
              <pre className="text-[11px] rounded-lg p-2.5 overflow-x-auto" style={{ background: "#F7FAFC", border: "1px solid #E2E8F0", color: "#4A5568" }}>
{JSON.stringify(bodyPreview, null, 2)}
              </pre>
              {!fieldKeys.length && (
                <p className="text-[11px] mt-1" style={{ color: "#DD6B20" }}>No field keys yet — give each field inside this form a “Field key”.</p>
              )}
            </PanelSection>
          ) : null}
        </div>
      );
    }

    default:
      return <div className="text-sm" style={{ color: "#A0AEC0" }}>No properties available.</div>;
  }
}

// Collect the `fieldKey` of every data-capture child (recursively through nested layout blocks).
function collectFieldKeys(blocks: Block[]): string[] {
  const out: string[] = [];
  const walk = (list: Block[]) => {
    for (const b of list) {
      if (b.content.fieldKey) out.push(b.content.fieldKey);
      if (b.content.children && b.content.children.length) walk(b.content.children);
    }
  };
  walk(blocks);
  return out;
}

// Repeatable key/value rows (API headers / static body). Empty rows are kept while editing.
function KeyValueRows({ rows, onChange, keyPlaceholder, valuePlaceholder, addLabel }:
  { rows: { key: string; value: string }[]; onChange: (rows: { key: string; value: string }[]) => void; keyPlaceholder: string; valuePlaceholder: string; addLabel: string }) {
  const list = rows || [];
  return (
    <div className="space-y-1.5">
      {list.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input type="text" className="flex-1 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0", minWidth: 0 }}
            value={r.key} placeholder={keyPlaceholder} onChange={(e) => onChange(list.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} />
          <input type="text" className="flex-1 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0", minWidth: 0 }}
            value={r.value} placeholder={valuePlaceholder} onChange={(e) => onChange(list.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
          <button type="button" aria-label="Remove row" onClick={() => onChange(list.filter((_, j) => j !== i))} style={{ color: "#CBD5E0" }}><Trash2 size={13} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, { key: "", value: "" }])}
        className="w-full text-xs py-2 rounded-lg border border-dashed" style={{ borderColor: "#CBD5E0", color: "#718096" }}>{addLabel}</button>
    </div>
  );
}

// ─── Small inspector field helpers (shared by the new blocks) ──────────────────
function TextField({ value, onChange, placeholder, label, field }: { value?: string; onChange: (v: string) => void; placeholder?: string; label?: string; field?: string }) {
  return <input type="text" aria-label={label} data-field={field} className="w-full text-sm border rounded-lg p-2.5 focus:outline-none focus:ring-2"
    style={{ borderColor: "#E2E8F0", outlineColor: "var(--brand-primary)" } as React.CSSProperties}
    value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
}
function SelectField({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: [string, string][]; label?: string }) {
  return <select aria-label={label} className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
    value={value} onChange={(e) => onChange(e.target.value)}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>;
}
// Gap 10 (DA-BUG-1098) — slider + typed value; the allowed range is always shown and a typed
// value outside it is clamped with an inline note (never silently).
function RangeField({ min, max, value, onChange, label }: { min: number; max: number; value: number; onChange: (v: number) => void; label?: string }) {
  const [note, setNote] = useState<string | null>(null);
  return <div>
    <div className="flex items-center gap-2">
      <input type="range" min={min} max={max} value={value} onChange={(e) => { setNote(null); onChange(Number(e.target.value)); }} className="flex-1" aria-label={label} />
      <input type="number" min={min} max={max} value={value} aria-label={label ? `${label} value` : undefined}
        className="w-14 text-xs border rounded p-1 text-right" style={{ borderColor: "#E2E8F0" }}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (e.target.value === "" || !Number.isFinite(n)) return;
          const c = Math.min(max, Math.max(min, Math.round(n)));
          setNote(c !== n ? `Allowed ${min}–${max}; set to ${c}.` : null);
          onChange(c);
        }} />
    </div>
    <p className="text-[11px] mt-0.5" style={{ color: note ? "#C05621" : "#A0AEC0" }}>{note || `${min}–${max}`}</p>
  </div>;
}
function ListField({ value, onChange }: { value?: string[]; onChange: (v: string[]) => void }) {
  // P1 (DA-BUG-1380): keep a raw text buffer. The old version re-derived the array on every
  // keystroke with .trim().filter(Boolean), so a space typed mid-word was stripped before the
  // next character ("Zero transfer fees" → "Zerotransferfees") and Enter was swallowed. We now
  // edit a raw string and only trim + drop blank lines on blur.
  const joined = (value || []).join("\n");
  const [text, setText] = useState(joined);
  const lastEmitted = useRef(joined);
  useEffect(() => {
    // Re-sync only when the value changes from the OUTSIDE (e.g. a different block is selected),
    // never from our own emits (which we record in lastEmitted) — that would clobber the caret.
    if (joined !== lastEmitted.current) { setText(joined); lastEmitted.current = joined; }
  }, [joined]);
  const emit = (arr: string[]) => { lastEmitted.current = arr.join("\n"); onChange(arr); };
  return <textarea rows={4} className="w-full text-sm border rounded-lg p-2.5 resize-none focus:outline-none"
    style={{ borderColor: "#E2E8F0" }} value={text}
    onChange={(e) => { setText(e.target.value); emit(e.target.value.split("\n")); }}
    onBlur={() => { const out = text.split("\n").map((s) => s.trim()).filter(Boolean); setText(out.join("\n")); emit(out); }} />;
}
function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return <button type="button" role="switch" onClick={() => onChange(!checked)} aria-checked={checked}
    style={{
      width: 38, height: 22, padding: 0, border: "none", borderRadius: 999, boxSizing: "border-box",
      background: checked ? "var(--brand-primary)" : "#CBD5E0", position: "relative", flexShrink: 0,
      display: "inline-block", verticalAlign: "middle", cursor: "pointer", transition: "background 0.15s",
    }}>
    <span style={{
      position: "absolute", top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: "50%",
      background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.25)", transition: "left 0.15s",
    }} />
  </button>;
}
// Segmented button group (SM/MD/LG, Primary/…, List/Grid, Solid/Dashed/Dotted, …).
function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
      {options.map(([v, l], i) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className="flex-1 text-xs py-1.5 font-medium transition-colors focus:outline-none"
          style={{ background: value === v ? "var(--brand-primary)" : "#FFFFFF", color: value === v ? "#FFFFFF" : "#4A5568", borderLeft: i ? "1px solid #E2E8F0" : "none" }}>
          {l}
        </button>
      ))}
    </div>
  );
}
// Small uppercase section header grouping controls (Figma "Container", "Close button", …).
function SectionHeader({ children }: { children: React.ReactNode }) {
  return <p className="text-xs font-semibold uppercase tracking-wide mt-5 mb-2.5 first:mt-0" style={{ color: "#94A3B8", letterSpacing: "0.04em" }}>{children}</p>;
}
// Collapsible advanced section (Figma "Screen restrictions", "Advanced integrations", "Delivery rules").
function CollapsibleSection({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t mt-3 pt-2" style={{ borderColor: "#E2E8F0" }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between py-1 focus:outline-none">
        <span className="text-xs font-semibold" style={{ color: "#4A5568" }}>{title}</span>
        <ChevronDown size={14} style={{ color: "#A0AEC0", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
      </button>
      {open && <div className="pt-1">{children}</div>}
    </div>
  );
}
// Add/remove a simple string list (e.g. excluded screens).
// Status pill palette for a screen's origin (Registered / Observed from events / Both).
const STATUS_BADGE_SCREEN: Record<ScreenItem["status"], { label: string; bg: string; fg: string }> = {
  registered: { label: "Registered", bg: "#E9F6EC", fg: "#2F855A" },
  observed: { label: "Observed", bg: "#EBF2FF", fg: "#2B6CB0" },
  both: { label: "Both", bg: "#F0EBFF", fg: "#6B46C1" },
};
// Screen multi-select: search/add input above a checkbox list of merged screens with
// Registered/Observed/Both badges. Shared by "Screens that trigger evaluation" and
// "Excluded screens" so both pick from the same list and can add unobserved names.
// Platform marks shown in front of a screen name — Android head / Apple logo (both for "both").
function PlatformIcon({ platform }: { platform: ScreenPlatform }) {
  if (platform === "android") return <BsAndroid2 size={14} title="Android" style={{ color: "#3DDC84", flexShrink: 0 }} />;
  if (platform === "ios") return <BsApple size={13} title="iOS" style={{ color: "#4A5568", flexShrink: 0 }} />;
  return (
    <span className="inline-flex items-center gap-1 flex-shrink-0" title="Android & iOS">
      <BsAndroid2 size={13} style={{ color: "#3DDC84" }} />
      <BsApple size={12} style={{ color: "#4A5568" }} />
    </span>
  );
}

// Compact Android / iOS / Both selector for a screen's platform.
function PlatformSelect({ value, onChange }: { value: ScreenPlatform; onChange: (p: ScreenPlatform) => void }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as ScreenPlatform)} onClick={(e) => e.stopPropagation()}
      className="text-xs border rounded-md px-1.5 py-1 flex-shrink-0" style={{ borderColor: "#E2E8F0", color: "#4A5568" }} aria-label="Platform">
      {PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
    </select>
  );
}

function ScreenListField({ screens, loading, selected, onToggle, onAdd, onSetPlatform, placeholder }: {
  screens: ScreenItem[];
  loading: boolean;
  selected: ScreenRef[];
  onToggle: (name: string) => void;
  onAdd: (name: string, platform: ScreenPlatform) => void;
  onSetPlatform: (name: string, platform: ScreenPlatform) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState("");
  const [addPlatform, setAddPlatform] = useState<ScreenPlatform>("both");
  const filter = search.trim().toLowerCase();
  const visible = filter ? screens.filter((s) => s.name.toLowerCase().includes(filter) || (s.label || "").toLowerCase().includes(filter)) : screens;
  const selectedNames = new Set(selected.map((s) => s.name));
  const add = () => { if (search.trim()) { void onAdd(search.trim(), addPlatform); setSearch(""); } };
  return (
    <>
      {/* Add a custom screen name WITH its platform (name differs per OS). min-w-0 keeps the
          input shrinkable so the dropdown + Add button always stay inside the panel. */}
      <div className="flex items-center gap-1.5 mb-2">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder || "Search or add a screen name…"} className="flex-1 min-w-0 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
        <PlatformSelect value={addPlatform} onChange={setAddPlatform} />
        <button type="button" disabled={!search.trim()} onClick={add}
          className="text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40 flex-shrink-0" style={{ background: "var(--brand-tint)", color: "var(--brand-primary)" }}>+ Add</button>
      </div>
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E2E8F0", maxHeight: 200, overflowY: "auto" }}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-5 text-xs" style={{ color: "#A0AEC0" }}><Loader2 size={13} className="animate-spin" /> Loading screens…</div>
        ) : visible.length === 0 ? (
          <div className="text-center px-3 py-5"><p className="text-xs mb-1" style={{ color: "#718096" }}>{filter ? "No screens match." : "No screens yet."}</p><p className="text-xs" style={{ color: "#A0AEC0" }}>Screens appear from your event data, or add one above.</p></div>
        ) : visible.map((s) => {
          const sel = selected.find((x) => x.name === s.name);
          return (
            <label key={s.name} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer border-b last:border-b-0" style={{ borderColor: "#EDF2F7" }}>
              <input type="checkbox" checked={selectedNames.has(s.name)} onChange={() => onToggle(s.name)} />
              {/* Platform mark in front of the name (Android/Apple) — the OS the screen belongs to. */}
              {s.platform && <PlatformIcon platform={s.platform} />}
              <span className="flex-1 min-w-0 text-sm truncate" style={{ color: "#2D3748" }}>{s.label || s.name}</span>
              {/* When selected, pick the platform this screen targets. */}
              {sel && <PlatformSelect value={sel.platform} onChange={(p) => onSetPlatform(s.name, p)} />}
              <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: STATUS_BADGE_SCREEN[s.status].bg, color: STATUS_BADGE_SCREEN[s.status].fg }}>{STATUS_BADGE_SCREEN[s.status].label}</span>
            </label>
          );
        })}
      </div>
      {/* Any selected screens the backend list doesn't contain (freshly added names) — still editable. */}
      {selected.filter((s) => !screens.some((sc) => sc.name === s.name)).map((s) => (
        <div key={s.name} className="flex items-center gap-2.5 px-3 py-2 mt-1 rounded-lg border" style={{ borderColor: "#EDF2F7" }}>
          <input type="checkbox" checked readOnly onClick={() => onToggle(s.name)} />
          <span className="flex-1 text-sm truncate" style={{ color: "#2D3748" }}>{s.name}</span>
          <PlatformSelect value={s.platform} onChange={(p) => onSetPlatform(s.name, p)} />
        </div>
      ))}
    </>
  );
}
// Single color control: native picker swatch + hex text (matches the Figma's color rows).
function ColorField({ value, onChange, label, dark, onDark }: { value?: string; onChange: (v: string) => void; label?: string; dark?: string; onDark?: (c: string | undefined) => void }) {
  const hex = swatchHex(value);
  return (
    <>
    <div className="flex gap-2 items-center">
      <input type="color" value={hex} onChange={(e) => onChange(e.target.value)} aria-label={label ? `${label} picker` : undefined}
        style={{ width: 36, height: 32, padding: 0, border: "1px solid #E2E8F0", borderRadius: 6, background: "none", cursor: "pointer" }} />
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={label ? `${label} hex` : undefined}
        className="flex-1 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} placeholder="#000000" />
    </div>
    {onDark && <DarkColorRow label={label || "Colour"} value={dark} onChange={onDark} />}
    </>
  );
}
// Emoji / icon picker: current glyph + a grid of common emoji + free text. (Icon/Badge)
const EMOJI_CHOICES = ["🔔","⭐","🎉","🎁","💎","🔥","✅","💰","🚀","❤️","📣","🏆","⚡","🛡️","🎯","💳","📈","🤝","🌟","🔓","👑","🧾","📦","⏰","💬","🎀","🪙","✨","📊","🙌","💡","🔑","📍","🎟️","🏦","💸","🧡","✔️","⚠️","🎊"];
function EmojiPicker({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <div className="flex gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Pick an emoji"
          className="flex items-center justify-center rounded-lg border"
          style={{ width: 44, height: 44, borderColor: "#E2E8F0", background: "#F7FAFC", fontSize: 22 }}>
          {value || "🙂"}
        </button>
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="Emoji or text" className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }} />
      </div>
      {open && (
        <div className="absolute z-40 mt-1 p-2 rounded-lg border shadow-lg grid grid-cols-8 gap-1"
          style={{ borderColor: "#E2E8F0", background: "#FFFFFF", width: 272 }}>
          {EMOJI_CHOICES.map((e) => (
            <button key={e} type="button" onClick={() => { onChange(e); setOpen(false); }}
              className="flex items-center justify-center rounded hover:bg-gray-100"
              style={{ width: 30, height: 30, fontSize: 18 }}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}
// Dynamic-value variable chips (R-1, DA-BUG-1049) — a chip appends `$key` (space-separated so it
// never glues onto the previous word). Fallback form `${key|fallback}`; `{{key}}` / `{{key|fallback}}`
// stay valid (both SDKs resolve all five syntaxes; inAppMergeTags.ts mirrors them).
function insertVariable(text: string, token: string): string {
  return text && !/\s$/.test(text) ? `${text} ${token}` : `${text}${token}`;
}
function MergeTags({ onInsert }: { onInsert: (token: string) => void }) {
  return (
    <div>
      <p className="text-xs mb-1.5" style={{ color: "#A0AEC0" }}>Dynamic value</p>
      <div className="flex flex-wrap gap-1.5">
        {/* P2 (DA-BUG-1381): don't offer email/phone as insertable chips — they're identifying
            keys the server never fills (they'd render as a fallback/tag). They stay valid if typed. */}
        {DEFAULT_VARIABLES.filter((t) => !IDENTIFYING_KEYS.includes(t)).map((t) => (
          <button key={t} type="button" onClick={() => onInsert(`$${t}`)}
            className="text-xs px-2 py-1 rounded-md border focus:outline-none"
            style={{ borderColor: "var(--brand-border)", color: "var(--brand-primary)", background: "var(--brand-tint)" }}>
            {`$${t}`}
          </button>
        ))}
      </div>
      <p className="text-[11px] mt-1.5" style={{ color: "#A0AEC0" }}>
        Add a fallback with <code>{"${first_name|valued customer}"}</code> — shown when the value is empty.
      </p>
    </div>
  );
}
// R2-16 — countdown duration as value × unit, stored in seconds (device `durationSeconds`).
const DURATION_UNITS: [string, string, number][] = [["minutes", "Minutes", 60], ["hours", "Hours", 3600], ["days", "Days", 86400]];
function DurationField({ seconds, onChange }: { seconds?: number; onChange: (v: number) => void }) {
  const s = Number(seconds) > 0 ? Number(seconds) : 0;
  const initial = s && s % 86400 === 0 ? "days" : s && s % 3600 === 0 ? "hours" : s ? "minutes" : "hours";
  const [unit, setUnit] = useState(initial);
  const mult = DURATION_UNITS.find(([u]) => u === unit)![2];
  const value = s ? Math.round((s / mult) * 100) / 100 : "";
  return (
    <div className="flex gap-2">
      <input type="number" min={1} aria-label="Countdown duration" className="w-24 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
        value={value} onChange={(e) => { const n = Number(e.target.value); onChange(Number.isFinite(n) && n > 0 ? Math.round(n * mult) : 0); }} />
      <select aria-label="Countdown duration unit" className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
        value={unit} onChange={(e) => { const next = e.target.value; const m = DURATION_UNITS.find(([u]) => u === next)![2]; setUnit(next); if (s) onChange(Math.round((s / mult) * m)); }}>
        {DURATION_UNITS.map(([u, l]) => <option key={u} value={u}>{l}</option>)}
      </select>
    </div>
  );
}
// Gap 5 — the message's coupon blocks, offered by a copyCoupon action ({id, label}).
export const CouponChoices = createContext<{ id: string; label: string }[]>([]);
// Unified action selector (Button / Tap / CTA / On-submit). Figma options:
// Dismiss · Navigate to screen · Open URL · Open deep link · Emit custom event · Go to next page · Copy coupon code.
const ACTION_LABELS: [ActionType, string][] = [
  ["none", "No action"], ["dismiss", "Dismiss message"], ["navigate", "Navigate to screen"], ["openUrl", "Open URL"],
  ["deepLink", "Open deep link"], ["emitEvent", "Emit custom event"], ["nextPage", "Go to next page"], ["previousPage", "Go to previous page"], ["copyCoupon", "Copy coupon code"],
];
// Default action menu excludes "No action" (button/CTA always do something); pass `allow` to narrow.
const BUTTON_ACTIONS: ActionType[] = ["dismiss", "navigate", "openUrl", "deepLink", "emitEvent", "nextPage", "previousPage", "copyCoupon"];
function ActionField({ action, onChange, allow, label }: { action?: BlockAction; onChange: (a: BlockAction) => void; allow?: ActionType[]; label?: string }) {
  const a = action || { type: "dismiss" };
  const coupons = useContext(CouponChoices);
  const opts = ACTION_LABELS.filter(([t]) => !allow || allow.includes(t));
  return (
    <div>
      <SelectField label={label} value={a.type} onChange={(v) => onChange({ ...a, type: v as ActionType })} options={opts} />
      {a.type === "navigate" && <div className="mt-2"><TextField value={a.screen} onChange={(v) => onChange({ ...a, screen: v })} placeholder="Screen name" /></div>}
      {a.type === "openUrl" && <div className="mt-2 space-y-2"><TextField value={a.url} onChange={(v) => onChange({ ...a, url: v })} placeholder="https://…" /><DeepLinkPicker onPick={(e: any) => onChange({ ...a, url: e.web_fallback_url || e.android_link || e.ios_link })} /></div>}
      {a.type === "deepLink" && <div className="mt-2 space-y-2"><TextField value={a.url} onChange={(v) => onChange({ ...a, url: v })} placeholder="app://path" /><TextField value={a.fallbackUrl} onChange={(v) => onChange({ ...a, fallbackUrl: v })} placeholder="Fallback https://…" /><DeepLinkPicker onPick={(e: any) => onChange({ ...a, url: e.android_link || e.ios_link, fallbackUrl: e.web_fallback_url })} /></div>}
      {a.type === "copyCoupon" && (
        <div className="mt-2">
          <SelectField label="Coupon to copy" value={a.couponBlockId || ""} onChange={(v) => onChange({ ...a, couponBlockId: v || undefined })}
            options={[["", coupons.length ? "First coupon in the message" : "No coupon block yet"], ...coupons.map((c) => [c.id, c.label] as [string, string])]} />
        </div>
      )}
      {a.type === "emitEvent" && <div className="mt-2"><TextField value={a.event} onChange={(v) => onChange({ ...a, event: v })} placeholder="event_name" /></div>}
    </div>
  );
}
// ─── Message Settings Panel ───────────────────────────────────────────────────

type PropTab = "style" | "trigger" | "audience" | "schedule" | "frequency" | "goal";

// Background-image upload/replace/remove for the Style panel. Reuses the Image-block upload flow
// (validateImageFile → uploadMediaFile: same endpoint, auth, tenant isolation, validation). A failed
// or cancelled upload keeps the current image (onUpload fires only on success); the caller preserves
// fit + scrim when it sets the new url.
export function BackgroundImageControls({ value, onUpload, onRemove, canSave = true }: {
  value?: string;
  onUpload: (url: string) => void;
  onRemove: () => void;
  canSave?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canSave) return;
    const file = e.target.files?.[0];
    if (!file) return;                       // cancelled → keep the current image
    const validationError = validateImageFile(file);
    if (validationError) { setError(validationError); e.target.value = ""; return; }
    setUploading(true);
    setError(null);
    try {
      const up = await uploadMediaFile(file);
      onUpload(up.url);                      // success → set url (caller keeps fit + scrim)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");  // failure → keep current
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="mt-2">
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} disabled={!canSave} />
      <div className="flex items-center gap-2">
        {value && (
          <div data-testid="bg-image-thumb" className="rounded-lg overflow-hidden border flex-shrink-0" style={{ borderColor: "#E2E8F0", width: 48, height: 48, background: "#F7FAFC" }}>
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <button type="button" data-testid="bg-image-upload"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60"
          style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
          disabled={uploading || !canSave}
          onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : value ? "Replace image" : "Upload background image"}
        </button>
        {value && (
          <button type="button" data-testid="bg-image-remove"
            className="py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60"
            style={{ borderColor: "#E2E8F0", color: "#E53E3E" }}
            disabled={uploading || !canSave}
            onClick={onRemove}>Remove</button>
        )}
      </div>
      {error && <p data-testid="bg-image-error" className="text-xs mt-1.5" style={{ color: "#E53E3E" }}>{error}</p>}
    </div>
  );
}

function MessageSettingsPanel({
  propTab, setPropTab, messageType, messageStyle, setMessageStyle,
  triggerSettings, setTriggerSettings,
  audienceSettings, setAudienceSettings,
  scheduleSettings, setScheduleSettings,
  frequencySettings, setFrequencySettings,
  goalSettings, setGoalSettings,
  deliverySettings, setDeliverySettings,
  hasDismissButton = false,
  pagesForAppearance = [],
  canSaveInAppMessage = true,
}: {
  pagesForAppearance?: { blocks: Block[]; style?: any }[];
  hasDismissButton?: boolean;
  canSaveInAppMessage?: boolean;
  propTab: PropTab;
  setPropTab: (t: PropTab) => void;
  messageType: MessageType;
  messageStyle: MessageStyle;
  setMessageStyle: (s: MessageStyle) => void;
  triggerSettings: TriggerSettings;
  setTriggerSettings: (s: TriggerSettings) => void;
  audienceSettings: AudienceSettings;
  setAudienceSettings: (s: AudienceSettings) => void;
  scheduleSettings: ScheduleSettings;
  setScheduleSettings: (s: ScheduleSettings) => void;
  frequencySettings: FrequencySettings;
  setFrequencySettings: (s: FrequencySettings) => void;
  goalSettings: GoalSettings;
  setGoalSettings: (s: GoalSettings) => void;
  deliverySettings: DeliverySettings;
  setDeliverySettings: (s: DeliverySettings) => void;
}) {
  const TABS: { id: PropTab; label: string; icon: React.ReactNode }[] = [
    { id: "style", label: "Style", icon: <Layers size={13} /> },
    { id: "trigger", label: "Trigger", icon: <Clock size={13} /> },
    { id: "audience", label: "Audience", icon: <Users size={13} /> },
    { id: "schedule", label: "Schedule", icon: <CalendarClock size={13} /> },
    { id: "frequency", label: "Frequency", icon: <Settings size={13} /> },
    { id: "goal", label: "Goal", icon: <Target size={13} /> },
  ];

  const [cohorts, setCohorts] = useState<{ id: string; name: string; userCount: number }[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [screens, setScreens] = useState<ScreenItem[]>([]);
  const [screensLoading, setScreensLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCohortsLoading(true);
      try {
        // Cohorts/segments engine; falls back to the main API when the dedicated host isn't set (local dev).
        const cohortBase = process.env.REACT_APP_COHORT || process.env.REACT_APP_API_URL;
        const { data: res } = await axios.get(`${cohortBase}/cohorts?page=1&limit=100`, { headers: getHeaders() });
        const rawCohorts = res?.data?.cohorts || res?.cohorts || [];
        const list = rawCohorts.map((c: any) => ({ id: c._id || c.id, name: c.name, userCount: c.user_count ?? c.userCount ?? 0 }));
        if (!cancelled) setCohorts(list);
      } catch {
        if (!cancelled) setCohorts([]);
      } finally {
        if (!cancelled) setCohortsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Contact tags (for Audience → Tags). From GET /tags.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTagsLoading(true);
      try {
        const { data: res } = await axios.get(`${process.env.REACT_APP_API_URL}/tags`, { headers: getHeaders() });
        const raw = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.tags) ? res.tags : (Array.isArray(res) ? res : []));
        const list = raw.map((tag: any) => ({ id: tag._id || tag.id, name: tag.name || tag.label })).filter((tag: { id: string; name: string }) => tag.id && tag.name);
        if (!cancelled) setTags(list);
      } catch {
        if (!cancelled) setTags([]);
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Screens that trigger evaluation — merged Registered (tbl_screens) + Observed (event data).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setScreensLoading(true);
      try {
        const { data: res } = await axios.get(`${process.env.REACT_APP_API_URL}/in-app/screens`, { headers: getHeaders() });
        const list: ScreenItem[] = (res?.data?.screens || []).map((s: any) => ({ name: s.name, label: s.label ?? null, status: s.status || "observed", platform: PLATFORMS.includes(s.platform) ? s.platform : undefined }));
        if (!cancelled) setScreens(list);
      } catch {
        if (!cancelled) setScreens([]);
      } finally {
        if (!cancelled) setScreensLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Conversion events for the Goal tab (from the Conversion Events module).
  const [conversionEvents, setConversionEvents] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res } = await axios.get(`${process.env.REACT_APP_API_URL}/event-conversations`, { headers: getHeaders() });
        const list = (res?.data?.event_conversations || res?.data?.eventConversations || []).map((e: any) => ({ id: e._id || e.id, name: e.event_name || e.name }));
        if (!cancelled) setConversionEvents(list);
      } catch { if (!cancelled) setConversionEvents([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  // P2-7 — every tracked event name (POST /fetch-unique-events, as Conversion Events uses).
  const [trackedEvents, setTrackedEvents] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    axios.post(`${process.env.REACT_APP_API_URL}/fetch-unique-events`, {}, { headers: getApiHeaders() })
      .then(({ data }) => { if (!cancelled) setTrackedEvents((data?.data?.events || []).map((e: any) => e?.name).filter((n: any) => typeof n === "string" && n)); })
      .catch(() => { if (!cancelled) setTrackedEvents([]); });
    return () => { cancelled = true; };
  }, []);

  // Property names of a selected event (Trigger → Custom event occurs, and Goal →
  // Conversion event). Cached per event name; feeds the condition property dropdown.
  const [eventAttrs, setEventAttrs] = useState<Record<string, string[]>>({});
  const [eventAttrsLoading, setEventAttrsLoading] = useState(false);
  const requestedAttrsRef = useRef<Set<string>>(new Set());
  const loadEventAttrs = (eventName: string) => {
    const ev = (eventName || "").trim();
    if (!ev || requestedAttrsRef.current.has(ev)) return;
    requestedAttrsRef.current.add(ev);
    setEventAttrsLoading(true);
    axios.post(`${process.env.REACT_APP_API_URL}/fetch-unique-attributes`, { event: ev }, { headers: getHeaders() })
      .then(({ data }) => {
        const names: string[] = (data?.data?.attributes || []).map((a: any) => a?.name).filter(Boolean);
        setEventAttrs((p) => ({ ...p, [ev]: names }));
      })
      .catch(() => setEventAttrs((p) => ({ ...p, [ev]: [] })))
      .finally(() => setEventAttrsLoading(false));
  };
  // Trigger → Custom event occurs
  useEffect(() => {
    if (triggerSettings.event === "custom_event") loadEventAttrs(triggerSettings.customEvent || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSettings.event, triggerSettings.customEvent]);
  // Goal → Conversion event. goalSettings.event holds the event NAME (DA-BUG-0691: it used to hold
  // the Conversion-Events row id, which Event's funnel never matches). A message saved with an id is
  // mapped to the name once the Conversion Events list arrives; the next save stores the name.
  useEffect(() => {
    const name = goalEventName(goalSettings.event, conversionEvents);
    if (name !== goalSettings.event) { setGoalSettings({ ...goalSettings, event: name }); return; }
    loadEventAttrs(name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalSettings.event, conversionEvents]);

  // A screen's default platform = its registered platform if known, else "both".
  const defaultPlatformFor = (name: string): ScreenPlatform =>
    (screens.find((s) => s.name === name)?.platform) || "both";
  const toggleScreen = (name: string) => {
    const cur = triggerSettings.screens || [];
    const next = cur.some((s) => s.name === name)
      ? cur.filter((s) => s.name !== name)
      : [...cur, { name, platform: defaultPlatformFor(name) }];
    setTriggerSettings({ ...triggerSettings, screens: next });
  };
  const setScreenPlatform = (name: string, platform: ScreenPlatform) => {
    setTriggerSettings({ ...triggerSettings, screens: (triggerSettings.screens || []).map((s) => (s.name === name ? { ...s, platform } : s)) });
  };
  const toggleExcludedScreen = (name: string) => {
    const cur = triggerSettings.excludedScreens || [];
    const next = cur.some((s) => s.name === name)
      ? cur.filter((s) => s.name !== name)
      : [...cur, { name, platform: defaultPlatformFor(name) }];
    setTriggerSettings({ ...triggerSettings, excludedScreens: next });
  };
  const setExcludedScreenPlatform = (name: string, platform: ScreenPlatform) => {
    setTriggerSettings({ ...triggerSettings, excludedScreens: (triggerSettings.excludedScreens || []).map((s) => (s.name === name ? { ...s, platform } : s)) });
  };
  const toggleTag = (id: string) => {
    const ids = audienceSettings.tagIds || [];
    const nextIds = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    const nextNames = tags.filter((t) => nextIds.includes(t.id)).map((t) => t.name);
    setAudienceSettings({ ...audienceSettings, tagIds: nextIds, tagNames: nextNames });
  };
  const toggleDay = (d: number) => {
    const days = scheduleSettings.activeDays || [];
    setScheduleSettings({ ...scheduleSettings, activeDays: days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b) });
  };
  // Register a screen name into the merged list (so you can target screens the backend
  // hasn't observed yet). Best-effort POST; returns the cleaned name. Selection is handled
  // by the caller so the same registration feeds both trigger screens and excluded screens.
  const ensureScreenRegistered = async (name: string, platform: ScreenPlatform): Promise<string> => {
    const clean = name.trim();
    if (!clean) return clean;
    if (!screens.some((s) => s.name === clean)) {
      setScreens((prev) => [...prev, { name: clean, label: null, status: "registered" as const, platform }].sort((a, b) => a.name.localeCompare(b.name)));
      try { await axios.post(`${process.env.REACT_APP_API_URL}/in-app/screens`, { name: clean, platform }, { headers: getHeaders() }); } catch { /* best-effort */ }
    }
    return clean;
  };
  // Add-a-screen for "Screens that trigger evaluation" (selects into trigger.screens).
  const addTriggerScreen = async (name: string, platform: ScreenPlatform) => {
    const clean = await ensureScreenRegistered(name, platform);
    if (clean && !(triggerSettings.screens || []).some((s) => s.name === clean)) {
      setTriggerSettings({ ...triggerSettings, screens: [...(triggerSettings.screens || []), { name: clean, platform }] });
    }
  };
  // Add-a-screen for "Excluded screens" (selects into trigger.excludedScreens).
  const addExcludedScreen = async (name: string, platform: ScreenPlatform) => {
    const clean = await ensureScreenRegistered(name, platform);
    if (clean && !(triggerSettings.excludedScreens || []).some((s) => s.name === clean)) {
      setTriggerSettings({ ...triggerSettings, excludedScreens: [...(triggerSettings.excludedScreens || []), { name: clean, platform }] });
    }
  };

  return (
    <div>
      {/* Tabs — 3-column grid, text-only, solid-blue selected (matches Figma) */}
      <div className="grid grid-cols-3 gap-1.5 mb-4">
        {TABS.map((tab) => (
          <button key={tab.id}
            className="text-xs font-semibold py-2 rounded-lg transition-colors focus:outline-none"
            style={{
              backgroundColor: propTab === tab.id ? "var(--brand-primary)" : "#F1F5F9",
              color: propTab === tab.id ? "#FFFFFF" : "#64748B",
            }}
            onClick={() => setPropTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {propTab === "style" && (() => {
        // BUG-03 — every Style control is gated by the surface's config, so a
        // meaningless control (corner radius on a full-bleed fullscreen, backdrop
        // on an edge banner) simply isn't shown.
        const cfg = SURFACE_CONFIG[messageType];
        const set = (patch: Partial<MessageStyle>) => setMessageStyle({ ...messageStyle, ...patch });
        return (
          <div>
            <SectionHeader>Appearance</SectionHeader>
            <PanelSection title="Light / dark" hint="Auto follows the phone's setting. Messages without any Dark colour stay light.">
              <SelectField label="Appearance" value={effectiveAppearance(messageStyle.appearance, pagesForAppearance)}
                onChange={(v) => set({ appearance: v as Appearance })}
                options={[["auto", "Auto (follow the device)"], ["light", "Always light"], ["dark", "Always dark"]]} />
            </PanelSection>
            <SectionHeader>Container</SectionHeader>
            <PanelSection title="Background">
              <ColorField label="Background" value={messageStyle.background} onChange={(c) => set({ background: c })} dark={messageStyle.backgroundDark} onDark={(c) => set({ backgroundDark: c })} />
              <div className="mt-2"><ColorSwatches colors={BG_COLORS} value={messageStyle.background} onChange={(c) => set({ background: c })} /></div>
              {cfg.style.solidBackgroundOnly && <p className="text-xs" style={{ color: "#A0AEC0" }}>Banners use a solid background only.</p>}
            </PanelSection>
            {/* FEAT-INAPP-MODAL-BG: Modal supports a card background image + scrim, like Fullscreen. */}
            {(messageType === "fullscreen" || messageType === "modal") && (
              <PanelSection title="Background image" hint="Drawn behind the content, with an optional dark overlay so text stays readable.">
                <TextField label="Background image URL" field="backgroundImage.url" value={messageStyle.backgroundImage?.url}
                  onChange={(v) => set({ backgroundImage: v.trim() ? { url: v, fit: messageStyle.backgroundImage?.fit || "cover" } : undefined })} placeholder="https://…/photo.jpg" />
                {/* Upload / Replace / Remove — reuses the Image-block upload flow; preserves fit + scrim. */}
                <BackgroundImageControls
                  value={messageStyle.backgroundImage?.url}
                  canSave={canSaveInAppMessage}
                  onUpload={(url) => set({ backgroundImage: { url, fit: messageStyle.backgroundImage?.fit || "cover" } })}
                  onRemove={() => set({ backgroundImage: undefined })} />
                {messageStyle.backgroundImage?.url && (
                  <div className="mt-2 space-y-2">
                    <SelectField label="Background image fit" value={messageStyle.backgroundImage.fit || "cover"}
                      onChange={(v) => set({ backgroundImage: { url: messageStyle.backgroundImage!.url, fit: v === "contain" ? "contain" : "cover" } })}
                      options={[["cover", "Cover (fill, crop)"], ["contain", "Contain (whole image)"]]} />
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-20" style={{ color: "#718096" }}>Scrim</span>
                      <input type="range" min={0} max={80} aria-label="Scrim opacity" className="flex-1"
                        value={Math.round((messageStyle.scrimOpacity || 0) * 100)} onChange={(e) => set({ scrimOpacity: Number(e.target.value) / 100 })} />
                      <span className="text-xs w-10 text-right" style={{ color: "#718096" }}>{Math.round((messageStyle.scrimOpacity || 0) * 100)}%</span>
                    </div>
                    <ColorField label="Scrim colour" value={messageStyle.scrimColor || "#000000"} onChange={(c) => set({ scrimColor: c })}
                      dark={messageStyle.scrimColorDark} onDark={(c) => set({ scrimColorDark: c })} />
                  </div>
                )}
              </PanelSection>
            )}
            {cfg.style.width && (
              <PanelSection title="Width (% of screen)"><RangeField label="Width (% of screen)" min={50} max={100} value={messageStyle.width ?? 90} onChange={(v) => set({ width: v })} /></PanelSection>
            )}
            {cfg.style.cornerRadius && (
              <PanelSection title="Corner radius"><RangeField label="Corner radius" min={0} max={32} value={messageStyle.cornerRadius} onChange={(v) => set({ cornerRadius: v })} /></PanelSection>
            )}
            {cfg.style.backdropOpacity && (
              <PanelSection title="Backdrop opacity"><RangeField label="Backdrop opacity" min={0} max={80} value={messageStyle.backdropOpacity} onChange={(v) => set({ backdropOpacity: v })} /></PanelSection>
            )}

            {cfg.style.positions.length > 0 && (
              <>
                <SectionHeader>Placement</SectionHeader>
                <PanelSection title="Position">
                  <SelectField value={messageStyle.position} onChange={(v) => set({ position: v })} options={cfg.style.positions.map(p => [p.value, p.label] as [string, string])} />
                </PanelSection>
              </>
            )}

            {cfg.dismiss.closeButton !== "none" && (
              <>
                <SectionHeader>Close button</SectionHeader>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-sm" style={{ color: "#4A5568" }}>Show close button {cfg.dismiss.closeButton === "required" && <span style={{ color: "#E53E3E" }}>*</span>}</span>
                  <Toggle checked={messageStyle.closeButton} onChange={(v) => { if (cfg.dismiss.closeButton !== "required") set({ closeButton: v }); }} />
                </div>
                {messageStyle.closeButton && (
                  <PanelSection title="Position">
                    <SelectField value={messageStyle.closeButtonPosition || "top-right"} onChange={(v) => set({ closeButtonPosition: v })}
                      options={[["top-right", "Top right"], ["top-left", "Top left"], ["inside", "Inside content"]]} />
                  </PanelSection>
                )}
                {/* DA-BUG-1401 (opt-in, modal only): first image fills the top edge with the close overlaid. */}
                {messageType === "modal" && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm" style={{ color: "#4A5568" }}>Image to top edge
                      <span className="block text-xs" style={{ color: "#A0AEC0" }}>First image fills the top; close overlays it</span>
                    </span>
                    <Toggle checked={!!messageStyle.imageHeroTop} onChange={(v) => set({ imageHeroTop: v })} />
                  </div>
                )}
                {cfg.dismiss.tapBackdrop && (
                  <div className="flex items-center justify-between py-1.5"><span className="text-sm" style={{ color: "#4A5568" }}>Tap backdrop to close</span><Toggle checked={messageStyle.tapBackdrop} onChange={(v) => set({ tapBackdrop: v })} /></div>
                )}
                {cfg.dismiss.swipe && (
                  <div className="flex items-center justify-between py-1.5"><span className="text-sm" style={{ color: "#4A5568" }}>Swipe to dismiss</span><Toggle checked={messageStyle.swipeToDismiss} onChange={(v) => set({ swipeToDismiss: v })} /></div>
                )}
                {cfg.dismiss.autoDismiss !== "none" && (
                  <PanelSection title="Auto-dismiss (seconds)">
                    <div className="flex items-center gap-2">
                      <input type="range" min={0} max={30} value={messageStyle.autoDismissSecs} onChange={(e) => set({ autoDismissSecs: Number(e.target.value) })} className="flex-1" />
                      <span className="text-xs w-14 text-right" style={{ color: "#718096" }}>{messageStyle.autoDismissSecs > 0 ? `${messageStyle.autoDismissSecs}s` : "Off"}</span>
                    </div>
                  </PanelSection>
                )}
                {!hasExitPath(messageStyle, messageType) && !hasDismissButton && (
                  <p className="text-xs mt-1" style={{ color: "#E53E3E" }}>⚠ This message has no way to close — add a close button, swipe, auto-dismiss, or a button whose action is Dismiss.</p>
                )}
              </>
            )}

            <SectionHeader>Animation</SectionHeader>
            <PanelSection title="Entry animation">
              <SelectField value={messageStyle.animation} onChange={(v) => set({ animation: v })} options={cfg.style.animations.map(a => [a.value, a.label] as [string, string])} />
            </PanelSection>
          </div>
        );
      })()}

      {propTab === "trigger" && (() => {
        const t = triggerSettings;
        const setT = (patch: Partial<TriggerSettings>) => setTriggerSettings({ ...t, ...patch });
        // P2-7 — any tracked event (event schema) or conversion event is suggested, and any name
        // can be typed (an event the app will start sending). No fixed list.
        const eventOpts: string[] = Array.from(new Set([...trackedEvents, ...conversionEvents.map((e) => e.name)].filter(Boolean)));
        return (
        <div>
          <SectionHeader>Show when</SectionHeader>
          <div className="space-y-1.5 mb-4">
            {([
              ["app_open", "App opened", "The app launches or returns to the foreground."],
              ["session_start", "Session started", "A new engagement session begins."],
              ["screen_view", "Screen viewed", "A registered screen becomes active."],
              ["screen_time", "Time spent on a screen", "The customer stays on a screen for a set duration."],
              ["custom_event", "Custom event occurs", "The app reports a selected business event."],
              ["session_time", "Time spent in a session", "Active time in the session reaches a threshold."],
              ["app_request", "App requests display", "The application explicitly requests this message opportunity."],
            ] as const).map(([val, label, desc]) => {
              const on = t.event === val;
              return (
                <label key={val} className="flex items-start gap-2.5 cursor-pointer rounded-lg p-2.5 transition-colors"
                  style={{ border: `1px solid ${on ? "var(--brand-primary)" : "#E2E8F0"}`, background: on ? "var(--brand-tint)" : "#FFFFFF" }}
                  onClick={() => setT({ event: val })}>
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ borderColor: on ? "var(--brand-primary)" : "#CBD5E0" }}>
                    {on && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium" style={{ color: "#2D3748" }}>{label}</p>
                    <p className="text-xs" style={{ color: "#A0AEC0" }}>{desc}</p>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Conditional controls per trigger type */}
          {t.event === "screen_view" && (
            <>
              <SectionHeader>Screens that trigger evaluation</SectionHeader>
              <ScreenListField screens={screens} loading={screensLoading} selected={t.screens || []} onToggle={toggleScreen} onAdd={addTriggerScreen} onSetPlatform={setScreenPlatform} />
              <div className="flex items-center justify-between mt-3">
                <div><p className="text-xs font-medium" style={{ color: "#4A5568" }}>Require same screen at display time</p><p className="text-xs" style={{ color: "#A0AEC0" }}>Cancel if the customer navigates away before the delay elapses.</p></div>
                <Toggle checked={!!t.requireSameScreen} onChange={(v) => setT({ requireSameScreen: v })} />
              </div>
            </>
          )}

          {t.event === "screen_time" && (
            <>
              <PanelSection title="Screen">
                {/* Picking a screen defaults its platform from the screens list; the selector
                    lets you override which OS this OS-specific name applies to. */}
                <div className="flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <SelectField value={t.screenName || ""} onChange={(v) => setT({ screenName: v, screenPlatform: screens.find((s) => s.name === v)?.platform || "both" })}
                      options={[["", "Select screen"], ...screens.map((s) => [s.name, s.name] as [string, string])]} />
                  </div>
                  <PlatformSelect value={t.screenPlatform || "both"} onChange={(p) => setT({ screenPlatform: p })} />
                </div>
              </PanelSection>
              <PanelSection title="Active duration">
                <div className="flex gap-2">
                  <input type="number" min={1} className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                    value={t.activeDuration ?? 30} onChange={(e) => setT({ activeDuration: Number(e.target.value) })} />
                  <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={t.activeDurationUnit || "seconds"} onChange={(e) => setT({ activeDurationUnit: e.target.value as "seconds" | "minutes" })}><option value="seconds">Seconds</option><option value="minutes">Minutes</option></select>
                </div>
                <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>Only counts active time — background time does not count.</p>
              </PanelSection>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm" style={{ color: "#4A5568" }}>Reset timer when leaving screen</span>
                <Toggle checked={t.resetTimerOnLeave !== false} onChange={(v) => setT({ resetTimerOnLeave: v })} />
              </div>
            </>
          )}

          {t.event === "custom_event" && (
            <>
              <PanelSection title="Event">
                <input type="text" list="custom-event-options" aria-label="Custom event name"
                  className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                  placeholder="Search or type an event name" value={t.customEvent || ""}
                  onChange={(e) => setT({ customEvent: e.target.value })} />
                <datalist id="custom-event-options">
                  {eventOpts.map((v) => <option key={v} value={v} />)}
                </datalist>
              </PanelSection>
              <PanelSection title="Only when it occurs">
                <div className="flex items-center gap-2">
                  <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={t.occurrenceOp || "at_least"} onChange={(e) => setT({ occurrenceOp: e.target.value as "exactly" | "at_least" })}><option value="exactly">exactly</option><option value="at_least">at least</option></select>
                  <input type="number" min={1} className="w-16 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={t.occurrenceCount ?? 1} onChange={(e) => setT({ occurrenceCount: Number(e.target.value) })} />
                  <span className="text-xs" style={{ color: "#718096" }}>time(s)</span>
                </div>
              </PanelSection>
              <PanelSection title="Event property conditions (optional)">
                <ConditionBuilder conditions={t.eventConditions || []} onChange={(c) => setT({ eventConditions: c })}
                  propertyOptions={t.customEvent ? (eventAttrs[t.customEvent.trim()] || []) : []}
                  propertyLoading={eventAttrsLoading} />
              </PanelSection>
            </>
          )}

          {t.event === "session_time" && (
            <PanelSection title="Active time in session reaches">
              <div className="flex gap-2">
                <input type="number" min={1} className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                  value={t.sessionThreshold ?? 60} onChange={(e) => setT({ sessionThreshold: Number(e.target.value) })} />
                <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={t.sessionThresholdUnit || "seconds"} onChange={(e) => setT({ sessionThresholdUnit: e.target.value as "seconds" | "minutes" })}><option value="seconds">Seconds</option><option value="minutes">Minutes</option></select>
              </div>
            </PanelSection>
          )}

          {t.event === "app_request" && (
            <>
              <div className="flex items-start gap-2 rounded-lg p-2.5 mb-1" style={{ background: "#EBF4FF", border: "1px solid #BEE3F8" }}>
                <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: "#3182CE" }} />
                <p className="text-xs" style={{ color: "#2C5282" }}>The application must explicitly request this trigger by its key. It will not fire automatically.</p>
              </div>
              <PanelSection title="Trigger key">
                <input type="text" value={t.requestKey || ""} onChange={(e) => setT({ requestKey: e.target.value })}
                  placeholder="e.g. premium_upgrade_prompt" className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }} />
                <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>The identifier your app passes when it requests this message.</p>
              </PanelSection>
              <PanelSection title="Context property conditions (optional)">
                <ConditionBuilder conditions={t.contextConditions || []} onChange={(c) => setT({ contextConditions: c })} />
              </PanelSection>
            </>
          )}

          <PanelSection title="Display delay">
            <div className="flex gap-2">
              <input type="number" min={0} className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                value={t.delay} onChange={(e) => setT({ delay: Number(e.target.value) })} />
              <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={t.delayUnit} onChange={(e) => setT({ delayUnit: e.target.value })}><option value="seconds">Seconds</option><option value="minutes">Minutes</option></select>
            </div>
          </PanelSection>

          <CollapsibleSection title="Excluded screens">
            <p className="text-xs mb-2" style={{ color: "#A0AEC0" }}>This message is never shown on these screens (e.g. payment or login flows).</p>
            <ScreenListField screens={screens} loading={screensLoading} selected={t.excludedScreens || []} onToggle={toggleExcludedScreen} onAdd={addExcludedScreen} onSetPlatform={setExcludedScreenPlatform} />
          </CollapsibleSection>
        </div>
        );
      })()}

      {propTab === "audience" && (
        <div>
          <PanelSection title="Who can receive this message">
            <div className="space-y-1.5">
              {([
                ["all", "All Customers", "Everyone who passes the trigger."],
                ["segments", "Segment / Cohort", "Only members of the segment you pick."],
                ["tags", "Tags", "Only contacts carrying the tags you pick."],
              ] as const).map(([val, label, desc]) => (
                <label key={val} className="flex items-start gap-2.5 cursor-pointer rounded-lg p-2"
                  style={{ border: `1px solid ${audienceSettings.type === val ? "var(--brand-border)" : "#E2E8F0"}`, background: audienceSettings.type === val ? "var(--brand-tint)" : "#FFFFFF" }}
                  onClick={() => setAudienceSettings({ ...audienceSettings, type: val })}>
                  <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ borderColor: audienceSettings.type === val ? "var(--brand-primary)" : "#CBD5E0" }}>
                    {audienceSettings.type === val && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
                  </div>
                  <div><p className="text-xs font-medium" style={{ color: "#2D3748" }}>{label}</p><p className="text-xs" style={{ color: "#A0AEC0" }}>{desc}</p></div>
                </label>
              ))}
            </div>
          </PanelSection>

          {audienceSettings.type === "segments" && (
            <PanelSection title="Segment / Cohort">
              {cohortsLoading ? (
                <div className="flex items-center gap-2 text-xs py-2" style={{ color: "#A0AEC0" }}><Loader2 size={13} className="animate-spin" /> Loading segments…</div>
              ) : (
                <SelectField value={(audienceSettings.cohortIds || [])[0] || ""}
                  onChange={(id) => {
                    const c = cohorts.find((x) => x.id === id);
                    setAudienceSettings({ ...audienceSettings, cohortIds: id ? [id] : [], cohortNames: c ? [c.name] : [], estimatedReach: c ? c.userCount : 0 });
                  }}
                  options={[["", cohorts.length ? "Select a segment / cohort" : "No segments found"], ...cohorts.map((c) => [c.id, `${c.name} (${c.userCount.toLocaleString()})`] as [string, string])]} />
              )}
            </PanelSection>
          )}
          {audienceSettings.type === "tags" && (
            <PanelSection title="Tags">
              <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#E2E8F0", maxHeight: 200, overflowY: "auto" }}>
                {tagsLoading ? (
                  <div className="flex items-center justify-center gap-2 py-4 text-xs" style={{ color: "#A0AEC0" }}><Loader2 size={13} className="animate-spin" /> Loading tags…</div>
                ) : tags.length === 0 ? (
                  <div className="text-center px-3 py-4 text-xs" style={{ color: "#A0AEC0" }}>No tags found — create tags on the Contacts page.</div>
                ) : tags.map((tag) => (
                  <label key={tag.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer border-b last:border-b-0" style={{ borderColor: "#EDF2F7" }}>
                    <input type="checkbox" checked={(audienceSettings.tagIds || []).includes(tag.id)} onChange={() => toggleTag(tag.id)} />
                    <span className="flex-1 text-sm truncate" style={{ color: "#2D3748" }}>{tag.name}</span>
                  </label>
                ))}
              </div>
            </PanelSection>
          )}

          <div className="rounded-xl p-3 border" style={{ backgroundColor: "var(--brand-tint)", borderColor: "var(--brand-border)" }}>
            <p className="text-xs font-semibold mb-0.5" style={{ color: "var(--brand-primary-hover)" }}>Audience summary</p>
            <p className="text-sm font-bold" style={{ color: "#1E3A8A" }}>
              {audienceSettings.type === "" ? "No audience chosen yet"
                : audienceSettings.type === "all" ? "All Customers"
                : audienceSettings.type === "segments" ? ((audienceSettings.cohortNames || [])[0] ? `${audienceSettings.cohortNames![0]} · ~${(audienceSettings.estimatedReach ?? 0).toLocaleString()} customers` : "No segment selected")
                : `${(audienceSettings.tagIds || []).length} tag(s) selected`}
            </p>
          </div>
        </div>
      )}

      {propTab === "schedule" && (() => {
        const s = scheduleSettings;
        const setS = (patch: Partial<ScheduleSettings>) => setScheduleSettings({ ...s, ...patch });
        const DAY_FULL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        const TZ_LABEL: Record<string, string> = { customer_local: "Customer local time", UTC: "UTC", "Asia/Karachi": "Asia/Karachi (PKT)", "America/New_York": "America/New_York (EST)" };
        const days = (s.activeDays || []).slice().sort((a, b) => a - b);
        const daysSummary = days.length === 7 ? "Every day" : days.length === 0 ? "No days" : days.map((i) => DAY_FULL[i]).join(", ");
        const summary = [
          s.startMode === "scheduled" && s.startDate ? `Starts ${s.startDate}` : "Active immediately",
          s.endEnabled && s.endDate ? `Ends ${s.endDate}` : "No end date",
          daysSummary,
          s.timeWindowEnabled && s.timeStart && s.timeEnd ? `${s.timeStart}–${s.timeEnd}` : "All hours",
          TZ_LABEL[s.timezone] || s.timezone,
        ].join(" · ");
        return (
        <div>
          <SectionHeader>Campaign start</SectionHeader>
          {([["immediate", "Start immediately"], ["scheduled", "Schedule start date"]] as const).map(([val, label]) => (
            <label key={val} className="flex items-center gap-2.5 cursor-pointer py-1.5" onClick={() => setS({ startMode: val })}>
              <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: s.startMode === val ? "var(--brand-primary)" : "#CBD5E0" }}>
                {s.startMode === val && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />}
              </div>
              <span className="text-sm" style={{ color: "#2D3748" }}>{label}</span>
            </label>
          ))}
          {s.startMode === "scheduled" && (
            <input type="date" className="w-full text-sm border rounded-lg p-2.5 mt-2" style={{ borderColor: "#E2E8F0" }}
              value={s.startDate} onChange={(e) => setS({ startDate: e.target.value })} />
          )}

          <div className="flex items-center justify-between mt-5">
            <span className="text-sm font-medium" style={{ color: "#2D3748" }}>Set end date</span>
            <Toggle checked={s.endEnabled} onChange={(v) => setS({ endEnabled: v })} />
          </div>
          {s.endEnabled && (
            <div className="mt-2">
              <p className="text-sm font-medium mb-1.5" style={{ color: "#2D3748" }}>End date</p>
              <input type="date" className="w-full text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                value={s.endDate} onChange={(e) => setS({ endDate: e.target.value })} />
              <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>Campaign stops showing after this date.</p>
            </div>
          )}

          <SectionHeader>Active days</SectionHeader>
          <div className="flex gap-1">
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <button key={i} type="button" onClick={() => toggleDay(i)}
                className="flex-1 text-xs font-semibold rounded-md py-2 transition-colors"
                style={{ background: (s.activeDays || []).includes(i) ? "var(--brand-primary)" : "#FFFFFF", color: (s.activeDays || []).includes(i) ? "#fff" : "#718096", border: "1px solid #E2E8F0" }}>{d}</button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-5">
            <span className="text-sm font-medium" style={{ color: "#2D3748" }}>Restrict to time window</span>
            <Toggle checked={s.timeWindowEnabled} onChange={(v) => setS({ timeWindowEnabled: v })} />
          </div>
          {s.timeWindowEnabled && (
            <div className="flex gap-2 mt-2">
              <input type="time" className="flex-1 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={s.timeStart} onChange={(e) => setS({ timeStart: e.target.value })} />
              <input type="time" className="flex-1 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={s.timeEnd} onChange={(e) => setS({ timeEnd: e.target.value })} />
            </div>
          )}

          <div className="mt-5">
            <p className="text-sm font-medium mb-1.5" style={{ color: "#2D3748" }}>Timezone</p>
            <SelectField value={s.timezone} onChange={(v) => setS({ timezone: v })}
              options={[["customer_local", "Customer's local timezone"], ["UTC", "UTC"], ["Asia/Karachi", "Asia/Karachi (PKT)"], ["America/New_York", "America/New_York (EST)"]]} />
          </div>
          <div className="mt-3">
            <p className="text-sm font-medium mb-1.5" style={{ color: "#2D3748" }}>Fallback timezone</p>
            <SelectField value={s.fallbackTimezone} onChange={(v) => setS({ fallbackTimezone: v })}
              options={[["UTC", "UTC"], ["Asia/Karachi", "Asia/Karachi"]]} />
            <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>Used when the customer timezone is unknown.</p>
          </div>

          <div className="rounded-xl p-3 border mt-5" style={{ backgroundColor: "var(--brand-tint)", borderColor: "var(--brand-border)" }}>
            <p className="text-xs font-semibold mb-0.5" style={{ color: "var(--brand-primary-hover)" }}>Summary</p>
            <p className="text-sm" style={{ color: "#1E3A8A" }}>{summary}</p>
          </div>
        </div>
        );
      })()}

      {propTab === "frequency" && (
        <div>
          <p className="text-xs mb-3" style={{ color: "#A0AEC0" }}>Limits are applied per identified customer. When identity is unavailable, per installation.</p>
          <PanelSection title="Once per session" hint="Show this message at most once within a single app session."><Toggle checked={frequencySettings.oncePerSession} onChange={(v) => setFrequencySettings({ ...frequencySettings, oncePerSession: v })} /></PanelSection>
          <PanelSection title="Exempt from the global cap" hint="For critical messages: shown even when the tenant-wide limit (In-App settings) is reached, and not counted toward it."><Toggle checked={!!deliverySettings.exemptFromGlobalCap} onChange={(v) => setDeliverySettings({ ...deliverySettings, exemptFromGlobalCap: v })} /></PanelSection>
          <PanelSection title="Maximum impressions per day" hint="Cap how many times a customer sees this message in one day.">
            <div className="flex items-center gap-2">
              <Toggle checked={frequencySettings.maxPerDayEnabled} onChange={(v) => setFrequencySettings({ ...frequencySettings, maxPerDayEnabled: v })} />
              {frequencySettings.maxPerDayEnabled && <input type="number" min={1} className="w-1/2 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={frequencySettings.maxPerDay} onChange={(e) => setFrequencySettings({ ...frequencySettings, maxPerDay: Number(e.target.value) })} />}
            </div>
          </PanelSection>
          <PanelSection title="Maximum lifetime impressions" hint="Total times this message can ever be shown to a customer.">
            <div className="flex items-center gap-2">
              <Toggle checked={frequencySettings.maxLifetimeEnabled} onChange={(v) => setFrequencySettings({ ...frequencySettings, maxLifetimeEnabled: v })} />
              {frequencySettings.maxLifetimeEnabled && <input type="number" min={1} className="w-1/2 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={frequencySettings.maxLifetime} onChange={(e) => setFrequencySettings({ ...frequencySettings, maxLifetime: Number(e.target.value) })} />}
            </div>
          </PanelSection>
          <PanelSection title="Cooldown after dismissal" hint="Wait this long before showing it again once a customer dismisses it.">
            <div className="flex items-center gap-2">
              <Toggle checked={frequencySettings.cooldownEnabled} onChange={(v) => setFrequencySettings({ ...frequencySettings, cooldownEnabled: v })} />
              {frequencySettings.cooldownEnabled && <>
                <input type="number" min={1} className="w-1/2 text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={frequencySettings.cooldownValue} onChange={(e) => setFrequencySettings({ ...frequencySettings, cooldownValue: Number(e.target.value) })} />
                <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }} value={frequencySettings.cooldownUnit} onChange={(e) => setFrequencySettings({ ...frequencySettings, cooldownUnit: e.target.value as "hours" | "days" })}><option value="hours">Hours</option><option value="days">Days</option></select>
              </>}
            </div>
          </PanelSection>
          <PanelSection title="Stop showing after conversion" hint="Stop showing it once the customer completes the goal event."><Toggle checked={frequencySettings.stopAfterConversion} onChange={(v) => setFrequencySettings({ ...frequencySettings, stopAfterConversion: v })} /></PanelSection>

          <CollapsibleSection title="Delivery rules">
            <p className="text-xs mb-2" style={{ color: "#A0AEC0" }}>When two or more messages qualify at the same moment, only one can show. The SDK picks the highest priority (ties → most recently published).</p>
            <PanelSection title="Priority" hint="Higher priority wins when messages compete to display.">
              <SelectField value={deliverySettings.priority} onChange={(v) => setDeliverySettings({ ...deliverySettings, priority: v as DeliverySettings["priority"] })}
                options={[["low", "Low"], ["normal", "Normal"], ["high", "High"], ["urgent", "Urgent"]]} />
            </PanelSection>
            <PanelSection title="When another message is already showing" hint="One in-app message is shown at a time. “Replace” interrupts only when this message is higher priority.">
              <SelectField value={deliverySettings.onConflict} onChange={(v) => setDeliverySettings({ ...deliverySettings, onConflict: v as DeliverySettings["onConflict"] })}
                options={[["discard", "Skip this one (it can trigger again later)"], ["queue", "Show it after the current one closes"], ["replace", "Replace the message that's showing"]]} />
            </PanelSection>
            <PanelSection title="Show on devices that haven't synced recently" hint={`A phone that hasn't fetched messages for ${STALE_DAYS} days hides this message, in case it was paused or changed since. Turn on to show it anyway (for example, onboarding or help messages that are always safe).`}>
              <div className="flex items-center gap-2" data-testid="offline-ok">
                <Toggle checked={deliverySettings.offlineOk} onChange={(v) => setDeliverySettings({ ...deliverySettings, offlineOk: v })} />
                <span className="text-xs" style={{ color: "#4A5568" }}>{deliverySettings.offlineOk ? `Shown even after ${STALE_DAYS} days without a sync` : `Hidden after ${STALE_DAYS} days without a sync`}</span>
              </div>
            </PanelSection>
          </CollapsibleSection>
        </div>
      )}

      {propTab === "goal" && (
        <div>
          <PanelSection title="Conversion event">
            <SelectField value={goalSettings.event} onChange={(v) => setGoalSettings({ ...goalSettings, event: v })}
              options={[["", "— Select event —"], ...goalEventOptions(conversionEvents, [], goalSettings.event).map((o) => [o.value, o.label] as [string, string])]} />
            {conversionEvents.length === 0 && <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>No conversion events found — define them in the Conversion Events module.</p>}
          </PanelSection>
          <PanelSection title="Event property conditions (optional)">
            {(() => {
              const goalEvent = goalSettings.event || "";
              return <ConditionBuilder conditions={goalSettings.conditions || []} onChange={(c) => setGoalSettings({ ...goalSettings, conditions: c })}
                propertyOptions={goalEvent ? (eventAttrs[goalEvent] || []) : []} propertyLoading={eventAttrsLoading} />;
            })()}
          </PanelSection>
          <PanelSection title="Attribution window">
            <div className="flex gap-2">
              <input type="number" min={1} className="flex-1 text-sm border rounded-lg p-2.5" style={{ borderColor: "#E2E8F0" }}
                value={goalSettings.window} onChange={(e) => setGoalSettings({ ...goalSettings, window: Number(e.target.value) })} />
              <select className="text-sm border rounded-lg p-2" style={{ borderColor: "#E2E8F0" }}
                value={goalSettings.windowUnit} onChange={(e) => setGoalSettings({ ...goalSettings, windowUnit: e.target.value as "hours" | "days" })}><option value="hours">Hours</option><option value="days">Days</option></select>
            </div>
            <p className="text-xs mt-1" style={{ color: "#A0AEC0" }}>After the impression — recorded when the message actually becomes visible.</p>
          </PanelSection>
          <PanelSection title="Stop showing after conversion"><Toggle checked={goalSettings.stopAfterConversion} onChange={(v) => setGoalSettings({ ...goalSettings, stopAfterConversion: v })} /></PanelSection>
          <PanelSection title="Block interactions to record"><Toggle checked={goalSettings.trackBlockInteractions} onChange={(v) => setGoalSettings({ ...goalSettings, trackBlockInteractions: v })} /></PanelSection>
        </div>
      )}
    </div>
  );
}

// ─── Template Thumbnails ──────────────────────────────────────────────────────

function TemplateThumbnail({ templateKey }: { templateKey: string }) {
  switch (templateKey) {
    case "welcome":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-16 flex items-center justify-center text-2xl"
            style={{ background: "linear-gradient(135deg, var(--brand-primary), #5A67D8)" }}>👋</div>
          <div className="absolute top-16 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-1.5 mx-auto" style={{ backgroundColor: "#2D3748", width: "80%" }} />
            <div className="h-1.5 rounded-full mb-1 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "75%" }} />
            <div className="h-1.5 rounded-full mb-3 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "65%" }} />
            <div className="h-6 rounded-lg mx-1" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-1.5 rounded-full mt-2 mx-auto" style={{ backgroundColor: "#E2E8F0", width: "33%" }} />
          </div>
        </div>
      );
    case "announcement":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-12 flex items-center justify-center text-xl"
            style={{ background: "linear-gradient(135deg, #38A169, #319795)" }}>⚡</div>
          <div className="absolute top-12 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-1.5" style={{ backgroundColor: "#2D3748", width: "80%" }} />
            <div className="h-1.5 rounded-full mb-1" style={{ backgroundColor: "#E2E8F0", width: "75%" }} />
            <div className="h-1.5 rounded-full mb-3" style={{ backgroundColor: "#E2E8F0", width: "65%" }} />
            <div className="flex gap-2">
              <div className="flex-1 h-6 rounded-lg" style={{ backgroundColor: "#38A169" }} />
              <div className="flex-1 h-6 rounded-lg border-2" style={{ borderColor: "#38A169" }} />
            </div>
          </div>
        </div>
      );
    case "promo":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white">
          <div className="absolute top-0 left-0 right-0 h-20 flex items-center justify-center text-3xl"
            style={{ background: "linear-gradient(135deg, #D69E2E, #DD6B20)" }}>💎</div>
          <div className="absolute top-20 left-0 right-0 px-3 pt-2">
            <div className="h-2.5 rounded-full mb-3 mx-auto" style={{ backgroundColor: "#2D3748", width: "75%" }} />
            <div className="flex gap-1.5 justify-center mb-3">
              {["02", "14", "37"].map((n) => (
                <div key={n} className="w-8 h-6 rounded flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: "#DD6B20", fontSize: 10 }}>{n}</div>
              ))}
            </div>
            <div className="h-5 rounded-lg" style={{ backgroundColor: "var(--brand-primary)" }} />
          </div>
        </div>
      );
    case "update":
      return (
        <div className="w-full h-full relative overflow-hidden bg-white p-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-lg">🔄</div>
            <div>
              <div className="h-2 rounded-full w-24 mb-1" style={{ backgroundColor: "#2D3748" }} />
              <div className="h-1.5 rounded-full w-20" style={{ backgroundColor: "#E2E8F0" }} />
            </div>
          </div>
          <div className="space-y-1 mb-3">
            {[0, 1].map((r) => <div key={r} className="h-1.5 rounded-full" style={{ backgroundColor: "#F7FAFC", width: r === 0 ? "90%" : "60%" }} />)}
          </div>
          <div className="h-5 rounded-lg" style={{ backgroundColor: "#10B981" }} />
        </div>
      );
    default:
      return <div className="w-full h-full" style={{ backgroundColor: "#F7FAFC" }} />;
  }
}

// ─── Template Picker Modal ────────────────────────────────────────────────────

function TemplatePickerModal({ onSelect, onClose }: { onSelect: (key: string) => void; onClose: () => void }) {
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
      <div className="rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ backgroundColor: "#FFFFFF", width: 680, maxHeight: "90vh" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "#E2E8F0" }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: "#1A202C" }}>Choose a template</h2>
            <p className="text-sm mt-0.5" style={{ color: "#718096" }}>Start with a pre-built layout and customize</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
            <X size={18} style={{ color: "#718096" }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(TEMPLATES).map(([key, template]) => (
              <div key={key}
                className="relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all hover:shadow-lg"
                style={{ borderColor: hovered === key ? "var(--brand-primary)" : "#E2E8F0" }}
                onMouseEnter={() => setHovered(key)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelect(key)}>
                <div className="h-40 relative overflow-hidden" style={{ backgroundColor: "#F7FAFC" }}>
                  <TemplateThumbnail templateKey={key} />
                  {hovered === key && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "rgba(49,130,206,0.15)" }}>
                      <span className="px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-lg"
                        style={{ backgroundColor: "var(--brand-primary)" }}>
                        Use template
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold" style={{ color: "#1A202C" }}>{template.name}</p>
                  <p className="text-xs mt-0.5" style={{ color: "#718096" }}>{template.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: "#E2E8F0" }}>
          <button className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: "#4A5568" }}
            onClick={() => onSelect("blank")}>
            Start blank →
          </button>
          <button className="text-sm transition-colors hover:opacity-70" style={{ color: "#718096" }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

export function PreviewModal({
  device, setDevice, blocks, pages, startPage = 0, messageType, messageStyle, onClose, previewMode = "light", brand = {},
}: {
  device: Device;
  setDevice: (d: Device) => void;
  blocks: Block[];
  pages?: { id: string; name?: string; blocks: Block[] }[];
  startPage?: number;
  messageType: MessageType;
  messageStyle: MessageStyle;
  onClose: () => void;
  previewMode?: "light" | "dark";   // P14 (DA-BUG-1389): resolve "auto" appearance
  brand?: Brand;                    // P14: tenant brand colours for the dark resolution
}) {
  const DEVICES: Device[] = ["ios", "android"];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // P10 (DA-BUG-1386): the device frame is 720px tall and was scaled a fixed 1.15× (~828px), so on a
  // short window the status bar and home indicator clipped off. Scale to fit the viewport instead
  // (never above 1.15×), leaving room for the platform toggle + page arrows (~180px of chrome).
  const [frameScale, setFrameScale] = useState(1.15);
  useEffect(() => {
    const fit = () => setFrameScale(Math.max(0.5, Math.min(1.15, (window.innerHeight - 180) / 720)));
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  // Preview 20 (R2-27): Preview walks the message's pages as the phone does; the side arrows and
  // page dots move between pages, the iOS / Android toggle only switches the platform.
  const allPages = pages && pages.length ? pages : [{ id: "p", blocks }];
  const first = Math.min(Math.max(startPage, 0), allPages.length - 1);
  const [pageIdx, setPageIdx] = useState(first);
  const [closed, setClosed] = useState(false);
  const [run, setRun] = useState(0);   // remounts the message on Replay (timers, picks)
  const pageBlocks = allPages[Math.min(pageIdx, allPages.length - 1)].blocks;
  const replay = () => { setPageIdx(first); setClosed(false); setRun((r) => r + 1); };
  // Buttons act as on the phone (iOS perform / Android runAction): nextPage advances (closes on the
  // last page), previousPage goes back (no-op on the first), dismiss / open URL / deep link / navigate
  // close the message; emit event, copy coupon and none keep it open.
  const act = useCallback((a?: BlockAction) => {
    const t = a?.type;
    if (t === "nextPage") setPageIdx((i) => { if (i + 1 < allPages.length) return i + 1; setClosed(true); return i; });
    else if (t === "previousPage") setPageIdx((i) => Math.max(0, i - 1));
    else if (t === "dismiss" || t === "openUrl" || t === "deepLink" || t === "navigate") setClosed(true);
  }, [allPages.length]);

  // Preview 1 (R2-1 / F2): merge tags resolve as on the phone, with sample values the author picks.
  const [sampleVars, setSampleVars] = useState<Record<string, string>>({});
  const attrKeys: string[] = [];
  const collect = (bs: Block[]) => bs.forEach((b) => {
    const k = b.type === "countdown" && b.content.countdownMode === "attribute" ? (b.content.attribute || "").trim() : "";
    if (k && !attrKeys.includes(k)) attrKeys.push(k);
    if (b.content.children) collect(b.content.children);
  });
  allPages.forEach((pg) => collect(pg.blocks));
  const sampleKeys = [...DEFAULT_VARIABLES, ...attrKeys].filter((k, i, all) => !IDENTIFYING_KEYS.includes(k) && all.indexOf(k) === i);
  const shownBlocks = resolvePreviewBlocks(pageBlocks, sampleVars);
  // P14 (DA-BUG-1389): apply the message's OWN dark palette in Preview (not just the host chrome),
  // mirroring the editor canvas — so a dark-appearance message shows its backgroundDark/colorDark
  // instead of a white card with dark text.
  const appearance = effectiveAppearance(messageStyle.appearance, allPages.map((p) => ({ blocks: p.blocks, style: messageStyle })));
  const previewDark = appearance === "dark" || (appearance === "auto" && previewMode === "dark");
  const previewSurface = previewDark ? darkPreviewSurface(messageStyle, brand) : null;
  const previewStyle = previewSurface ? { ...messageStyle, background: cssOf(previewSurface) } : messageStyle;
  const darkBlocks = previewSurface ? darkPreviewBlocks(shownBlocks, previewSurface, brand) : shownBlocks;

  const overlay = closed ? (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
      <p className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: "rgba(0,0,0,0.65)", color: "#FFFFFF" }}>Message closed</p>
      <button type="button" onClick={replay} className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: "#FFFFFF", color: "#1A202C", boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }}>Replay</button>
    </div>
  ) : (
    <MessageOverlay
      key={`${run}-${pageIdx}`}
      messageType={messageType}
      messageStyle={previewStyle}
      blocks={darkBlocks}
      selectedBlockId={null}
      onSelectBlock={() => {}}
      onUpdateBlock={() => {}}
      onDeleteBlock={() => {}}
      onDuplicateBlock={() => {}}
      onDropInto={() => {}}
      dragOverIndex={null}
    />
  );
  const content = <PreviewRunCtx.Provider value={{ vars: sampleVars, act }}><PreviewTheme.Provider value={previewDark ? DARK_PREVIEW : null}><div className="relative w-full h-full"><SampleAppBackground />{overlay}</div></PreviewTheme.Provider></PreviewRunCtx.Provider>;

  const frame = device === "android"
    ? <AndroidFrame>{content}</AndroidFrame>
    : <IPhoneFrame>{content}</IPhoneFrame>;
  const n = allPages.length;

  // V3-P16 (DA-BUG-1421): justify-center clipped the platform toggle above the viewport at short window
  // heights (flex overflow); scroll + top gutter keep the iOS/Android switch visible and clickable.
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-start overflow-y-auto py-16" style={{ backgroundColor: "rgba(15,15,15,0.95)" }}>
      <button onClick={onClose} className="absolute top-6 left-6 flex items-center gap-2 text-white hover:opacity-70 transition-opacity">
        <ChevronLeftIcon size={18} /> <span className="text-sm">Exit preview</span>
      </button>
      {/* Preview 25 (F22): the scaled frame (720 px × 1.15) grows 54 px above its box and covered the
          toggle, so clicks hit the frame (the top banner); the toggle stacks above it and clears it. */}
      <div data-testid="platform-toggle" className="flex items-center gap-3" style={{ position: "relative", zIndex: 20, marginBottom: 72 }}>
        {DEVICES.map((d) => (
          <button key={d} className="px-4 py-2 rounded-full text-sm font-medium transition-all"
            style={{ backgroundColor: device === d ? "#FFFFFF" : "rgba(255,255,255,0.1)", color: device === d ? "#1A202C" : "#FFFFFF" }}
            onClick={() => setDevice(d)}>
            {d === "ios" ? "iOS" : "Android"}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-8">
        <button aria-label="Previous page" className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors disabled:opacity-30"
          disabled={closed || pageIdx === 0} onClick={() => setPageIdx((i) => Math.max(0, i - 1))}>
          <ChevronLeftIcon size={20} />
        </button>
        <div data-testid="preview-frame" style={{ transform: `scale(${frameScale})`, transformOrigin: "center", position: "relative", zIndex: 0 }}>
          {frame}
        </div>
        <button aria-label="Next page" className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors disabled:opacity-30"
          disabled={closed || pageIdx >= n - 1} onClick={() => setPageIdx((i) => Math.min(n - 1, i + 1))}>
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="absolute right-6 top-20 w-56 rounded-xl p-3" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
        <p className="text-xs font-semibold text-white mb-1">Sample values</p>
        <p className="text-[11px] mb-2" style={{ color: "rgba(255,255,255,0.6)" }}>Empty shows the tag's fallback, as on a phone.</p>
        {sampleKeys.map((k) => (
          <label key={k} className="flex items-center gap-2 mb-1.5">
            <span className="text-[11px] w-24 truncate" style={{ color: "rgba(255,255,255,0.75)" }}>{k}</span>
            <input aria-label={`Sample value ${k}`} value={sampleVars[k] || ""} onChange={(e) => setSampleVars((v) => ({ ...v, [k]: e.target.value }))}
              className="flex-1 min-w-0 rounded px-1.5 py-0.5 text-xs" style={{ backgroundColor: "rgba(255,255,255,0.9)", color: "#1A202C" }} />
          </label>
        ))}
        <p className="text-[11px] mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>{IDENTIFYING_KEYS.join(", ")} are never filled by the server: they show their fallback, or the tag.</p>
      </div>
      <div className="flex flex-col items-center gap-2" style={{ position: "relative", zIndex: 20, marginTop: 72 }}>
        <div className="flex gap-2">
          {allPages.map((pg, i) => (
            <button key={pg.id || i} aria-label={`Go to page ${i + 1}`} className="rounded-full transition-all"
              style={{ width: pageIdx === i ? 20 : 6, height: 6, backgroundColor: pageIdx === i ? "#FFFFFF" : "rgba(255,255,255,0.3)" }}
              onClick={() => { setClosed(false); setPageIdx(i); }} />
          ))}
        </div>
        <span className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>Page {pageIdx + 1} of {n}</span>
      </div>
    </div>
  );
}

// ─── Block Icons & Labels ─────────────────────────────────────────────────────

function blockIcon(type: BlockType, size = 14) {
  const props = { size, className: "flex-shrink-0" };
  switch (type) {
    case "heading": return <Heading {...props} />;
    case "text": return <Type {...props} />;
    case "image": return <ImageIcon {...props} />;
    case "button": return <Square {...props} />;
    case "divider": return <Minus {...props} />;
    case "spacer": return <AlignLeft {...props} />;
    case "icon": return <Sparkles {...props} />;
    case "feature-list": return <ListChecks {...props} />;
    case "container": return <BoxIcon {...props} />;
    case "row": return <RowsIcon {...props} />;
    case "columns": return <ColumnsIcon {...props} />;
    case "countdown": return <Timer {...props} />;
    case "coupon": return <Tag {...props} />;
    case "product": return <ShoppingBag {...props} />;
    case "rating": return <Star {...props} />;
    case "nps": return <BarChart3 {...props} />;
    case "choice": return <CheckSquare {...props} />;
    case "input": return <TextCursorInput {...props} />;
    case "consent": return <CheckSquare {...props} />;
    case "progress": return <BarChart3 {...props} />;
    case "carousel": return <Layers {...props} />;
    case "video": return <PlayCircle {...props} />;
    case "form": return <ListChecks {...props} />;
    default: return <Square {...props} />;
  }
}

function blockLabel(block: Block): string {
  const c = block.content;
  if (c.text) return c.text.slice(0, 22);
  if (block.type === "image") return c.imageAlt ? c.imageAlt : "Image";
  return block.type.charAt(0).toUpperCase() + block.type.slice(1);
}

// ─── Palette Groups ───────────────────────────────────────────────────────────

const PALETTE_GROUPS: { label: string; items: { type: BlockType; label: string }[] }[] = [
  {
    label: "Basic",
    items: [
      { type: "heading", label: "Heading" },
      { type: "text", label: "Text" },
      { type: "image", label: "Image" },
      { type: "button", label: "Button" },
      { type: "icon", label: "Icon / Badge" },
      { type: "feature-list", label: "Feature List" },
    ],
  },
  {
    label: "Layout",
    items: [
      { type: "container", label: "Container" },
      { type: "row", label: "Row" },
      { type: "columns", label: "Columns" },
      { type: "divider", label: "Divider" },
      { type: "spacer", label: "Spacer" },
    ],
  },
  {
    label: "Offers",
    items: [
      { type: "countdown", label: "Countdown" },
      { type: "coupon", label: "Coupon Code" },
      { type: "product", label: "Product / Offer Card" },
    ],
  },
  {
    label: "Interactive",
    items: [
      { type: "carousel", label: "Carousel" },
      { type: "rating", label: "Rating / Feedback" },
      { type: "choice", label: "Single / Multi Choice" },
      { type: "input", label: "Input Field" },
      { type: "consent", label: "Consent Checkbox" },
      { type: "progress", label: "Progress Bar" },
      { type: "video", label: "Video" },
      { type: "form", label: "Form (grouped fields)" },
    ],
  },
];

// ─── Status Badge ─────────────────────────────────────────────────────────────

type MessageStatus = "draft" | "active" | "paused" | "archived";

const STATUS_BADGE: Record<MessageStatus, { label: string; bg: string; fg: string }> = {
  draft: { label: "Draft", bg: "var(--brand-primary)", fg: "#FFFFFF" },
  active: { label: "Active", bg: "#C6F6D5", fg: "#22543D" },
  paused: { label: "Paused", bg: "#FEEBC8", fg: "#7B341E" },
  archived: { label: "Archived", bg: "#E2E8F0", fg: "#4A5568" },
};

// D-1 — the saved style carries `appearance` explicitly once any dark value exists (Auto unless the
// author chose); with no dark value and no choice it is left out (devices stay light, as today).
function styleForSave(style: MessageStyle, pages: { blocks: Block[] }[]): MessageStyle {
  const withStyle = pages.map((p) => ({ blocks: p.blocks, style }));
  const { appearance, ...rest } = style;
  if (appearance) return { ...rest, appearance };
  return hasDarkValues(withStyle) ? { ...rest, appearance: "auto" } : rest;
}

const LIVE_SAVE_CONFIRM =
  "This message is live. Saving updates it on customers' phones at their next sync — there is no separate Republish step. Save and update the live message now?";

// ─── Issues modal (P-4 / F29 / F5, DA-BUG-1091) ───────────────────────────────
// Every issue with its severity, page and block. Clicking one closes the modal and takes the
// author to it (page, block selected + flashed, field focused). Publish opens the same modal.
function IssuesModal({ issues, pages, onPick, onClose }: {
  issues: Issue[]; pages: { name: string; blocks: Block[] }[];
  onPick: (i: Issue) => void; onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const where = (i: Issue) => {
    if (i.pageIndex == null) return "Message settings";
    const page = pages[i.pageIndex];
    const blk = i.blockId ? treeFind(page?.blocks || [], i.blockId) : null;
    return `${pages.length > 1 || i.pageIndex > 0 ? `Page ${i.pageIndex + 1}` : "Page 1"}${blk ? ` · ${blockLabel(blk)}` : ""}`;
  };
  const blockers = issues.filter((i) => i.severity === "blocker").length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.45)" }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Issues to fix before publishing"
        className="rounded-2xl shadow-2xl flex flex-col" style={{ background: "#FFFFFF", width: 520, maxHeight: "75vh" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "#E2E8F0" }}>
          <p className="text-sm font-semibold" style={{ color: "#1A202C" }}>
            {issues.length} {issues.length === 1 ? "issue" : "issues"}{blockers ? ` · ${blockers} block${blockers === 1 ? "s" : ""} publishing` : ""}
          </p>
          <button type="button" aria-label="Close issues" onClick={onClose} style={{ color: "#A0AEC0" }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-1.5">
          {issues.map((i, k) => (
            <button key={`${k}-${i.message}`} type="button" onClick={() => onPick(i)}
              className="w-full text-left rounded-lg border px-3 py-2 hover:bg-gray-50" style={{ borderColor: "#E2E8F0" }}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                  style={i.severity === "blocker" ? { background: "#FED7D7", color: "#C53030" } : { background: "#FEFCBF", color: "#975A16" }}>
                  {i.severity === "blocker" ? "Blocks publish" : "Warning"}
                </span>
                <span className="text-xs" style={{ color: "#718096" }}>{where(i)}</span>
              </div>
              <span className="text-sm" style={{ color: "#2D3748" }}>{i.message}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function InAppDesigner({ onBack }: { onBack?: () => void }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  // F16 — under <PermissionRoute> the group's permission list is already loaded; reuse it
  // (PERM-163) instead of a second, serial round trip before the editor can render.
  const routePerms = usePermissionRouteContext();
  const fromRoute = routePerms && routePerms.group === "tools/in-app-messages" && !routePerms.loading ? routePerms.permissions : null;
  const [featurePermissions, setFeaturePermissions] = useState<any[]>(fromRoute || []);
  const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(!fromRoute);
  // Confirmed via a live backend response: this feature is grouped under the
  // list page's "tools/in-app-messages" slug (returns in-app-message-list +
  // in-app-designer), not this editor's own route (which includes the message id).
  const permParams = "tools/in-app-messages";

  const fetchPermissions = async () => {
    setFeaturePermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setFeaturePermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setFeaturePermissions([]);
    } finally {
      setFeaturePermissionsLoading(false);
    }
  };
  useEffect(() => {
    if (fromRoute) return;
    fetchPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "in-app-designer" covers create/edit for both new and existing messages.
  // Publish is its own slug ("in-app-message-publish", same as InAppMessageList.jsx)
  // since a user can be allowed to edit a message without being allowed to make it
  // live. When canSaveInAppMessage is false, the canvas stays visible (view-only)
  // and only the Save control is hidden, rather than blocking the whole page.
  const canSaveInAppMessage = hasPermission("in-app-designer", featurePermissions);
  const canPublishInAppMessage = hasPermission("in-app-message-publish", featurePermissions);

  const [messageId, setMessageId] = useState<string | null>(routeId ?? null);
  const [status, setStatus] = useState<MessageStatus>("draft");
  const [loading, setLoading] = useState<boolean>(!!routeId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showIssues, setShowIssues] = useState(false);
  const [draftRevisions, setDraftRevisions] = useState(false); // backend supports P-2 draft revisions
  const [hasDraft, setHasDraft] = useState(false);             // Active message has unpublished edits
  // P-8: the tenant's allowed deep-link schemes (In-App settings); [] = any custom scheme.
  const [deepLinkSchemes, setDeepLinkSchemes] = useState<string[]>([]);
  // DA-BUG-1054: tenant brand colours (In-App settings) for the dark preview's brand fallback.
  const [brand, setBrand] = useState<Brand>({});
  // Preview "device setting": the message's appearance decides; Auto follows this toggle.
  const [previewMode, setPreviewMode] = useState<"light" | "dark">("light");
  useEffect(() => {
    let off = false;
    axios.get(`${process.env.REACT_APP_API_URL}/in-app-settings`, { headers: getApiHeaders() })
      .then(({ data }) => {
        if (off) return;
        if (Array.isArray(data?.data?.deep_link_schemes)) setDeepLinkSchemes(data.data.deep_link_schemes);
        setBrand({ brandColor: data?.data?.brand_color || null, brandColorDark: data?.data?.brand_color_dark || null });
      })
      .catch(() => { /* older backend → any custom scheme */ });
    return () => { off = true; };
  }, []);
  const [toast, setToast] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = (type: "success" | "error", text: string) => {
    setToast({ type, text });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2600);
  };

  // Multi-page: a message is a sequence of pages, each with its own blocks. `blocks`
  // is the CURRENT page's blocks (derived), so every block operation stays unchanged;
  // the surface `messageStyle` is message-level (the backend uses pages[0].style).
  // F1 — a new draft starts blank and Untitled (the template picker opens); it never starts from
  // another message's name or copy. An existing message is loaded below.
  const [pages, setPages] = useState<{ id: string; name: string; blocks: Block[] }[]>(() => [
    { id: uid(), name: "Page 1", blocks: [] },
  ]);
  const [currentPage, setCurrentPage] = useState(0);
  const currentPageRef = useRef(0);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  const blocks = pages[currentPage]?.blocks ?? [];
  const setBlocks = useCallback((next: Block[] | ((b: Block[]) => Block[])) => {
    setPages(ps => ps.map((p, i) => i === currentPageRef.current
      ? { ...p, blocks: typeof next === "function" ? (next as (b: Block[]) => Block[])(p.blocks) : next }
      : p));
  }, []);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [leftTab, setLeftTab] = useState<"blocks" | "layers">("blocks");
  const [messageType, setMessageType] = useState<MessageType>("modal");
  const [device, setDevice] = useState<Device>("ios");
  const [messageStyle, setMessageStyle] = useState<MessageStyle>(
    coerceStyleForSurface(
      { background: "#FFFFFF", width: 90, cornerRadius: 16, backdropOpacity: 40, animation: "fade-scale", position: "center",
        closeButtonPosition: "top-right", closeButton: true, tapBackdrop: true, swipeToDismiss: false, autoDismissSecs: 0,
        appearance: "auto" as Appearance }, // DA-BUG-1421 (PB-6): new messages follow the device so authored dark colours apply
      "modal",
    )
  );
  const [triggerSettings, setTriggerSettings] = useState<TriggerSettings>({ event: "", delay: 0, delayUnit: "seconds", screens: [], requireSameScreen: false });
  const [audienceSettings, setAudienceSettings] = useState<AudienceSettings>({ type: "", cohortIds: [], cohortNames: [] });
  const [scheduleSettings, setScheduleSettings] = useState<ScheduleSettings>({
    startMode: "immediate", startDate: "", endEnabled: false, endDate: "",
    activeDays: [0, 1, 2, 3, 4, 5, 6], timeWindowEnabled: false, timeStart: "09:00", timeEnd: "21:00",
    timezone: "customer_local", fallbackTimezone: "UTC",
  });
  const [frequencySettings, setFrequencySettings] = useState<FrequencySettings>({
    oncePerSession: true, maxPerDayEnabled: false, maxPerDay: 1, maxLifetimeEnabled: false, maxLifetime: 3,
    cooldownEnabled: false, cooldownValue: 24, cooldownUnit: "hours", stopAfterConversion: true,
  });
  const [goalSettings, setGoalSettings] = useState<GoalSettings>({ event: "", conditions: [], window: 7, windowUnit: "days", stopAfterConversion: true, trackBlockInteractions: false });
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings>(deliveryFromApi({}));
  const [messageName, setMessageName] = useState("Untitled message");
  const [editingName, setEditingName] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(!routeId);
  const [showPreview, setShowPreview] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [propTab, setPropTab] = useState<PropTab>("style");

  const draggedTypeRef = useRef<BlockType | null>(null);
  const draggedBlockIdRef = useRef<string | null>(null);

  // History
  const [history, setHistory] = useState<Block[][]>([blocks]);
  const [historyIdx, setHistoryIdx] = useState(0);

  // Load an existing message when the route carries an :id
  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
        const { data: res } = await axios.get(`${API_URL}/${routeId}`, { headers: getHeaders() });
        // F16: client timing log for the slow-open investigation (no message content logged).
        console.info("[in-app designer] load timings", { messageMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0) });
        const live = res?.data;
        if (!live) throw new Error("not found");
        // P-2 (DA-BUG-1133): GET returns the LIVE doc; unpublished edits of an Active message come in
        // draft_revision. The editor works on the draft (overlaid on the live doc).
        const dr = live.has_unpublished_changes === true && live.draft_revision && typeof live.draft_revision === "object" ? live.draft_revision : null;
        const msg = dr ? { ...live, ...Object.fromEntries(Object.entries(dr).filter(([k]) => k !== "saved_at")) } : live;
        if (cancelled) return;

        setMessageId(msg.id ?? routeId);
        setMessageName(msg.name ?? "");
        setMessageType(msg.message_type ?? "modal");

        // Backend persists the design as multi-page { pages: [{ id,name,blocks,style }] };
        // load every page (fall back to the legacy single-page { blocks, style } shape).
        const loadedPages: { id: string; name: string; blocks: Block[] }[] =
          Array.isArray(msg.design?.pages) && msg.design.pages.length
            ? msg.design.pages.map((p: any, i: number) => ({ id: p.id || uid(), name: p.name || `Page ${i + 1}`, blocks: Array.isArray(p.blocks) ? editorBlocksFromApi(p.blocks, uid) as Block[] : [] }))
            : [{ id: uid(), name: "Page 1", blocks: Array.isArray(msg.design?.blocks) ? editorBlocksFromApi(msg.design.blocks, uid) as Block[] : [] }];
        const page0 = msg.design?.pages?.[0];
        setPages(loadedPages);
        setCurrentPage(0);
        currentPageRef.current = 0;
        setHistory([loadedPages[0].blocks]);
        setHistoryIdx(0);

        const loadedType: MessageType = msg.message_type ?? "modal";
        const st = page0?.style ?? msg.design?.style ?? {};
        setMessageStyle(coerceStyleForSurface({
          background: st.background ?? "#FFFFFF",
          ...(st.backgroundDark ? { backgroundDark: st.backgroundDark } : {}),
          ...(["auto", "light", "dark"].includes(st.appearance) ? { appearance: st.appearance } : {}),
          ...(st.backgroundImage && typeof st.backgroundImage.url === "string" ? { backgroundImage: { url: st.backgroundImage.url, fit: (st.backgroundImage.fit === "contain" ? "contain" : "cover") as "cover" | "contain" } } : {}),
          ...(typeof st.scrimOpacity === "number" ? { scrimOpacity: st.scrimOpacity } : {}),
          ...(typeof st.scrimColor === "string" ? { scrimColor: st.scrimColor } : {}),
          ...(typeof st.scrimColorDark === "string" ? { scrimColorDark: st.scrimColorDark } : {}),
          ...(st.imageHeroTop === true ? { imageHeroTop: true } : {}), // DA-BUG-1401
          width: st.width ?? 90,
          cornerRadius: st.cornerRadius ?? 16,
          backdropOpacity: st.backdropOpacity ?? 40,
          animation: st.animation ?? "fade-scale",
          position: st.position ?? "center",
          closeButtonPosition: st.closeButtonPosition ?? st.close_button_position ?? "top-right",
          closeButton: st.closeButton ?? st.close_button ?? true,
          tapBackdrop: st.tapBackdrop ?? st.tap_backdrop ?? false,
          swipeToDismiss: st.swipeToDismiss ?? st.swipe_to_dismiss ?? false,
          autoDismissSecs: st.autoDismissSecs ?? st.auto_dismiss_secs ?? 0,
        }, loadedType));
        const tr = msg.trigger || {};
        const tc = tr.conditions || {};
        setTriggerSettings({
          event: tr.event ?? "app_open",
          delay: tr.delay ?? tr.display_delay_seconds ?? 0,
          delayUnit: tr.delay_unit ?? "seconds",
          screens: toScreenRefs(tr.screens),
          requireSameScreen: tr.require_same_screen ?? false,
          screenName: tc.screen_name ?? "",
          screenPlatform: PLATFORMS.includes(tc.screen_platform) ? tc.screen_platform : "both",
          activeDuration: tc.active_duration ?? 30,
          activeDurationUnit: tc.active_duration_unit === "minutes" ? "minutes" : "seconds",
          resetTimerOnLeave: tc.reset_timer_on_leave ?? true,
          customEvent: tr.custom_event_name ?? "",
          occurrenceOp: tc.occurrence_op === "exactly" ? "exactly" : "at_least",
          occurrenceCount: tc.occurrence_count ?? 1,
          eventConditions: Array.isArray(tr.event_conditions) ? tr.event_conditions : [],
          sessionThreshold: tc.session_threshold ?? 60,
          sessionThresholdUnit: tc.session_threshold_unit === "minutes" ? "minutes" : "seconds",
          requestKey: tr.request_key ?? "",
          contextConditions: Array.isArray(tr.context_conditions) ? tr.context_conditions : [],
          excludedScreens: toScreenRefs(tr.screen_restrictions?.excluded_screens),
        });
        const aud = msg.audience || {};
        const audType: AudienceSettings["type"] = aud.type === "segments" || aud.type === "cohorts" || aud.type === "cohort" ? "segments" : aud.type === "tags" ? "tags" : aud.type === "all" ? "all" : "";
        const audIds: string[] = Array.isArray(aud.cohort_ids) ? aud.cohort_ids : (aud.cohort_id ? [aud.cohort_id] : []);
        setAudienceSettings({
          type: audType, cohortIds: audIds,
          cohortNames: Array.isArray(aud.cohort_names) ? aud.cohort_names : (aud.cohort_name ? [aud.cohort_name] : []),
          tagIds: Array.isArray(aud.tag_ids) ? aud.tag_ids : [],
          tagNames: Array.isArray(aud.tag_names) ? aud.tag_names : [],
          estimatedReach: aud.estimated_reach ?? undefined,
        });
        const sch = msg.schedule || {};
        setScheduleSettings({
          startMode: sch.start_mode === "scheduled" ? "scheduled" : "immediate",
          startDate: sch.start_date ?? "",
          endEnabled: sch.end_enabled ?? false,
          endDate: sch.end_date ?? "",
          activeDays: Array.isArray(sch.active_days) ? sch.active_days : [0, 1, 2, 3, 4, 5, 6],
          timeWindowEnabled: sch.time_window?.enabled ?? false,
          timeStart: sch.time_window?.start ?? "09:00",
          timeEnd: sch.time_window?.end ?? "21:00",
          timezone: sch.timezone ?? "customer_local",
          fallbackTimezone: sch.fallback_timezone ?? "UTC",
        });
        const fr = msg.frequency || {};
        setFrequencySettings(frequencyFromApi(fr));
        const gl = msg.goal || {};
        setGoalSettings({
          event: gl.event ?? "",
          conditions: Array.isArray(gl.conditions) ? gl.conditions : [],
          window: gl.attribution_window?.value ?? gl.window ?? 7,
          windowUnit: (gl.attribution_window?.unit ?? gl.window_unit) === "hours" ? "hours" : "days",
          stopAfterConversion: gl.stop_after_conversion ?? true,
          trackBlockInteractions: gl.track_block_interactions ?? false,
        });
        setDeliverySettings(deliveryFromApi(msg.delivery));
        setStatus(msg.status ?? "draft");
        // P-2 (DA-BUG-1133): a backend that keeps edits of an Active message as a draft revision
        // says so with `has_unpublished_changes`; an older one doesn't send the key (Save updates live).
        setDraftRevisions(Object.prototype.hasOwnProperty.call(msg, "has_unpublished_changes"));
        setHasDraft(msg.has_unpublished_changes === true);
      } catch {
        if (!cancelled) setLoadError("Couldn't load this message.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const buildPayload = () => ({
    name: messageName,
    message_type: messageType,
    design: { pages: pages.map(p => ({ id: p.id, name: p.name, blocks: apiBlocksFromEditor(p.blocks as any) as Block[], style: styleForSave(messageStyle, pages) })) },
    trigger: {
      event: triggerSettings.event,
      delay: triggerSettings.delay,
      delay_unit: triggerSettings.delayUnit,
      display_delay_seconds: triggerSettings.delayUnit === "minutes" ? triggerSettings.delay * 60 : triggerSettings.delay,
      screens: triggerSettings.event === "screen_view" ? (triggerSettings.screens ?? []) : [],
      require_same_screen: triggerSettings.requireSameScreen ?? false,
      custom_event_name: triggerSettings.event === "custom_event" ? (triggerSettings.customEvent ?? "") : undefined,
      event_conditions: triggerSettings.event === "custom_event" ? (triggerSettings.eventConditions ?? []) : [],
      request_key: triggerSettings.event === "app_request" ? (triggerSettings.requestKey ?? "") : undefined,
      context_conditions: triggerSettings.event === "app_request" ? (triggerSettings.contextConditions ?? []) : [],
      conditions: {
        ...(triggerSettings.event === "screen_time" ? { screen_name: triggerSettings.screenName, screen_platform: triggerSettings.screenPlatform, active_duration: triggerSettings.activeDuration, active_duration_unit: triggerSettings.activeDurationUnit, reset_timer_on_leave: triggerSettings.resetTimerOnLeave } : {}),
        ...(triggerSettings.event === "custom_event" ? { occurrence_op: triggerSettings.occurrenceOp, occurrence_count: triggerSettings.occurrenceCount } : {}),
        ...(triggerSettings.event === "session_time" ? { session_threshold: triggerSettings.sessionThreshold, session_threshold_unit: triggerSettings.sessionThresholdUnit } : {}),
      },
      screen_restrictions: {
        excluded_screens: triggerSettings.excludedScreens ?? [],
      },
    },
    audience: {
      type: audienceSettings.type === "segments" ? "cohorts" : audienceSettings.type,
      cohort_ids: audienceSettings.type === "segments" ? (audienceSettings.cohortIds ?? []) : [],
      cohort_names: audienceSettings.type === "segments" ? (audienceSettings.cohortNames ?? []) : [],
      tag_ids: audienceSettings.type === "tags" ? (audienceSettings.tagIds ?? []) : [],
      tag_names: audienceSettings.type === "tags" ? (audienceSettings.tagNames ?? []) : [],
      estimated_reach: audienceSettings.estimatedReach ?? null,
    },
    schedule: {
      start_mode: scheduleSettings.startMode,
      start_date: scheduleSettings.startMode === "scheduled" ? scheduleSettings.startDate : null,
      end_enabled: scheduleSettings.endEnabled,
      end_date: scheduleSettings.endEnabled ? scheduleSettings.endDate : null,
      active_days: scheduleSettings.activeDays,
      time_window: { enabled: scheduleSettings.timeWindowEnabled, start: scheduleSettings.timeStart, end: scheduleSettings.timeEnd },
      timezone: scheduleSettings.timezone,
      fallback_timezone: scheduleSettings.fallbackTimezone,
    },
    frequency: frequencyToApi(frequencySettings),
    goal: {
      event: goalSettings.event,
      conditions: goalSettings.conditions,
      attribution_window: { value: goalSettings.window, unit: goalSettings.windowUnit },
      stop_after_conversion: goalSettings.stopAfterConversion,
      track_block_interactions: goalSettings.trackBlockInteractions,
    },
    delivery: deliveryToApi(deliverySettings),
  });

  // Create on first save, update thereafter
  const persist = async () => {
    const payload = buildPayload();
    if (messageId) {
      const { data: res } = await axios.put(`${API_URL}/${messageId}`, payload, { headers: getHeaders() });
      return res?.data;
    }
    const { data: res } = await axios.post(API_URL, payload, { headers: getHeaders() });
    const saved = res?.data;
    if (saved?.id) {
      setMessageId(saved.id);
      navigate(`/tools/in-app/${saved.id}`, { replace: true });
    }
    return saved;
  };

  // P-2 — RRS updates the live snapshot when an Active message is saved (no separate Republish
  // step; the backend publish model is unchanged). So a Save of a live message passes the same
  // publish rules and says, before it happens, that phones get the change on their next sync.
  const handleSave = async () => {
    if (saving || publishing) return;
    // P-2: with draft revisions an Active message's Save is stored apart and devices keep the
    // published version until Republish; without them, Save updates live (warned, rules applied).
    if (status === "active" && !draftRevisions) {
      if (validationIssues().length) { setShowIssues(true); return; }
      if (!window.confirm(LIVE_SAVE_CONFIRM)) return;
    }
    setSaving(true);
    try {
      const saved = await persist();
      if (saved?.status) setStatus(saved.status);
      if (saved && Object.prototype.hasOwnProperty.call(saved, "has_unpublished_changes")) { setDraftRevisions(true); setHasDraft(saved.has_unpublished_changes === true); }
      showToast("success", status === "active"
        ? (draftRevisions || saved?.has_unpublished_changes ? "Changes saved — not live until you republish" : "Saved — live on phones at their next sync")
        : "Draft saved");
    } catch {
      showToast("error", "Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  // R-3 — a test link needs a saved message; a new draft is saved first.
  const [showSendTest, setShowSendTest] = useState(false);
  const openSendTest = async () => {
    if (!messageId) {
      try { await persist(); } catch { showToast("error", "Save the message before sending a test"); return; }
    }
    setShowSendTest(true);
  };

  // P-2 — drop the unpublished edits and reload the live version.
  const discardDraft = async () => {
    if (!messageId || !window.confirm("Discard the unpublished changes and go back to the live version?")) return;
    try {
      await axios.post(`${API_URL}/${messageId}/discard-draft`, {}, { headers: getHeaders() });
      setHasDraft(false);
      showToast("success", "Unpublished changes discarded");
      window.location.reload();
    } catch {
      showToast("error", "Couldn't discard — try again");
    }
  };

  const handlePublish = async () => {
    if (saving || publishing) return;
    const blockers = validationIssues();
    if (blockers.length) {
      setShowIssues(true); // P-4: list every blocker; a toast alone vanished without saying what
      return;
    }
    if (targetsAllCustomers(buildPayload()) && !window.confirm(ALL_CUSTOMERS_CONFIRM)) return;
    setPublishing(true);
    try {
      const saved = await persist();
      const id = saved?.id ?? messageId;
      if (!id) throw new Error("missing id");
      const { data: res } = await axios.post(`${API_URL}/${id}/publish`, {}, { headers: getHeaders() });
      setStatus(res?.data?.status ?? "active");
      setHasDraft(false);
      // IMG: RRS imports external images on publish; tell the author about any it could not fetch.
      const failed = res?.data?.image_import?.failed;
      if (Array.isArray(failed) && failed.length) {
        showToast("error", `Published, but ${failed.length} image${failed.length === 1 ? "" : "s"} could not be imported: ${failed.map((f: any) => (typeof f === "string" ? f : f?.url)).filter(Boolean).slice(0, 2).join(", ")}`);
      } else showToast("success", "Message published");
    } catch (e: any) {
      // PB-2 (DA-BUG-1421): surface the server's validation reason (e.g. "Product card needs a URL"),
      // not a generic string, so the author can fix what actually failed.
      const serverMsg = e?.response?.data?.message || (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors.join(", ") : null);
      showToast("error", serverMsg ? `Couldn't publish: ${serverMsg}` : "Couldn't publish — try again");
    } finally {
      setPublishing(false);
    }
  };

  // BUG-13 — accessible reorder without dragging. Moves a block one slot up/down.
  const moveBlock = (idx: number, dir: -1 | 1) => {
    const to = idx + dir;
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[to]] = [next[to], next[idx]];
    updateBlocks(next);
  };

  // BUG-12 — undo/redo preserve the current selection when the block still exists,
  // so the user doesn't get bounced back to Message Settings and lose their place.
  const keepSelection = (nextBlocks: Block[]) =>
    setSelectedBlockId(id => (id && treeFind(nextBlocks, id) ? id : null));

  const undo = () => {
    if (historyIdx > 0) {
      const prev = historyIdx - 1;
      setHistoryIdx(prev);
      setBlocks(history[prev]);
      keepSelection(history[prev]);
    }
  };
  const redo = () => {
    if (historyIdx < history.length - 1) {
      const next = historyIdx + 1;
      setHistoryIdx(next);
      setBlocks(history[next]);
      keepSelection(history[next]);
    }
  };

  const updateBlocks = useCallback((newBlocks: Block[]) => {
    setBlocks(newBlocks);
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newBlocks]);
    setHistoryIdx(prev => prev + 1);
  }, [historyIdx, setBlocks]);

  // ─── Pages (multi-page) ───────────────────────────────────────────────────────
  const switchPage = (i: number, pageList = pages) => {
    const target = Math.max(0, Math.min(i, pageList.length - 1));
    setSelectedBlockId(null);
    setHistory([pageList[target]?.blocks ?? []]);
    setHistoryIdx(0);
    setCurrentPage(target);
  };
  const addPage = () => {
    const np = { id: uid(), name: `Page ${pages.length + 1}`, blocks: [] as Block[] };
    const next = [...pages, np];
    setPages(next);
    switchPage(next.length - 1, next);
  };
  const deletePage = (i: number) => {
    if (pages.length <= 1) return;
    const next = pages.filter((_, j) => j !== i);
    setPages(next);
    switchPage(Math.min(currentPage, next.length - 1), next);
  };

  // Edit/delete/duplicate are tree-recursive so blocks nested inside a layout
  // block (container / row / columns) are first-class editable too.
  const updateBlock = (id: string, content: Partial<BlockContent>) => {
    setBlocks(prev => treeUpdate(prev, id, content));
  };

  const deleteBlock = (id: string) => {
    if (selectedBlockId === id) setSelectedBlockId(null);
    updateBlocks(treeDelete(blocks, id));
  };

  const duplicateBlock = (id: string) => {
    const { list, newId } = treeDuplicate(blocks, id, uid);
    updateBlocks(list);
    if (newId) setSelectedBlockId(newId);
  };

  // Drop a palette block INTO a layout block (append to its children).
  const dropBlockInto = (parentId: string) => {
    const type = draggedTypeRef.current;
    if (!type) return;
    if (!surfaceCfg.allowedBlocks.includes(type)) {
      showToast("error", `${type.charAt(0).toUpperCase() + type.slice(1)} isn't supported on ${messageType}.`);
    } else {
      const child: Block = { id: uid(), type, content: defaultBlockContent(type) };
      updateBlocks(treeInsertChild(blocks, parentId, child));
      setSelectedBlockId(child.id);
    }
    draggedTypeRef.current = null;
    draggedBlockIdRef.current = null;
    setDragOverIndex(null);
  };

  const surfaceCfg = SURFACE_CONFIG[messageType];

  // BUG-01 — clicking a palette tile appends the block (the action most users try
  // first). Blocks the surface doesn't support are rejected with a clear message.
  const addBlock = (type: BlockType) => {
    if (!surfaceCfg.allowedBlocks.includes(type)) {
      showToast("error", `${type.charAt(0).toUpperCase() + type.slice(1)} isn't supported on ${messageType}.`);
      return;
    }
    const newBlock: Block = { id: uid(), type, content: defaultBlockContent(type) };
    updateBlocks(addBlockToTree(blocks, selectedBlockId, newBlock));
    setSelectedBlockId(newBlock.id);
  };

  // BUG-04 — switching surface must not silently drop unsupported blocks. Warn and
  // let the user confirm; coerce style onto the new surface's allowed options.
  const changeSurface = (type: MessageType) => {
    if (type === messageType) return;
    const cfg = SURFACE_CONFIG[type];
    const dropped = [...new Set(blocks.filter(b => !cfg.allowedBlocks.includes(b.type)).map(b => b.type))];
    if (dropped.length && !window.confirm(
      `${type} doesn't support: ${dropped.join(", ")}. Those blocks won't be shown on this surface. Switch anyway?`
    )) return;
    setMessageType(type);
    setMessageStyle(prev => coerceStyleForSurface(prev, type));
    // Return the inspector to Message Settings — a selected block may be dropped on
    // the new surface, and surface is a message-level change.
    setSelectedBlockId(null);
  };

  const selectTemplate = (key: string) => {
    if (key === "blank") {
      updateBlocks([]);
      setSelectedBlockId(null);
    } else {
      const t = TEMPLATES[key];
      if (t) {
        updateBlocks(t.blocks.map(b => ({ ...b, id: uid() })));
        setMessageType(t.type);
        setMessageStyle(prev => coerceStyleForSurface(prev, t.type));
        setSelectedBlockId(null);
      }
    }
    setShowTemplatePicker(false);
  };

  // BUG-02 / BUG-07 — publish is gated on a real validation pass. The rules live in
  // ./inAppRules so the list-row Publish applies exactly the same ones (P-4).
  const validationIssues = (): string[] => validateMessageDoc(buildPayload(), { deepLinkSchemes, brokenImages, brokenVideos, brand });
  // F11 — probe every https image URL in the message once; failures become publish blockers.
  const [brokenImages, setBrokenImages] = useState<string[]>([]);
  const imageKey = imageUrlsOf({ design: { pages } }).join("\n");
  useEffect(() => {
    let off = false;
    const urls = imageKey ? imageKey.split("\n") : [];
    probeImages(urls).then((bad) => { if (!off) setBrokenImages(bad); });
    return () => { off = true; };
  }, [imageKey]);
  // P7 (DA-BUG-1385) — probe every https video URL once; a 403/404/unplayable video blocks publish.
  const [brokenVideos, setBrokenVideos] = useState<string[]>([]);
  const videoKey = videoUrlsOf({ design: { pages } }).join("\n");
  useEffect(() => {
    let off = false;
    const urls = videoKey ? videoKey.split("\n") : [];
    probeVideos(urls).then((bad) => { if (!off) setBrokenVideos(bad); });
    return () => { off = true; };
  }, [videoKey]);
  const issueList = validateMessageIssues(buildPayload(), { deepLinkSchemes, brokenImages, brokenVideos, brand });
  const issues = issueList.map((i) => i.message);
  // F5 — go to an issue: its page, select + flash the block, focus the named field.
  const [focusTarget, setFocusTarget] = useState<{ blockId: string; field: string | null; n: number } | null>(null);
  const goToIssue = (i: Issue) => {
    setShowIssues(false);
    if (i.pageIndex != null && i.pageIndex !== currentPage) switchPage(i.pageIndex);
    if (i.blockId) {
      setSelectedBlockId(i.blockId);
      setFocusTarget({ blockId: i.blockId, field: i.field, n: Date.now() });
    } else if (/Trigger tab/.test(i.message)) setPropTab("trigger");
    else if (/Audience tab/.test(i.message)) setPropTab("audience");
    else if (/Schedule tab/.test(i.message)) setPropTab("schedule");
  };
  useEffect(() => {
    if (!focusTarget) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-block-id="${focusTarget.blockId}"]`) as HTMLElement | null;
      if (el) {
        if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.setAttribute("data-flash", "true");
        el.style.outline = "3px solid #F6AD55";
        setTimeout(() => { el.removeAttribute("data-flash"); el.style.outline = ""; }, 1500);
      }
      const layer = document.querySelector(`[data-layer-id="${focusTarget.blockId}"]`) as HTMLElement | null;
      if (layer && typeof layer.scrollIntoView === "function") layer.scrollIntoView({ block: "nearest" });
      if (focusTarget.field) {
        const f = document.querySelector(`[data-inspector] [data-field="${focusTarget.field}"]`) as HTMLElement | null;
        if (f) f.focus();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [focusTarget, selectedBlockId, currentPage]);

  const handlePaletteDragStart = (e: React.DragEvent, type: BlockType) => {
    draggedTypeRef.current = type;
    draggedBlockIdRef.current = null;
    // Setting drag data makes the drop fire reliably across browsers.
    try { e.dataTransfer.setData("text/plain", type); e.dataTransfer.effectAllowed = "copy"; } catch { /* noop */ }
  };

  const handleOutlineDragStart = (id: string) => {
    draggedBlockIdRef.current = id;
    draggedTypeRef.current = null;
  };

  const handleOutlineDrop = (targetIdx: number) => {
    if (!draggedBlockIdRef.current) return;
    const fromIdx = blocks.findIndex(b => b.id === draggedBlockIdRef.current);
    if (fromIdx === -1) return;
    const newBlocks = [...blocks];
    const [removed] = newBlocks.splice(fromIdx, 1);
    newBlocks.splice(targetIdx > fromIdx ? targetIdx - 1 : targetIdx, 0, removed);
    updateBlocks(newBlocks);
    draggedBlockIdRef.current = null;
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    // Canvas drop inserts a NEW palette block; outline-row reorder is handled in the Layers list.
    if (!draggedTypeRef.current) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = "copy"; } catch { /* noop */ }
    // Compute the insertion slot from the REAL rendered block positions (the blocks live in
    // the centered card, not spread over the whole canvas) — compare the cursor Y to each
    // top-level block's vertical midpoint. First block whose midpoint is below the cursor →
    // insert before it; past the last midpoint → append.
    const items = Array.from((e.currentTarget as HTMLElement).querySelectorAll("[data-blk-idx]")) as HTMLElement[];
    if (items.length === 0) { setDragOverIndex(0); return; }
    let idx = items.length;
    for (let i = 0; i < items.length; i++) {
      const r = items[i].getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) { idx = i; break; }
    }
    setDragOverIndex(idx);
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedTypeRef.current) {
      const type = draggedTypeRef.current;
      if (!surfaceCfg.allowedBlocks.includes(type)) {
        showToast("error", `${type.charAt(0).toUpperCase() + type.slice(1)} isn't supported on ${messageType}.`);
      } else {
        const newBlock: Block = { id: uid(), type, content: defaultBlockContent(type) };
        const idx = dragOverIndex ?? blocks.length;
        const newBlocks = [...blocks.slice(0, idx), newBlock, ...blocks.slice(idx)];
        updateBlocks(newBlocks);
        setSelectedBlockId(newBlock.id);
      }
    }
    draggedTypeRef.current = null;
    draggedBlockIdRef.current = null;
    setDragOverIndex(null);
  };

  const selectedBlock = treeFind(blocks, selectedBlockId);
  const flatAll = (bs: Block[]): Block[] => bs.reduce<Block[]>((acc, b) => acc.concat(b, b.content.children ? flatAll(b.content.children) : []), []);
  const couponChoices = pages.flatMap((p, pi) => flatAll(p.blocks).filter((b) => b.type === "coupon")
    .map((b) => ({ id: b.id, label: `${b.content.code || "Coupon"}${pages.length > 1 ? ` (page ${pi + 1})` : ""}` })));

  const MESSAGE_TYPES: { id: MessageType; label: string }[] = [
    { id: "modal", label: "Modal" },
    { id: "banner", label: "Banner" },
    { id: "slide-in", label: "Slide-in" },
    { id: "fullscreen", label: "Fullscreen" },
  ];

  const appearance = effectiveAppearance(messageStyle.appearance, pages.map((p) => ({ blocks: p.blocks, style: messageStyle })));
  const previewDark = appearance === "dark" || (appearance === "auto" && previewMode === "dark");
  const previewSurface = previewDark ? darkPreviewSurface(messageStyle, brand) : null;
  const previewBlocks = previewSurface ? darkPreviewBlocks(blocks, previewSurface, brand) : blocks;
  const previewStyle = previewSurface ? { ...messageStyle, background: cssOf(previewSurface) } : messageStyle;
  const renderDeviceContent = () => (
    <PreviewTheme.Provider value={previewDark ? DARK_PREVIEW : null}>
    <div className="relative w-full h-full" onClick={() => setSelectedBlockId(null)}>
      <SampleAppBackground />
      <div
        className="absolute inset-0"
        onDragOver={handleCanvasDragOver}
        onDrop={handleCanvasDrop}
        onDragLeave={() => setDragOverIndex(null)}
      >
        <MessageOverlay
          messageType={messageType}
          messageStyle={previewStyle}
          blocks={previewBlocks}
          selectedBlockId={selectedBlockId}
          onSelectBlock={setSelectedBlockId}
          onUpdateBlock={updateBlock}
          onDeleteBlock={deleteBlock}
          onDuplicateBlock={duplicateBlock}
          onDropInto={dropBlockInto}
          dragOverIndex={dragOverIndex}
        />
      </div>
    </div>
    </PreviewTheme.Provider>
  );

  const deviceFrame = device === "android"
    ? <AndroidFrame>{renderDeviceContent()}</AndroidFrame>
    : <IPhoneFrame>{renderDeviceContent()}</IPhoneFrame>;

  if (featurePermissionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3" style={{ backgroundColor: "#F7FAFC" }}>
        <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: "var(--brand-border)", borderTopColor: "var(--brand-primary)" }} />
      </div>
    );
  }

  // F16 (DA-BUG-1137): no full-screen spinner while the message loads — the frame renders at once
  // and the canvas shows a loading veil; Save / Publish stay disabled until the message is in.

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-3" style={{ backgroundColor: "#F7FAFC" }}>
        <p className="text-sm font-medium" style={{ color: "#E53E3E" }}>{loadError}</p>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: "#E2E8F0", color: "#4A5568" }} onClick={onBack}>
          <ChevronLeftIcon size={15} /> Go back
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ fontFamily: "Inter, system-ui, sans-serif", backgroundColor: "#F7FAFC" }}>

      {/* Top Bar */}
      <div className="flex items-center px-4 gap-3 flex-shrink-0 border-b"
        style={{ height: 56, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
        {/* Left */}
        <div className="flex items-center gap-2 flex-shrink-0" style={{ width: 260 }}>
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#718096" }} onClick={onBack}>
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            {editingName ? (
              <input autoFocus
                className="text-sm font-semibold border-b-2 bg-transparent outline-none"
                style={{ color: "#1A202C", borderColor: "var(--brand-primary)", maxWidth: 160 }}
                value={messageName}
                onChange={(e) => setMessageName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => { if (e.key === "Enter") setEditingName(false); }} />
            ) : (
              <button className="text-sm font-semibold truncate hover:opacity-70 transition-opacity"
                style={{ color: "#1A202C", maxWidth: 160 }} onClick={() => setEditingName(true)}>
                {messageName}
              </button>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
              style={{ backgroundColor: STATUS_BADGE[status].bg, color: STATUS_BADGE[status].fg }}>
              {STATUS_BADGE[status].label}
            </span>
          </div>
        </div>

        {/* Center — Message type */}
        <div className="flex-1 flex justify-center">
          <div className="flex gap-0.5 p-0.5 rounded-xl" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}>
            {MESSAGE_TYPES.map((t) => (
              <button key={t.id}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  backgroundColor: messageType === t.id ? "var(--brand-primary)" : "transparent",
                  color: messageType === t.id ? "#FFFFFF" : "#718096",
                }}
                onClick={() => changeSurface(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right */}
        {/* P4 (DA-BUG-1382): size to content. A fixed 260px cell overflowed its ~700px of buttons
            leftward over the centered surface tabs, so clicks on "Fullscreen" hit the device toggle. */}
        <div className="flex items-center gap-2 justify-end flex-shrink-0">
          <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}>
            {(["ios", "android"] as Device[]).map((d) => (
              <button key={d}
                className="p-1.5 rounded-md transition-all"
                style={{ backgroundColor: device === d ? "#FFFFFF" : "transparent", color: device === d ? "var(--brand-primary)" : "#718096" }}
                onClick={() => setDevice(d)} title={d === "ios" ? "iOS" : "Android"}>
                <Smartphone size={15} />
              </button>
            ))}
          </div>
          {/* DA-BUG-1054 — Light / Dark preview (the phone's setting); Always light/dark wins, as on the device. */}
          <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ backgroundColor: "#F7FAFC", border: "1px solid #E2E8F0" }}
            title={appearance === "light" ? "This message is always light" : appearance === "dark" ? "This message is always dark" : "Phone in light / dark mode"}>
            {(["light", "dark"] as const).map((m) => (
              <button key={m} type="button" aria-label={`Preview ${m}`} aria-pressed={previewMode === m}
                className="px-1.5 py-1 rounded-md text-[11px] font-semibold"
                style={{ backgroundColor: previewMode === m ? "#FFFFFF" : "transparent", color: previewMode === m ? "var(--brand-primary)" : "#718096" }}
                onClick={() => setPreviewMode(m)}>{m === "light" ? "Light" : "Dark"}</button>
            ))}
          </div>
          {appearance !== "auto" && previewMode !== appearance && (
            <span className="text-[10px]" style={{ color: "#A0AEC0" }}>{appearance === "light" ? "This message is always light" : "This message is always dark"}</span>
          )}
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-30"
            style={{ color: "#A0AEC0" }} onClick={undo} disabled={historyIdx === 0}><Undo2 size={16} /></button>
          <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100 disabled:opacity-30"
            style={{ color: "#A0AEC0" }} onClick={redo} disabled={historyIdx >= history.length - 1}><Redo2 size={16} /></button>
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors hover:bg-gray-50"
            style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
            onClick={() => setShowPreview(true)}>
            <Eye size={15} /> Preview
          </button>
          {/* R-3 (DA-BUG-1134): see the saved draft on a real phone (QR / deep link). */}
          <button type="button" aria-label="Send test to device" title="Send test to device"
            className="p-1.5 rounded-lg border transition-colors hover:bg-gray-50" style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
            onClick={openSendTest}><Smartphone size={15} /></button>
          {canSaveInAppMessage && (
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50"
              style={{ borderColor: "#E2E8F0", color: "#4A5568" }}
              onMouseEnter={(e) => { if (saving || publishing) return; const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--brand-border)"; el.style.color = "var(--brand-primary)"; el.style.backgroundColor = "var(--brand-tint)"; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "#E2E8F0"; el.style.color = "#4A5568"; el.style.backgroundColor = "transparent"; }}
              onClick={handleSave} disabled={saving || publishing || loading}>
              <SaveIcon size={15} /> {saving ? "Saving…" : status === "active" ? (draftRevisions ? "Save changes" : "Save to live") : "Save"}
            </button>
          )}
          {/* Gap 14: a fixed slot, so Preview / Save never shift when the badge appears or goes. */}
          <div data-testid="issues-slot" className="flex justify-center flex-shrink-0" style={{ width: 84 }}>
          {issues.length > 0 && (
            <button type="button" className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
              title="Show the issues to fix before publishing" aria-haspopup="dialog"
              onClick={() => setShowIssues(true)}
              style={{ backgroundColor: "#FED7D7", color: "#C53030" }}>
              {issues.length} {issues.length === 1 ? "issue" : "issues"}
            </button>
          )}
          </div>
          {canPublishInAppMessage && (
            <button
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: "var(--brand-primary)", boxShadow: "0 2px 8px rgb(var(--brand-primary-rgb) / 0.35)" }}
              title={issues.length ? issues.join("\n") : undefined}
              onMouseEnter={(e) => { if (saving || publishing) return; (e.currentTarget as HTMLElement).style.backgroundColor = "var(--brand-primary-hover)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "var(--brand-primary)"; }}
              onClick={handlePublish} disabled={saving || publishing || loading}>
              <Send size={14} /> {publishing ? "Publishing…" : status === "active" ? "Republish" : "Publish"}
            </button>
          )}
          {!canSaveInAppMessage && !canPublishInAppMessage && (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 border"
              style={{ borderColor: "#E2E8F0", color: "#718096" }}>
              View only
            </span>
          )}
        </div>
      </div>

      {status === "active" && hasDraft && (
        <div data-testid="unpublished-changes" className="flex items-center gap-3 px-4 py-2 border-b text-sm"
          style={{ background: "#FFFAF0", borderColor: "#FBD38D", color: "#7B341E" }}>
          <span className="font-semibold">Unpublished changes</span>
          <span className="text-xs">Phones still show the published version until you republish.</span>
          <div className="ml-auto flex gap-2">
            <button type="button" className="px-3 py-1 rounded-lg border text-xs font-semibold" style={{ borderColor: "#FBD38D" }} onClick={discardDraft}>Discard</button>
            {canPublishInAppMessage && (
              <button type="button" className="px-3 py-1 rounded-lg text-xs font-semibold text-white" style={{ background: "var(--brand-primary)" }} onClick={handlePublish}>Republish</button>
            )}
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="relative flex flex-1 overflow-hidden">

        {/* Left Panel */}
        <div className="flex-shrink-0 flex flex-col border-r overflow-hidden" style={{ width: 248, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
          {/* Templates button */}
          <div className="px-3 py-2.5 border-b" style={{ borderColor: "#E2E8F0" }}>
            <button
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border-2  text-sm font-medium transition-colors"
              style={{ borderColor: "#CBD5E0", color: "#718096" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#93C5FD"; (e.currentTarget as HTMLElement).style.color = "var(--brand-primary)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#CBD5E0"; (e.currentTarget as HTMLElement).style.color = "#718096"; }}
              onClick={() => setShowTemplatePicker(true)}>
              <Layers size={14} /> Templates
            </button>
          </div>

          {/* Blocks / Layers tabs */}
          <div className="flex px-3 pt-2.5 gap-1 border-b" style={{ borderColor: "#E2E8F0" }}>
            {([["blocks", "Blocks"], ["layers", "Layers"]] as const).map(([key, label]) => (
              <button key={key} type="button" onClick={() => setLeftTab(key)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-t-lg focus:outline-none transition-colors"
                style={{
                  color: leftTab === key ? "var(--brand-primary)" : "#A0AEC0",
                  borderBottom: leftTab === key ? "2px solid var(--brand-primary)" : "2px solid transparent",
                }}>
                {key === "blocks" ? <Square size={13} /> : <Layers size={13} />} {label}
                {key === "layers" && blocks.length > 0 && (
                  <span className="rounded-full px-1.5" style={{ fontSize: 9, background: leftTab === key ? "var(--brand-tint)" : "#EDF2F7", color: leftTab === key ? "var(--brand-primary)" : "#A0AEC0" }}>{blocks.length}</span>
                )}
              </button>
            ))}
          </div>

          {/* Block Palette (Blocks tab) */}
          <div className="flex-1 overflow-y-auto" hidden={leftTab !== "blocks"}>
            <div className="px-3 pt-3 pb-3">
              {PALETTE_GROUPS.map((group) => (
                <div key={group.label} className="mb-3">
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#A0AEC0" }}>{group.label}</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {group.items.map((item) => {
                      const supported = surfaceCfg.allowedBlocks.includes(item.type);
                      return (
                        <button key={item.type} type="button" draggable={supported}
                          disabled={!supported}
                          title={supported ? `Add ${item.label}` : `${item.label} isn't supported on ${messageType}`}
                          onDragStart={(e) => handlePaletteDragStart(e, item.type)}
                          onClick={() => addBlock(item.type)}
                          className="flex flex-col items-center gap-1 py-2 px-1 rounded-lg border transition-colors focus:outline-none focus:ring-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ backgroundColor: "#F7FAFC", borderColor: "#E2E8F0", cursor: supported ? "pointer" : "not-allowed" }}
                          onMouseEnter={(e) => { if (!supported) return; const el = e.currentTarget as HTMLElement; el.style.borderColor = "#93C5FD"; el.style.backgroundColor = "var(--brand-tint)"; }}
                          onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "#E2E8F0"; el.style.backgroundColor = "#F7FAFC"; }}>
                          <div style={{ color: "#718096" }}>{blockIcon(item.type, 16)}</div>
                          <span style={{ color: "#4A5568", fontSize: 10 }}>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Block Outline (Layers tab) */}
          <div className="flex-1 overflow-y-auto px-3 py-3" hidden={leftTab !== "layers"}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "#A0AEC0" }}>Blocks in message</p>
            {blocks.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-xs" style={{ color: "#CBD5E0" }}>No blocks yet.</p>
                <p className="text-xs" style={{ color: "#CBD5E0" }}>Drag from palette above.</p>
              </div>
            ) : (
              <div className="space-y-1" role="list">
                {blocks.map((block, idx) => (
                  <div key={block.id} role="listitem" draggable data-layer-id={block.id}
                    onDragStart={() => handleOutlineDragStart(block.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleOutlineDrop(idx)}
                    className="flex items-center gap-1 px-1.5 py-1 rounded-lg transition-all group border"
                    style={{
                      backgroundColor: selectedBlockId === block.id ? "var(--brand-tint)" : "transparent",
                      borderColor: selectedBlockId === block.id ? "var(--brand-border)" : "transparent",
                    }}>
                    <GripVertical size={12} className="flex-shrink-0 cursor-grab" style={{ color: "#CBD5E0" }} />
                    {/* Selecting the block is a real, focusable button (BUG-13). */}
                    <button type="button"
                      className="flex-1 flex items-center gap-2 min-w-0 text-left rounded focus:outline-none focus:ring-2"
                      onClick={() => setSelectedBlockId(block.id)}>
                      <span style={{ color: selectedBlockId === block.id ? "var(--brand-primary)" : "#718096" }}>
                        {blockIcon(block.type, 12)}
                      </span>
                      <span className="flex-1 text-xs truncate" style={{ color: selectedBlockId === block.id ? "var(--brand-primary-hover)" : "#4A5568" }}>
                        {blockLabel(block)}
                      </span>
                    </button>
                    {/* Keyboard-accessible reorder + delete (BUG-13). */}
                    <button type="button" title="Move up" aria-label="Move block up" disabled={idx === 0}
                      className="p-0.5 rounded focus:outline-none focus:ring-2 disabled:opacity-30"
                      style={{ color: "#A0AEC0" }}
                      onClick={(e) => { e.stopPropagation(); moveBlock(idx, -1); }}>
                      <ChevronUp size={12} />
                    </button>
                    <button type="button" title="Move down" aria-label="Move block down" disabled={idx === blocks.length - 1}
                      className="p-0.5 rounded focus:outline-none focus:ring-2 disabled:opacity-30"
                      style={{ color: "#A0AEC0" }}
                      onClick={(e) => { e.stopPropagation(); moveBlock(idx, 1); }}>
                      <ChevronDown size={12} />
                    </button>
                    <button type="button" title="Delete block" aria-label="Delete block"
                      className="p-0.5 rounded focus:outline-none focus:ring-2"
                      style={{ color: "#CBD5E0" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = "#FC8181"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = "#CBD5E0"}
                      onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Page pager (multi-page) */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-t flex-shrink-0" style={{ borderColor: "#E2E8F0", backgroundColor: "#FFFFFF" }}>
            {/* P15 (DA-BUG-1392): editor pager named distinctly from the Preview's page arrows so
                a screen reader / tests can tell them apart while the Preview overlay is open. */}
            <button type="button" aria-label="Previous page (editor)" disabled={currentPage === 0}
              onClick={() => switchPage(currentPage - 1)} className="p-1 rounded disabled:opacity-30" style={{ color: "#718096" }}>
              <ChevronLeftIcon size={14} />
            </button>
            <span className="text-xs font-medium flex-1 text-center truncate" style={{ color: "#4A5568" }}>
              {pages[currentPage]?.name || `Page ${currentPage + 1}`} <span style={{ color: "#A0AEC0" }}>({currentPage + 1}/{pages.length})</span>
            </span>
            <button type="button" aria-label="Next page (editor)" disabled={currentPage >= pages.length - 1}
              onClick={() => switchPage(currentPage + 1)} className="p-1 rounded disabled:opacity-30" style={{ color: "#718096" }}>
              <ChevronRight size={14} />
            </button>
            {pages.length > 1 && (
              <button type="button" aria-label="Delete page" onClick={() => deletePage(currentPage)} className="p-1 rounded" style={{ color: "#CBD5E0" }}
                onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = "#FC8181"} onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = "#CBD5E0"}>
                <Trash2 size={12} />
              </button>
            )}
            <button type="button" onClick={addPage}
              className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md" style={{ color: "var(--brand-primary)", background: "var(--brand-tint)" }}>
              <span style={{ fontSize: 13, lineHeight: 1 }}>+</span> Add page
            </button>
          </div>
        </div>

        {/* Canvas */}
        {loading && (
          <div role="status" className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3" style={{ background: "rgba(247,250,252,0.85)" }}>
            <div className="w-8 h-8 rounded-full border-2 animate-spin" style={{ borderColor: "var(--brand-border)", borderTopColor: "var(--brand-primary)" }} />
            <p className="text-sm" style={{ color: "#718096" }}>Loading message…</p>
          </div>
        )}
        {/* F10 — a centred flex scroller clips its overflowing top; auto margins centre the phone
            when it fits and let the canvas scroll to every edge when it doesn't. */}
        <div
          data-testid="designer-canvas"
          className="flex-1 flex overflow-auto"
          style={{ background: "radial-gradient(circle, #e2e8f0 1px, transparent 1px) 0 0 / 20px 20px, #f7fafc" }}
          onClick={() => setSelectedBlockId(null)}
        >
          <div className="m-auto py-8 px-4" onClick={(e) => e.stopPropagation()}>
            {deviceFrame}
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-shrink-0 border-l overflow-y-auto" style={{ width: 400, backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}>
          {selectedBlock ? (
            <div>
              <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "#E2E8F0" }}>
                <div className="flex items-center gap-2">
                  <span style={{ color: "var(--brand-primary)" }}>{blockIcon(selectedBlock.type, 14)}</span>
                  <span className="text-sm font-semibold capitalize" style={{ color: "#1A202C" }}>{selectedBlock.type}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#A0AEC0" }}
                    onClick={() => duplicateBlock(selectedBlock.id)}><Copy size={14} /></button>
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-red-50" style={{ color: "#A0AEC0" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.color = "#FC8181"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.color = "#A0AEC0"}
                    onClick={() => deleteBlock(selectedBlock.id)}><Trash2 size={14} /></button>
                  <button className="p-1.5 rounded-lg transition-colors hover:bg-gray-100" style={{ color: "#A0AEC0" }}
                    onClick={() => setSelectedBlockId(null)}><X size={14} /></button>
                </div>
              </div>
              <div className="p-5">
                <div data-inspector="">
                <CouponChoices.Provider value={couponChoices}>
                <BlockPropertyPanel block={selectedBlock} onUpdate={(c) => updateBlock(selectedBlock.id, c)}
                  background={messageType === "fullscreen" && blocks.some(b => b.type === "image" && b.content.imageUrl) ? null : messageStyle.background}
                  canSaveInAppMessage={canSaveInAppMessage} />
                </CouponChoices.Provider>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 px-5 py-3.5 border-b" style={{ borderColor: "#E2E8F0" }}>
                <Settings size={14} style={{ color: "var(--brand-primary)" }} />
                <p className="text-sm font-semibold" style={{ color: "#1A202C" }}>Message Settings</p>
              </div>
              <div className="p-5">
                <MessageSettingsPanel
                  hasDismissButton={pages.some((p) => hasDismissAction(p.blocks))}
                  pagesForAppearance={pages.map((p) => ({ blocks: p.blocks, style: messageStyle }))}
                  propTab={propTab} setPropTab={setPropTab}
                  messageType={messageType}
                  messageStyle={messageStyle} setMessageStyle={setMessageStyle}
                  triggerSettings={triggerSettings} setTriggerSettings={setTriggerSettings}
                  audienceSettings={audienceSettings} setAudienceSettings={setAudienceSettings}
                  scheduleSettings={scheduleSettings} setScheduleSettings={setScheduleSettings}
                  frequencySettings={frequencySettings} setFrequencySettings={setFrequencySettings}
                  goalSettings={goalSettings} setGoalSettings={setGoalSettings}
                  deliverySettings={deliverySettings} setDeliverySettings={setDeliverySettings}
                  canSaveInAppMessage={canSaveInAppMessage}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showTemplatePicker && <TemplatePickerModal onSelect={selectTemplate} onClose={() => setShowTemplatePicker(false)} />}
      {showPreview && (
        <PreviewModal
          device={device} setDevice={setDevice}
          blocks={blocks} pages={pages} startPage={currentPage} messageType={messageType} messageStyle={messageStyle}
          previewMode={previewMode} brand={brand}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showIssues && issueList.length > 0 && (
        <IssuesModal issues={issueList} pages={pages} onPick={goToIssue} onClose={() => setShowIssues(false)} />
      )}

      <SendTestModal isOpen={showSendTest} onClose={() => setShowSendTest(false)} messageId={messageId} />

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium"
          style={{
            backgroundColor: toast.type === "success" ? "#1A202C" : "#C53030",
            color: "#FFFFFF",
          }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

export default InAppDesigner;

